/* =====================================================================
   THE ROOM ENGINE — E4: THE OBJECTS ON THE DESK

   The desk, the paper and the two books, as things in the one scene of
   E1, lit by the one sun that lives inside the portal (ROOM-PLAN §3).

   Everything here is SELF-CONTAINED — its own shaders, its own meshes,
   its own draw calls — so engine.js keeps four small hooks and E3's
   lettering merges over the top of it without meeting a line of this.

   What it draws, in order:

     1. THE DESK, a shader quad. Its MOOD is `.board::before` in
        styles.css — the 178deg body gradient, the rain wash, the
        vignette, the lamp's pool at night and the wax catching toward
        the light — ported layer for layer, in the same order, in CSS
        pixels. Its WOOD is not: R6 threw out the three bands of
        stretched fractal noise (which read as brown with streaks) and
        grows the board instead — see THE OAK below. It is a TABLE
        and not a plane: it has an edge, and past the edge is the
        dark of the room (the room pose used to run to a banded horizon).

     2. THE SHADOWS ON THE OAK, in the same pass as the desk because
        they are on it: the sheet's four stacked shadows at the SVG's
        own distances (37 / 21 / 10 / 3.6 sheet units) and opacities
        (.30 / .34 / .26 / .18), struck along the sun (survey-map.js
        Desk.one :295), and the two books' — the contact shadow that
        never goes plus the cast one that is entirely the light's
        doing (styles.css .book-face box-shadow).

        These four were BAKED INTO the furniture SVG at serialisation
        time with a fixed `translate(-62.76 -7.55)` — a sun from
        whenever E1 saved the file. They are stripped from the raster
        now (`prepFurniture`) and struck from the live sun instead.

     3. THE BOOKS, two closed covers. Box meshes; buckram on one and
        morocco on the other, from the same gradients the CSS uses;
        the block's fore-edge along the foot and the fore side; a
        spine. Their labels stay in the DOM (E6 owns the open books)
        and `place()` puts the DOM box where the cover is drawn.

     4. THE PAPER. A grid, not a quad, so the sheet has a shape:
        - the DECKLE EDGE is the alpha of the paper path in the
          furniture texture, which is the same torn outline the SVG
          draws — one tap that was already being made;
        - the FOLD CREASES are vertex displacement. The sheet was
          folded in eight; the three vertical creases and the one
          across are tents 7 units wide, and the light that reaches
          them is the sun's, so the lit and dark bands the SVG painted
          at a fixed north-west (.sv-fold-lit / .sv-fold-dark) are
          stripped and SHADED instead;
        - the CURLED CORNER is a real fold: the triangle past the
          chamfer is reflected back across the fold line and lifted,
          so the back of the paper faces the room and throws a shadow
          on the face. The SVG's flat `.sv-curl` group is stripped.
        The 34 foxing marks and everything printed stay in the texture
        and are not lit (ROOM-PLAN §7 keeps them; they are charm and
        they cost nothing once they are a texture).

   R7 (ROOM-REVIEW-1): the paper and the books are SOLIDS. The sheet has
   a lie, a crease with a ridge in the mesh, a cut edge hanging to the oak
   and a turned corner that shades itself from its own height; the books
   have boards, squares, a recess and a round spine, every vertex with a
   normal. All three objects and the card (day.js) take a low sheen from
   the real camera, which is what reads as "solid" at the room pose. The
   three draws run against the depth buffer and put it back.

   Instruments retained here: `?seed=<n>` selects another board (the
   shipped one is 7; `status` names it), and `?books=0` isolates the desk
   without books.
   ===================================================================== */
(function () {
'use strict';

var Q = new URLSearchParams(location.search);
/* P2: the peel's shadow has one taste question, its EDGE. `?peel=a` (the
   default) lets the penumbra widen with the height of the paper that
   throws it, as daylight does; `?peel=b` keeps it a hard line at every
   height, as a lamp close by does. Nothing else is behind the switch. */
var PEEL_HARD = /[?&]peel=b\b/.test(location.search);
/* THE SEED IS FIXED (ROOM-REVIEW-1 R6 SEED). One desk, one place: a board
   that changed every refresh would make every harness diff meaningless and
   would be a thing to look at, which the desk must not be. `?seed=` is here
   so the twelve of the contact sheet can be drawn; the shipped one is this. */
var SEED = (function () { var v = parseFloat(Q.get('seed')); return isFinite(v) ? v : 7; })();
var NO_BOOKS = Q.get('books') === '0';
/* LT1: place.js's own `?lamp=0` — the lamp is not there at all, so the
   wood may not carry its pool either. The oak reads the same switch
   rather than being told about it, because objects.js is loaded whether
   or not the room ever is. */
var NO_LAMP = Q.get('lamp') === '0';
var R7 = '#define R7 1\n';


/* ==== R15: THE SHEET LIES OVER THE BOOKS ============================
   Tommy, on the merged engine: "old objects sat under the map, new sit on
   top, surely that's a decision independent of 3dness?" — it is. R7 gave
   the books a body and drew them against the depth buffer, and a board
   fifteen pixels thick beat a sheet lying at zero, so the two books came
   out ON the paper. 8102 and every picture before R7 have it the other
   way round: the sheet's foot corners lie OVER the books, with the spine
   and part of each cover showing past the paper's edge.

   It is put back as GEOMETRY, not as a draw order: the paper's height
   field carries a LIFT over each book's footprint, so the sheet is
   draped over the boards and the depth buffer is left to say what is in
   front of what. Two numbers:

     · how far the paper rides above the cover — the sheet is rag paper
       and it does not lie into the cloth; 2.2 units is about the 1.4 the
       card is given for "about a millimetre" (E6) and a half again.
     · how long the drape takes to come down off a board. A sheet folded
       in eight is stiff: fifteen css pixels of rise over twenty-six of
       run is a thirty-degree fall, which is about what a book under a
       map actually does to it. Any shorter and the fold reads as a step;
       any longer and the whole south margin bows. */
var PAPER_FLOAT = 2.2;       // sheet units above the cloth
/* DR2, 2026-09-21: AND DRAPE_RUN IS GONE. The twenty-six css px above were
   one run for the whole desk; the drape is per entry now (DRAPE_RUN_K,
   below) and there is nothing left that wants the literal. The paragraph
   above it stays because it is the argument the number came out of and
   because the hold, the shade and the two radii are still R15's. What used
   to ride in `uOverZ.z` is written nought — see undUniforms. */
var DRAPE_SHADE = 0.40;      // how much of the fall's own slope the light is given
/* ==== R22: THE PAPER BRIDGES THE BOARD'S EDGE ========================
   A stiff sheet folded in eight does not break over the arris of a board:
   it carries on flat a little way past it and falls from there. Six sheet
   units (3.6 css px, about the board's own square) — and it is not only
   manners. The drape is evaluated PER VERTEX and interpolated across the
   cell, so the chord cuts the corner at the arris: with the station just
   inside the board at the cover's height and the next one d units outside
   it at 1 - d/run of it, the paper crosses back under the cover a little
   way INSIDE the footprint whenever the cell is wider than about four
   times PAPER_FLOAT*run/H — thirteen units at the fit. When that happens
   the cover shows THROUGH the paper as a hard strip of cloth a couple of
   pixels wide down the board's edge (found with a graded grid at 23 units
   a cell; the even grid was inside the bound by two units and got away
   with it). The hold moves the whole fall out past the arris, which takes
   the crossing with it: at six units a cell of twenty is safe. */
var DRAPE_HOLD = 6.0;        // sheet units the paper carries on flat

/* R67 (b): AND THE TENT IS GONE. R63 built a ruled tent over the books
   as `?drape=tent` beside R45's wrap and asked Tommy to pick. He picked:
   "i prefer wrap to tent". So the wrap is the only drape there is — the
   switch, the three TENT_ constants, the `?tent=` tuner, the soft-max
   metric in bookM and bkRamp, and the ridge that rode in uOverF.z have all
   come out, and what is left is R15's run and R45's two radii exactly as
   they stood before R63. */

/* ==== R45: A BOARD'S EDGE IS A RADIUS, NOT A CREASE ==================
   Tommy: "are we happy with how the effect where the paper sits over the
   field guide and journal, or could it be better?"

   R22's fall is a straight plane with a CREASE struck at each end of it,
   and a crease is what paper does when it has been folded. A sheet laid
   over a shut book has not been folded over it — it is BENT, and paper
   bent over an arris takes a radius: its own bending stiffness against
   the weight of whatever hangs beyond. For a sheet this heavy over a
   board this thick that radius is a few millimetres, and with none of it
   the plateau over the field book came out as a card folded over a box —
   two ruled lines with a flat lit face between them, and a hard rounded
   corner where the cone comes round the board's own corner.

   The ramp is FILLETED, not eased. R22 argued a smoothstep off the edge
   is wrong because an S has its whole gradient in the middle of it and
   the fall came out as a bruise; that argument stands and the straight
   plane is kept. What is added is a quadratic fillet of half-width e over
   each END of the ramp, which is C1 where R22 was C0: the plane between
   the two radii is R22's plane, one tone, no gradient in it, and the two
   kinks are two radii instead.

   THE TWO RADII ARE NOT THE SAME. At the top the paper is bending over a
   board's own square — 1.8 css px of board with a chamfer round it — and
   at the foot it is coming down onto a flat table with the whole drape's
   weight behind it, which is a longer curve. Twice as long, measured by
   eye against the pictures: at equal radii the foot read as a second
   arris and the fall still had two lines in it.

   The top fillet is held clear of the board's own footprint: it is
   centred on the end of DRAPE_HOLD, so at a 3.5 css radius it begins
   1.1 css px OUTSIDE the arris and the paper over the cover is at the
   cover's height exactly, everywhere — which is what keeps R22's chord
   from crossing back under the cloth. */
/* AND A BOARD DOES NOT IRON THE PAPER. R15 put the sheet at the cover's
   height EXACTLY over a board's whole footprint and scaled its own
   gradient away with the same mask, so the plateau over the field book
   came out as a dead flat face with nothing in it — which is card, not
   paper, and is the other half of what reads as a box. A shut octavo is
   150 x 100 css px of cloth under a sheet that has been folded in eight
   and carried: the wave in it is PRESSED, not taken out. A quarter of it
   survives — under a millimetre of relief, which cannot reach the cloth
   through the two of float the paper rides at — and it is the gradient
   that matters more than the height: it is what puts the sheet's own
   grain back on the lit face. */
var LIE_BOARD = 0.25;
var DRAPE_FILL_TOP = 3.5;    // css px of radius where the paper leaves a board
/* DR2, 2026-09-21: and the FOOT's radius is gone with DRAPE_RUN. R45's 7
   css px were a quarter of R45's 26; the foot is a quarter of the entry's
   OWN run now (DRAPE_FOOT_K), which is the same proportion carried up, so
   the literal has nothing left to be the foot of. The arris keeps its
   3.5 — a board's own square does not get taller with the book. What used
   to ride in `uOverF.y` is written nought — see undUniforms. */

/* ==== DR1, 2026-09-21: IS THIS WHAT PAPER DOES? =====================
   Tommy, on the drape: "actually let's pause, do we actually think the
   distortion from the field guide and journal is realistic? surely the
   map would just sit on top of them and be angled slightly? the fold has
   always looked a bit weird but i hadn't clocked why ... if we did do the
   angle, have a think about how the angle would look considering the size
   of the field guide and journal (including the journal being thicker).
   maybe try a few concepts?" And: "i don't want us to create something
   that's a mess for the map and everything else ends up needing to be
   rethought or rendered differently."

   HE IS RIGHT ABOUT THE NUMBER. R15 gives the paper fifteen css px of
   rise in twenty-six of run and calls it thirty degrees; against the
   journal that is thirty millimetres of rise in fifteen of run, a wrap of
   SIXTY degrees with a radius at each end. That is what CLOTH does over a
   box, or what a sheet does when something is holding it down a
   centimetre from the board. Nothing is holding this sheet down. A
   surveyor's sheet folded in eight comes off an edge in a long shallow
   fall — three or four times the drop, not half of it — and where the
   sheet is stiff enough and the span short enough it does not fall at all:
   it BRIDGES, as a plate, and sits on the high point angled.

   So there are three drapes here and Tommy picks. The switch is read once
   and unknown values are the drape that shipped, so the default is
   untouched: `?drape=wrap` (or nothing) is R45's wrap to the letter, the
   emitted GLSL character for character.

     wrap   (default)  R15's run, R22's hold, R45's two radii
     paper  (concept B) the same fall, as long as paper takes: the run is
                        PER ENTRY and proportional to that entry's own
                        height, `run_i = DRAPE_RUN_K * top_i`, and the
                        foot's radius is a quarter of that run so the
                        touchdown is a long curve and not a second arris.
                        About 105 mm off the journal and 38 off the field
                        book, against the 15 both take today.
     card   (concept C) eight rigid panels hinged at the creases. The
                        sheet does not drape at all: each panel is a plate
                        on whatever holds its corners up, and the panels
                        meet at a kink of light along the creases. See
                        CARD_MM.

   THE WHOLE OF EACH CONCEPT IS IN THE HEIGHT FIELD, which is the one
   thing every consumer already reads — the GLSL `boardH`/`drapeH` and the
   JS mirror `O.paperHeight`, which have to agree. Nothing downstream is
   told which drape is on. ==================================== */
/* ==== DR2, 2026-09-21: HE PICKED. ====================================
   Tommy, on the three: "i like drape=paper".

   So concept B IS the drape, and there is no `?drape=` any more. The wrap
   and the card are OUT of the file — not behind a flag, not behind a
   default, gone: `DRAPE_MODE` and the query it read, `DRAPE_RUN`,
   `DRAPE_FILL_FOOT`, `CARD_MM`, `CARD_FEATHER`, `CARD_G_SHEET`, `cardZ`,
   `cardG`, `cardLift`, `phCardZ`, the `uOverS` uniform, the `max()`
   against the plateau, `RoomObjects.DRAPE`, `ctx.paper.DRAPE` and
   `Room.state().drape`. Every `if (DRAPE_MODE === ...)` in the emitted
   GLSL has become the arm concept B took, character for character: the
   proof this card carries is that the sheet's VS and FS, the map's
   `paperInk` and the oak's FS are IDENTICAL to what b494caa emitted under
   `?drape=paper`, byte for byte, with no query on the URL at all.

   The prose above and below stays as it is. It is the argument — what the
   wrap was doing wrong, where the 105 mm came from, why the card's west
   corner was 18.3 and not 18 — and an argument does not stop being true
   because two of its three answers have been thrown away. What is gone is
   the CODE.

   Two things DR1 decided stay decided and were not touched here: the
   lifted leaf carries no board (`smoothstep(0.94, 0.995, abs(fn.z))`) and
   the creases are nought at rest (`creaseGates()`, CR2: one gate a crease).
   ==================================================================== */
/* how many times its own height a board's fall takes. 3.5 is
   the shallow end of what a sheet folded in eight does off a table edge;
   at 1 it is R15's wrap and at 6 the whole south margin is one dome. */
var DRAPE_RUN_K = 3.5;
var LIP_OPEN = 0.4;          /* JW1: how much of the flat sheet's cast is let off under a held-up edge */
/* and the foot's radius as a fraction of that run. R45 had the foot at
   7/26 = .27 of the run at the shipped numbers, so a quarter is the same
   proportion carried up — which is the point: the touchdown has to stay a
   quarter of a long fall, or a 105 mm drape lands on a 7 px radius and
   reads as a second arris. */
var DRAPE_FOOT_K = 0.25;
/* ---- DR2: THE CARD'S DERIVATION, KEPT, WITH NO CODE UNDER IT --------
   Everything from here to the end of this block described `CARD_MM`,
   `CARD_FEATHER` and `CARD_G_SHEET`, which are deleted. It is left
   standing because it is the only written record of how eight rigid
   plates would have to be hinged on this sheet — the 18.3, the 8.1
   degrees, the seam the max() puts along a footprint — and because the
   concept is a plate over a SPAN, which is the thing this desk will want
   again the day something else is laid under the paper. Nothing below
   this line is executed. ------------------------------------------- */
/* concept C: the corner heights of the eight panels, in mm above the oak,
   at x = -420, -210, 0, 210, 420 and y = -216, 0, 216 (the sheet's own
   creases, which is why eight rigid panels can carry it at all). Read as
   a 5 x 3 grid, bilinear within each panel:

     y = -216:   0,  0,  0,  0,  0
     y =    0: 18.3, 0,  0, 30, 30
     y =  216: 18.3, 0,  0, 30, 30

   WHERE THE NUMBERS COME FROM. The journal is 30 mm thick at x 282..452,
   y 13..263, so it is under nearly the whole south-east panel: that panel
   lies FLAT on it at 30. The panel west of it is hinged on the crease at
   x = 0, where the paper is on the oak, and rests on the journal's west
   edge, so it ramps 0 -> 30 across 210 mm (8.1 degrees). The north-east
   panel is hinged on the north edge at y = -216 and roofs down from 30 at
   the middle crease (also 8.1 degrees, because the panel is 216 deep and
   the drop is 30). The field book is 11 mm thick at x -436..-336, so only
   its inner 84 mm is under the sheet at all: the south-west panel is a
   PLATE hinged on the oak along x = -210 and resting on the book's inner
   edge at x = -336, which puts its own west edge at 11 * 210/126 = 18.3
   and the plate at 11 exactly where the cloth is. The two middle columns
   have nothing under them and lie on the wood.

   The 18.3 is the derivation's number, not the 18 of the sketch: at 18
   the plate crosses the book's inner edge 0.2 mm LOW and the max() below
   has to lift it, which puts a seam along the footprint. At 18.3 the
   plate is tangent to the cover and there is no seam.
   (DR2: the CARD_MM declaration — west 18.3, east 30.0 — was here.)
   And how far the paper has to be off the wood before its own undulation
   is pressed out of it — a panel bridging something is held taut. Eight
   sheet units is under three millimetres.
   (DR2: the CARD_FEATHER declaration — 8.0 — was here.)
   And the plate's own tilt, added to the surface gradient the fragment
   shader lights the paper with: the whole of what a rigid panel does to
   the light, one tone per panel and a kink at each crease.
   (DR2: the CARD_G_SHEET declaration — the string that spliced
   cardG(vSheet)*uOverZ.w into SHEET_FS — was here; the line in SHEET_FS
   is the line it always was.)
   ---- end of the card's derivation -------------------------------- */

/* =====================================================================
   R17 — THE CHOSEN DESK (ROOM-REVIEW-3 R17)

   Tommy, on the merged engine: "is it obviously a desk or is it a
   background that could be perceived as wood but isn't a normal wood
   colour? i'm almost 'too close to it' to be sure how it'd be perceived."

   Tommy chose the amber timber, window fall, table edge and desk tells.
   ===================================================================== */

/* (a) THE TIMBER. The GRAIN is not changed — it is R6's quarter-sawn oak
   in every variant — only the board's own colour and the few constants
   that say how hard its rings, pores and fleck read, because that is what
   separates one timber from another once the anatomy is drawn. Each row:

     hi/bd/lo   the 178deg body gradient, which is what the board IS
     warm/cool  the figure's tint, applied as a multiplier on the board
     fig        how strong the slow figure is
     late       how dark the latewood band goes (1 = not at all)
     pore       how dark a vessel goes
     fleck      how pale the ray fleck is
     red        how much redder the latewood is than the earlywood
     ground     the CSS page colour under frame 1 (styles.css html.room),
                measured off each board's own fit and not guessed

   The chosen amber board follows. */
var TIM = { hi: '0.462,0.345,0.205', bd: '0.360,0.262,0.150', lo: '0.228,0.158,0.088',
            warm: '1.26,1.13,0.89', cool: '1.08,0.96,0.83',
            fig: '0.33', late: '0.68', pore: '0.49', fleck: '0.21', red: '0.28',
            ground: '#5d4a32' };
/* Tommy 2026-09-14: "let's make edge the default, i like it". */
/* the ground under frame 1 is painted with the board's mean tone (the
   commit after R12); a lookbook board has to take its own, or the page
   changes colour when the picture lands */
if (typeof document !== 'undefined' && document.head) {
  var st17 = document.createElement('style');
  st17.textContent = 'html.room body, html.room .board { background: ' + TIM.ground + '; }';
  document.head.appendChild(st17);
}

/* (b) THE LIGHT FALLS ACROSS THE DESK. Not a vignette: a single soft
   off-centre falloff whose bright side is the window's, plus a low satin
   band along the grain from the same direction. Wood reads as wood by its
   sheen more than by its colour. */
var FALL = true;

/* (c) THE TABLE IS AN OBJECT. The fit camera pulled back and pitched down
   a few degrees so the top's FRONT EDGE and a sliver of floor show along
   the bottom of the viewport. The floor here is NOT place.js's — the room
   is a 1 % Easter egg and must not be fetched for the front page — it is
   one more draw of the desk's own quad at the floor's height, under the
   desk's own program. */
var EDGE = true;
var EDGE_DEG = 22;
var EDGE_K = 1.10;
/* R25 (1): 1.05 -> 1.01. With the pull-back moderated from 1.20 to 1.10 the
   sheet's foot came down the frame and R17's front edge went off the bottom
   of it — measured, the arris landed at screen y 844 and the floor band was
   9 px. Bringing the table's own front edge in by 18 css px puts the arris
   at 826.4, the face at 11.5 px and the floor at 26 px (R17 shipped 11 and
   46 at k 1.20), and the two books still stand ON the top: their covers end
   at screen y ~805, a whole book's shadow short of the arris. */
/* R27 (1): back to R17's 1.05. R25 had to bring the table's front edge in
   to 1.01 to find a floor band at all; the ride-up frees ~50 css px at the
   bottom of the frame, so the edge can stand where it did and the books
   get a proper width of desk in front of them again instead of 29 px. */
var EDGE_NEAR = 1.05;
/* ==== B1: THE DESK IS 1150 mm HIGH, AND ONLY PLACE.JS SAYS SO =======
   This line read `var SLAB_MM = 34, DESK_H_MM = 730; // place.js's own
   two numbers`, and one of them had not been place.js's for some time:
   place.js:183 stands the desk at 1150 mm (Tommy's chosen standing desk,
   2026-09-16) and keeps 730 only as the `?desk=sitting` comparison. The
   floor below was struck at `-0.5 - slab - drop` with drop = 730, which
   is 764 mm under the top surface; place.js's own floor is 1150 mm under
   it. The two never showed at once — this one is gated on the stand-up
   being at nought — so the instant the move left zero the floor dropped
   386 mm in a single frame, measured 67.5 css px at the probe against
   7.0 px of camera motion in the same step.

   So there is ONE source now and it is place.js's, read at DRAW time
   because place.js arrives long after this file does. The fallback is
   only ever reached on the FRONT PAGE, where place.js is not fetched at
   all (base.njk: "not here at all ... fetched on the first Room.room(1)"),
   and it mirrors place.js:183's own switch rather than one number: 1150
   standing, because standing is the desk Tommy chose and the one the
   room will put under a cold `/` the moment it lands; 730 only under
   `?desk=sitting`, so that link's fit stands on the floor its own room
   will give it. scripts/test-room-measurements.js asserts the mirror
   against place.js's constants, which is what stops the two drifting a
   second time. ==== */
var DESK_STAND_MM = 1150, DESK_SIT_MM = 730;    // place.js:183's own pair
var SLAB_MM = 34;                               // place.js:184's SLAB
var DESK_SITTING = /[?&]desk=sitting\b/.test(location.search);
function deskTopMm() {                          // floor to the TOP SURFACE
  var P = window.RoomPlace;
  if (P && P.DESK_H_MM > 0) return P.DESK_H_MM;
  return DESK_SITTING ? DESK_SIT_MM : DESK_STAND_MM;
}
function deskSlabMm() {                         // the top's own thickness
  var P = window.RoomPlace;
  return (P && P.SLAB_MM > 0) ? P.SLAB_MM : SLAB_MM;
}
/* B1: how much of `deskT` the front page's floor takes to hand over to
   place.js's — see the hand-over block in drawDesk for why it is a
   quarter and not the whole of it. */
var HANDOVER = 0.25;

/* ==== DL1: THE FLOOR BAND IS BOARDS ===============================
   The band under the table (`uFace == 2` in deskFS) is drawn here because
   the front page never fetches place.js — see EDGE above — and it is the
   oak boards. FF1 grew a carpet arm for it (place.js's cream wool, copied
   into this program behind a `fitfloor` switch); it was deleted on
   Tommy's word, 2026-09-23 ("Delete the carpet arm"): it was about a
   quarter of the desk program's compile and read as dark wood at his fit. ==== */

/* (d) THE TELLS. One or two things only a desk has, cheap and quiet: the
   base of the lamp at the top-left edge of the fit (where place.js stands
   it, 170 mm left of the sheet), and a pencil lying along the sheet's
   foot. Both are struck in css pixels in the desk's own pass — no mesh,
   no draw, no byte on the wire.
   C0: and both are PIECES now — desk-lamp.js and desk-tells.js — which
   hand this shader their own `oak` block. The switch that used to say
   whether they were compiled is the list: nothing registers, nothing is
   compiled. `R17.tells` below stays because place.js and the report read
   it. */
/* how far the table runs toward the reader, as a multiple of the canvas
   below the sheet's centre. E4's number is 1.9 and place.js's metrics()
   copies it to set the wall and the legs against the same rect; it is
   exported so the two cannot drift. */
var O_NEAR = EDGE ? EDGE_NEAR : 1.9;

/* ==== R71 (a): THE DESK IS A DESK, AND ITS TOP IS A SIZE IN MILLIMETRES
   Tommy, 2026-09-15: "there's a wooden surface and that seems to be on
   another wooden surface that's large and that's above a floor? it should
   become an actual desk over a floor."

   The top was a multiple of the CANVAS — `max(cx, cw-cx)*2.05` wide and
   `cy*2.5 + (ch-cy)*1.05` deep — which at 1536 x 864 is 1868 x 893 mm and
   at any other canvas is something else. A desk is a size. MEASURED
   against what stands on it (mm east and mm south of the sheet's centre,
   from room-rev8/r71-specs/measure.out.json):

       the lamp      x -532 .. -368   y -412 .. -248
       the sheet     x -420 .. +420   y -216 .. +216
       book A        x -436 .. -336   y +114 .. +264
       book B        x +282 .. +452   y  +13 .. +263
       the laptop base x +476 .. +828 y  -65 .. +216
       the open lid reaches x +840 at its north-east corner

   Tommy's measured top is 1400 x 700. It is offset east of the sheet
   rather than shrinking any object. The lamp sits behind the paper's
   folding path; the full-size laptop turns twelve degrees toward the
   reader, clearing the journal by 24 mm with its open lid still on the
   desk. The books retain their established framing of the map.

   THE FIT IS THE FIT TO THE BIT. `deskT` is the blend: nought while the
   reader is at the desk, so `deskRect` returns the OLD numbers exactly
   and the oak's rim falloff (`uEdge`, the k term below) is the number it
   was; it runs to one over the first third of the stand-up, while the
   desk's far edge is still out of frame, so the top becomes a desk with
   nothing to see happening. */
var DESK_MM = { x0: -550, x1: 850, n: -436, s: 264 };
function deskT(S) {
  if (S && S.frameK !== undefined && !/[?&]mm=0(&|$)/.test(location.search)) return 1;   /* MM1, MP1: the real desk, always (the phone's too) */
  var c = S && S.cam; if (!c) return 0;
  var r = Math.max(c.room || 0, c.lap || 0, c.seat || 0);     /* US1: upright, seated at the real desk */
  var t = Math.min(1, Math.max(0, (r - 0.02) / 0.28));
  return t * t * (3 - 2 * t);
}
function deskRect(S, t) {
  var L = S.layout, f = S.fld, mm = f.sheet.w * L.k / 840;
  var cx = L.ox + f.sheet.w * L.k / 2, cy = L.oy + f.sheet.h * L.k / 2;
  /* RU3: upright in the room the frame is wider than its glass's own
     window would say (engine.js vp): the stand-in covers what it sees */
  var vw = L.cw, vh = L.ch;
  if (L.ah) { vw = L.aw * 1.0; vh = vw * L.ch / L.cw; }
  var x0 = (L.cw - vw) / 2, y0 = (L.ch - vh) / 2;
  var hw = Math.max(cx - x0, x0 + vw - cx) * 2.05;
  var near = (y0 + vh - cy) * O_NEAR, far = (cy - y0) * 2.5;
  /* RS1 (2026-10-01): AND NEVER THROUGH THE WALL. The far reach is a
     multiple of the canvas, and in a squarer window it ran to 1000 mm north
     of the sheet's centre: past the wall the curtain hangs on (586 mm, the
     desk's back edge plus the cloth's 150 mm projection). While the stand-in
     shrinks to the real desk over the first third of the move it covered
     the curtain. At 16:9 it reached 606 mm, past the top of the window
     either way, so the fit is unchanged. */
  /* RU3: but upright and seated the tall frame sees past the wall's foot
     looking down, and the oak stopped there in a dark band; the reach
     comes back to the wall over the first quarter of the stand-up, before
     the camera faces the curtain */
  var farW = Math.min(far, -(DESK_MM.n - 150) * mm);
  far = L.ah ? far + (farW - far) * Math.min(1, (t > 0 ? t : 0) * 4) : farW;
  var a = { x: cx - hw, y: cy - far, w: hw * 2, h: far + near };
  /* SD1 (2026-10-04): ON THE PHONE THE STAND-IN IS NEVER SHORTER THAN THE
     DESK. It is sized off the window, which at a desk holds the whole
     sheet; sideways the phone lays the PLATE across the glass (PH10) and
     the sheet runs far past the window, so folded from that view the eye
     came down over the packet beyond the stand-in's near edge: the real
     desk's front edge showed under the packet with the field book hanging
     over it, and a lighter band slid in as the reader stood (Tommy's
     recording: "glitching when you go zoom out to the room and back in").
     On the phone it reaches at least 400 mm past the real desk east, west
     and south, so the blend to the real desk only ever shrinks it. */
  if (L.side) {
    var sx0 = cx + (DESK_MM.x0 - 400) * mm, sx1 = cx + (DESK_MM.x1 + 400) * mm, sy1 = cy + (DESK_MM.s + 400) * mm;
    var ax0 = Math.min(a.x, sx0), ax1 = Math.max(a.x + a.w, sx1), ay1 = Math.max(a.y + a.h, sy1);
    a = { x: ax0, y: a.y, w: ax1 - ax0, h: ay1 - a.y };
  }
  if (!(t > 0)) return a;
  /* DD1 (2026-10-01): AND THE BOOKS ARE ON IT. R71's 264 mm front edge was
     measured with the books held up by the window's foot (O.books keeps a
     book inside the glass, so the show gives and it goes further under the
     paper). In a TALL window nothing holds them up, they lie further south,
     and the real desk ended short of them: coming back in from the room
     with the map folded, the books hung off the desk's front edge over a
     flat grey band of floor (Tommy: "when zooming out of the folded map to
     the room and back in, you get a weird effect at the top and bottom",
     with developer tools open). So the front edge is Tommy's 264 mm or,
     where a book would overhang it, 4 mm past that book. At his window the
     books end at 252 mm and the desk is the measured desk to the mm. */
  var sMM = DESK_MM.s, bk = O.books ? O.books(S, true) : null;
  if (bk) for (var bi = 0; bi < bk.length; bi++)
    if (bk[bi] && bk[bi].h > 0) sMM = Math.max(sMM, (bk[bi].y + bk[bi].h - cy) / mm + 4);
  var b = { x: cx + DESK_MM.x0 * mm, y: cy + DESK_MM.n * mm,
            w: (DESK_MM.x1 - DESK_MM.x0) * mm, h: (sMM - DESK_MM.n) * mm };
  if (t >= 1) return b;
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t,
           w: a.w + (b.w - a.w) * t, h: a.h + (b.h - a.h) * t };
}

/* =================================================================
   THE OAK — .board::before, layer for layer (styles.css:181)
   ================================================================= */
var DESK_COMMON = `
/* value noise, cubic-smoothed: feTurbulence's fractalNoise is Perlin and
   this is not, but what the desk asks of it is a stretched fibrous grain
   at a given frequency in each axis, and that is what both give. */
float h21(vec2 p){ p = fract(p*vec2(0.1031,0.1030)); p += dot(p, p.yx+33.33); return fract((p.x+p.y)*p.x); }
float vnoise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  f = f*f*(3.0-2.0*f);
  return mix(mix(h21(i), h21(i+vec2(1,0)), f.x),
             mix(h21(i+vec2(0,1)), h21(i+vec2(1,1)), f.x), f.y);
}
/* feTurbulence's fractalNoise is a sum of signed Perlin octaves mapped into
   0..1, so its values crowd round a half: measured on 8102's own desk, the
   oak's own figure carries about a unit of luma either side of the mean and
   the rest of what the eye reads as grain is the film below. Value noise is
   spread near-uniformly, so it is pulled to the same width — without this
   the desk came out in hard bands (sd 9.2 against the sheet's 3.6). */
/* THE OCTAVE COUNT IS FIXED, NOT A PARAMETER. A loop with a break in it
   does not unroll, and this shader runs over every pixel of the window:
   written out, and with the counts cut, the desk came down from 0.297 to
   0.255 ms a frame at 2400 x 1350 (three passes of 150, the minimum of
   the three, bracketed by a 1x1 readPixels). The SVG asks
   for 2/3/2 and the third octave of the fine band has a period of two
   thirds of a css pixel, which is under the grid it is being drawn on. */
float fbm2(vec2 p){
  float s = 0.5*vnoise(p) + 0.25*vnoise(p*2.0);
  return 0.5 + (s/0.75 - 0.5)*0.42;
}
float fbm1(vec2 p){ return 0.5 + (vnoise(p) - 0.5)*0.42; }
/* THE FILM OVER THE WHOLE WINDOW — .canvas::after in styles.css: one
   fixed, tiled sheet of grey fractalNoise at 12 %. It is written
   mix-blend-mode: multiply, and it never multiplies anything: the layer
   it would blend with is outside its stacking context, so what it does on
   the page is lay 12 % of a mid grey over everything. That is worth
   +11..+15 of luma on the oak and it is why the desk on 8102 is lighter and
   flatter than its own gradients are. Measured three ways on the bare desk
   (means 56.9 / 52.1 / 57.8 against this model's 57.1 / 50.6 / 57.4). */
float filmGrain(vec2 px){
  return 0.5 + (h21(floor(px)) - 0.5)*0.45;
}
/* a CSS radial-gradient's ending shape: percentages of the box, and the
   stop positions are fractions of the ending radius */
float rad(vec2 p, vec2 c, vec2 r){ return length((p - c)/r); }
`;

/* =================================================================
   THE OAK — R6. A PROCEDURAL BOARD, NOT A PICTURE OF ONE.

   E4's desk was the CSS recipe ported honestly: three bands of
   stretched fractal noise over a body gradient. Stretched noise has no
   rings, no pores, no planks and no direction, so it read as brown with
   streaks — Tommy: "a little underwhelming ... something better and
   more authentic". This grows the board instead. No texture, no bytes,
   one extra program-free function in the pass that was already running.

   WHAT A QUARTER-SAWN OAK BOARD IS, and how each part is drawn:

   - PLANKS. The top is boards, not a sheet. Each row of PW wood units
     is one plank with its own value, its own length offset along the
     grain (so no two planks show the same figure), its own ring
     spacing, and a seam with a lit lip on one side.

   - RINGS, from a distance field whose centre is off the plank's axis.
     On a quarter-sawn face the cut is radial, so the year rings meet
     the face nearly edge-on and run as near-parallel lines ALONG the
     plank — but the log's axis is not the plank's axis (the tree was
     not straight and the saw was), so the lines run at a slight slope
     and wander along their length. `rr` is the radius from the pith:
     the plank's own across-coordinate, plus that slope, plus a slow
     wander, plus the constant depth this board was cut at.

   - LATEWOOD. A year closes in dense dark wood, and that band — not
     the ring line — is what darkens. It is also the only part of the
     grain with any RELIEF, and its gradient is analytic here (the
     derivative of the two smoothsteps over d(rr)), so the board has a
     normal without a second evaluation of anything.

   - PORES. Oak is ring-porous: the year OPENS with a row of vessels
     big enough to see, and at arm's length the chains of them are what
     the eye actually reads as grain. Noise stretched 200:1 along the
     grain, thresholded, confined to the first fifth of the ring.

   - RAY FLECK. The reason anyone quarter-saws oak. The medullary rays
     are radial sheets; on this face you see them broadside, as ribbons
     lying ACROSS the grain, paler than the wood and the shiniest thing
     on the board.

   - THE SHEEN, which is most of what wood looks like at the room
     pose's grazing angle. Wax over an oriented fibre does not give a
     round highlight: it gives a BAND perpendicular to the grain
     (Kajiya-Kay — the specular lobe of a fibre is the cone about it),
     and the ray fleck gives a second band at right angles to the
     first, which is why real quarter-sawn oak flashes when you move.
     The light is the live sun by day and the lamp, as a point with its
     own falloff, at night.

   EVERYTHING IS BAND-LIMITED. The desk runs to the room's horizon and
   at a grazing camera the rings minify past a pixel; every one of these
   fields is faded toward its own mean by the fwidth of its own
   coordinate (`fade`), which is a one-instruction LOD and is what keeps
   the board from boiling as the camera tilts (the lesson of R1 (b)).

   IT IS QUIET. No ink, no cup rings, no knots, no burr: the desk is the
   ground the sheet is read on. And it is not a colour of its own — it
   is a MULTIPLIER on the board's own colour, so the vignette, the
   lamp's pool, the rain and the gradient all still hold, and nothing
   else on the page shifts.
   ================================================================= */
var WOOD_GLSL = `
uniform float uSeed;
uniform vec3 uEye;      // the camera, world css px (x right, y up-negated, z up)
uniform vec3 uLdir;     // the sun as a direction TOWARD the light, world
uniform vec4 uLampW;    // the lamp: world x, y, its height over the top, on

/* LK4 (audit A18): THE WOOD REPEATED. h21 is a fract(p*0.1031) hash, so
   integer cells ten apart hash to nearly the same value and cells 97
   apart to the same one: the fleck drifts stood in columns a regular
   step apart across every plank (vp-1028x862-default, f-folded). The
   oak's own noise is on an integer hash instead, which has no period a
   desk could ever show. Same statistics, no repeat. */
float hw(vec2 q){
  highp uvec2 u = uvec2(ivec2(floor(q)));
  highp uint h = u.x*1597334677u ^ u.y*3812015801u;
  h ^= h >> 16; h *= 2246822519u; h ^= h >> 13; h *= 3266489917u; h ^= h >> 16;
  return float(h)*(1.0/4294967296.0);
}
float vnw(vec2 p){
  vec2 i = floor(p), f = fract(p);
  f = f*f*(3.0-2.0*f);
  return mix(mix(hw(i), hw(i+vec2(1,0)), f.x),
             mix(hw(i+vec2(0,1)), hw(i+vec2(1,1)), f.x), f.y);
}
float fbw(vec2 p){ return 0.5 + (vnw(p) - 0.5)*0.42; }
float hs(vec2 q){ return hw(q*vec2(97.0, 7.0) + vec2(floor(uSeed*1013.0), 29.0)); }
/* a field is worth drawing while its own coordinate moves less than about
   a cell per pixel; past that it is fetched toward its mean instead */
float fade(float w){ return 1.0 - smoothstep(0.30, 0.85, w); }

const float PW = 330.0;        // a plank, wood units (~210 css px at the fit)
const float RELIEF = 0.055;    // how deep the latewood groove is

void oak(vec2 n, out float v, out float warm, out vec2 g,
         out vec2 T, out float gloss, out float fleck)
{
  float row = n.y/PW;
  float pi = floor(row), py = fract(row);
  float r1 = hs(vec2(pi, 1.0)), r2 = hs(vec2(pi, 2.0));
  float r3 = hs(vec2(pi, 3.0)), r4 = hs(vec2(pi, 4.0));

  float x = n.x + r1*3700.0;                 // no two planks are the same board
  float yy = (py - 0.5)*PW;

  /* THE LOG'S AXIS IS NOT THE PLANK'S */
  float slope = (r2 - 0.5)*0.075;
  float wob = fbw(vec2(x*0.0022, pi*9.1 + 3.0)) - 0.5;
  float rr = yy + slope*x + wob*34.0 + r3*900.0;

  /* a ring is a year and years differ */
  float per = 14.0*(0.70 + 0.6*r4);
  float ph = rr/per + (fbw(vec2(rr*0.010, pi*4.7)) - 0.5)*0.9;
  float k = fade(fwidth(ph));
  float t = fract(ph);

  /* LATEWOOD, and its gradient without a second evaluation */
  float a = smoothstep(0.72, 0.86, t), b = smoothstep(0.93, 1.00, t);
  float late = a*(1.0 - b)*k;
  float u1 = clamp((t - 0.72)/0.14, 0.0, 1.0), u2 = clamp((t - 0.93)/0.07, 0.0, 1.0);
  float dlate = (6.0*u1*(1.0 - u1)/0.14)*(1.0 - b) - a*(6.0*u2*(1.0 - u2)/0.07);
  g = (-RELIEF*dlate*k/per)*vec2(slope, 1.0);

  /* PORES, at the opening of the ring */
  /* THE CELL IS A DASH, NOT A PIXEL: at 0.55 along the grain the cell was
     1.2 css px and the board came out mottled like film grain rather than
     pored. A vessel chain is eight or ten units long and under two across. */
  vec2 pq = vec2(x*0.10, rr*1.2 + pi*31.0);
  /* AND NOT EVERY YEAR THE SAME. With one strength for every ring the rows
     came out as a woven hatch — corduroy, not oak. Each year gets its own. */
  float yr = hs(vec2(floor(ph)*0.37 + pi*3.1, 5.0));
  float pore = smoothstep(0.60, 0.88, vnw(pq))
             * (1.0 - smoothstep(0.05, 0.24, t))
             * (0.25 + 1.05*yr) * fade(fwidth(pq.x)*0.55);

  /* RAY FLECK, in drifts, across the grain */
  /* SHORT: at 0.020 across they ran forty px and read as drips down it. */
  vec2 fq = vec2(x*0.30, rr*0.055 + pi*17.0);
  fleck = smoothstep(0.62, 0.92, vnw(fq))
        * smoothstep(0.45, 0.78, vnw(vec2(x*0.010, rr*0.0042)))
        * fade(fwidth(fq.x)*0.8);

  float fib = (vnw(vec2(x*0.055, rr*0.42)) - 0.5)*fade(fwidth(rr*0.42)*0.5);
  float figure = fbw(vec2(x*0.0016, rr*0.0065)) - 0.5;   // the slow figure

  /* THE PLANK'S OWN EDGE */
  float e = min(py, 1.0 - py)*PW;
  /* written the way round the spec asks for (edge0 < edge1): a reversed
     smoothstep is undefined in GLSL ES even where every driver obliges */
  float seam = 1.0 - smoothstep(0.0, 3.0, e);
  float lip = max(0.0, (1.0 - smoothstep(3.5, 8.0, e)) - seam)*step(0.5, py);

  v = (0.93 + 0.14*r1)
    * (1.0 + figure*${TIM.fig})
    * (1.0 + fib*0.11)
    * mix(1.0, ${TIM.late}, late)
    * mix(1.0, ${TIM.pore}, pore)
    * (1.0 + fleck*${TIM.fleck})
    * mix(1.0, 0.34, seam)
    * (1.0 + lip*0.09);
  warm = clamp(fleck*0.95 + max(0.0, figure)*0.55 + max(0.0, fib)*0.35, 0.0, 1.0);
  /* latewood is not just darker, it is REDDER: the dense band keeps the
     heart's colour where the open earlywood has been bleached by the wax */
  warm -= late*${TIM.red};
  T = normalize(vec2(1.0, -slope));                 // the grain runs along the rings
  gloss = (1.0 - 0.80*pore)*(1.0 + 0.55*late);
}
`;

/* ==== LD3 (2026-10-02): THE OAK WITHOUT ITS GRAIN ======================
   Everything WOOD_GLSL declares above `oak()`, and an oak() that answers
   the board's mean: no rings, pores, fleck or figure, a flat normal. The
   driver drops every noise function the real one calls, so this links in
   a fraction of the oak's time. The two numbers are the oak's own mean
   multiplier and warmth over the fit's desk, struck by measurement
   against the real board (desk-only pixels, frame after the hand-over)
   so the cross-fade changes the figure and not the tone. */
var WOOD_MEAN = { v: '0.95', warm: '0.05' };
function WOOD_STUB() {
  return WOOD_GLSL.slice(0, WOOD_GLSL.indexOf('void oak(')) + `void oak(vec2 n, out float v, out float warm, out vec2 g,
         out vec2 T, out float gloss, out float fleck)
{
  v = ${WOOD_MEAN.v}; warm = ${WOOD_MEAN.warm}; g = vec2(0.0); T = vec2(1.0, 0.0); gloss = 1.0; fleck = 0.0;
}
`;
}

/* THE DESK'S SHADER IS BUILT, NOT WRITTEN TWICE. The bootstrap plain
   program and the oak program share the same builder; the branch is at
   build time so neither program pays for the other's instructions. */
/* LD3: `stub` builds the oak's own program with its grain taken out — the
   same faces, edge, lamp, sheen and shadows, with oak() answering the
   board's MEAN (WOOD_MEAN below) — the desk frame 1 draws while the real
   grain is still in the driver's hands. */
function deskFS(wood, stub) { return `#version 300 es
precision highp float;
in vec2 vUv; in vec2 vSheet;
out vec4 o;
uniform float uZ;          // the top's own plane, world
uniform vec3 uSheet;       // ox, oy, k
uniform vec2 uCanvas;      // css w, h
uniform vec4 uSun;         // dx, dy, len, cast
uniform vec4 uSun2;        // soft, dark, night, wet
uniform vec3 uSunP;        // the paper's own direction and length
uniform vec2 uLamp;        // where the wax catches, as a fraction of the box
uniform vec4 uLampD;       // LT1: the banker's lamp itself - its base x, y in css px, its head's height, and its ON
uniform float uLampF;      // LT1: 3.2/reach^2 in css px, so the oak's falloff IS the source's
uniform vec4 uPaper;       // the sheet in css px: x, y, w, h
/* ==== R58: AND THE SHEET AS IT WOULD BE IF NOTHING WERE FOLDED ======
   uPaper is what the paper still COVERS, and it shrinks as the map is
   folded — which is right for the shadow it throws and wrong for
   everything else that was reading a SCALE off it. A millimetre is a
   millimetre: the pencil is 175 mm of wood whatever the map is doing
   (Tommy, 2026-09-14: "the pencil gets shrunk alongside it"), the lamp's
   base is 82 mm of bronze, the floorboards are a board wide, and the
   pool of light on the oak is round the lamp and not round the packet.
   All four take uFull, which is the whole sheet and never moves. ==== */
uniform vec4 uFull;        // R58: the UNFOLDED sheet in css px
uniform float uCurl;       // the chamfer, in css px
` + shDecl() + `uniform vec4 uEdge;        // the table top in css px: x y w h
uniform float uEdged;
uniform float uDpr;
${oakDecl()}${glowDecl()}${(wood && EDGE) ? 'uniform int uFace;      // 0 the top, 1 the front edge, 2 the floor\nuniform vec2 uSlab;     // the top\'s thickness and the drop to the floor, css px\nuniform float uFloorA;  // B1: 1 - deskT, the hand-over to place.js\'s floor\n' : ''}` + DESK_COMMON + (wood ? (stub ? WOOD_STUB() : WOOD_GLSL) : '') + `
/* the paper's outline: a rectangle with the south-east corner chamfered,
   which is what the deckle path is once its two-unit wander is taken off
   (survey-map.js deckle :4622 — at these shadow offsets the wander is
   under a tenth of a pixel of coverage) */
float paperCov(vec2 p, vec4 r, float curl, float aa){
  vec2 d = max(r.xy - p, p - (r.xy + r.zw));
  float box = max(d.x, d.y);
  float ch = ((p.x - (r.x + r.z - curl)) + (p.y - (r.y + r.w - curl))) * 0.70710678;
  return (1.0 - smoothstep(-aa, aa, box)) * (1.0 - smoothstep(-aa, aa, ch));
}
/* a box-shadow: a rectangle, offset, with a gaussian-ish blur */
float boxCov(vec2 p, vec4 r, float blur){
  vec2 d = max(r.xy - p, p - (r.xy + r.zw));
  float s = max(d.x, d.y);
  return 1.0 - smoothstep(-blur, blur, s);
}
` + shLipGLSL() + `
void main(){
  vec2 p = vec2(uSheet.x + vSheet.x*uSheet.z, uSheet.y + vSheet.y*uSheet.z);
  float cw = uCanvas.x, ch = uCanvas.y;${(wood && EDGE) ? `
  /* ==== R25 (3): ONE oak() CALL SITE ===============================
     R17 wrote the sample three times — once for the floor, once for the
     front edge, once for the top — and the compiler inlined the whole
     grain function three times: measured +25 ms of compile on frame 1,
     for a variant that draws one quad three times. The three faces differ
     only in WHERE the board is sampled, so the sample point is chosen
     here and oak() is called ONCE, before the faces branch. */
  float sc = max(cw/2400.0, ch/1400.0);
  float br = 0.0, bi = 0.0, bf = 0.0, bw = 1.0, dn = 0.0;
  vec2 n;
  if (uFace == 2) {
    bw = 150.0*max(0.6, uFull.z/840.0);
    br = p.y/bw; bi = floor(br); bf = fract(br);
    n = vec2(p.x/sc*0.55 + bi*260.0, (bf - 0.5)*bw/sc*0.55 + bi*140.0 + 500.0);
  } else {
    /* the top's own edge, taken BEFORE the grain rather than after it, so
       a fragment past the table costs nothing at all */
    if (uFace == 0 && uEdged > 0.5) {
      vec2 e0 = max(uEdge.xy - p, p - (uEdge.xy + uEdge.zw));
      if (max(e0.x, e0.y) > 0.0) discard;
    }
    n = (p - vec2(cw,ch)*0.5)/sc + vec2(1200.0,700.0);
    /* the long edge of a quarter-sawn board is the FLAT-sawn face of it,
       so the figure on this one is broader than on the top: the same
       field, walked 2.4x faster across the thickness */
    if (uFace == 1) { dn = clamp(vUv.y, 0.0, 1.0); n.y += dn*uSlab.x*2.4/sc; }
  }
  float v, warm, gloss, fleck; vec2 gr, Tw;
  oak(n, v, warm, gr, Tw, gloss, fleck);

  /* ==== R17 (c): THE TABLE IS AN OBJECT ==============================
     Two more draws of THIS quad under THIS program: the top's front edge
     (a vertical band, dropped in the vertex shader by uDrop) and the
     floor (the same big rect, at the floor's height). No second program,
     no mesh, no place.js — the room is the 1 % Easter egg and is not
     fetched to show a sliver of board under the front page. */
  if (uFace == 2) {
    /* THE FLOOR IS NOT THE DESK — it is older, greyer, walked on, and it
       is BOARDS: 150 mm of it running across the room, each with its own
       value and its own stretch of figure, because a plain brown band at
       the foot of the picture is not a floor, it is a margin. */
    float bv = 0.80 + 0.34*h21(vec2(bi, 3.0));
    float sw = max(1.6/bw, fwidth(br)*0.9);
    float seam = 1.0 - smoothstep(0.0, sw, min(bf, 1.0 - bf));
    vec3 base = mix(vec3(${TIM.bd}), vec3(0.30,0.25,0.21), 0.55)*v*bv;
    base *= 1.0 - 0.55*seam;
    /* THE DESK STANDS ON IT. The board nearest the front edge is under
       the top and is the darkest; the light reaches the floor further
       out. This is the only shadow the floor gets and it is enough. */
    float d = clamp((p.y - (uEdge.y + uEdge.w))/max(1.0, uSlab.y*0.62), 0.0, 1.0);
    vec3 col = base*mix(0.50, 1.34, d)*(0.62 + 0.38*uSun.w)*(1.0 - uSun2.z*0.55);
    col = mix(col, vec3(filmGrain(gl_FragCoord.xy/uDpr)), 0.10);
    /* B1: and it HANDS OVER to the room's own floor rather than being cut
       away from in front of it — uFloorA is 1 - deskT, one at the desk
       pose and nought by the time the top is the measured desk. */
    o = vec4(col, uFloorA); return;
  }
  if (uFace == 1) {
    vec3 base = vec3(${TIM.bd})*v;
    base = mix(base, base*vec3(${TIM.warm}), max(0.0, warm)*0.42);
    base = mix(base, base*vec3(${TIM.cool}), max(0.0, -warm)*0.70);
    /* THE ARRIS IS EASED, and an eased arris catches the sky along its
       whole length. It is one bright line and it is the line that says
       "slab" — without it the front edge is a dark stripe and reads as
       a shadow under the desk rather than as the desk. */
    float cham = 1.0 - smoothstep(0.0, 0.26, dn);
    vec3 wl = normalize(vec3(-uSun.x, uSun.y, 0.6));
    /* a front edge in a lit room is not black: it takes the floor's bounce
       and the whole lower half of the sky, which is most of what a vertical
       face away from the window ever gets */
    float face = 0.44 + 0.26*max(0.0, dot(wl.xy, vec2(0.0,-1.0)));
    vec3 col = base*(face + cham*0.78*(0.35 + 0.65*uSun.w));
    col *= 1.0 - 0.34*smoothstep(0.35, 1.0, dn);
    col = mix(col, vec3(filmGrain(gl_FragCoord.xy/uDpr)), 0.12);
    o = vec4(col, uFloorA); return;          // B1: with the floor it stands on
  }
` : ''}
${(wood && EDGE) ? '' : `
  /* THE TABLE HAS AN EDGE. Past it is the room, which E8 owns; here it is
     the dark the canvas is cleared to. Without this the desk was a plane
     and the reader standing in the room looked up at a banded horizon of wood. */
  if (uEdged > 0.5) {
    vec2 e = max(uEdge.xy - p, p - (uEdge.xy + uEdge.zw));
    if (max(e.x, e.y) > 0.0) discard;
  }
`}
  vec3 col = vec3(${TIM.bd});                       // --board #31241a

  /* layer 6: linear-gradient(178deg, hi 0%, board 44%, lo 100%) */
  {
    float a = radians(178.0);
    vec2 dir = vec2(sin(a), -cos(a));
    float L = abs(cw*sin(a)) + abs(ch*cos(a));
    float t = clamp(dot(p - vec2(cw,ch)*0.5, dir)/L + 0.5, 0.0, 1.0);
    vec3 hi = vec3(${TIM.hi}), bd = vec3(${TIM.bd}), lo = vec3(${TIM.lo});
    col = t < 0.44 ? mix(hi, bd, t/0.44) : mix(bd, lo, (t-0.44)/0.56);
  }
  /* layer 5: the rain in the room */
  col = mix(col, vec3(0.094,0.110,0.133), uSun2.w*0.16);
${FALL ? `  /* ==== R17 (b): THE WINDOW'S LIGHT FALLS ACROSS THE DESK ============
     In place of layer 4's centred vignette. A vignette is a lens; this is
     a LIGHT — one soft falloff whose centre is the paper's neighbourhood
     pushed toward the window, with a plain directional term under it so
     that the corner away from the window is the darkest thing on the top
     and no ring is ever drawn. */
  {
    vec2 c = vec2(uFull.x + uFull.z*0.5, uFull.y + uFull.w*0.42);
    vec2 w = normalize(vec2(-uSun.x, -uSun.y) + vec2(1e-5, 1e-5));
    c += w*vec2(cw, ch)*0.17;
    float r = rad(p, c, vec2(cw*0.95, ch*1.00));
    float pool = 1.0 - smoothstep(0.14, 1.30, r);
    float dir = clamp(dot(p - c, w)/(cw*0.85) + 0.52, 0.0, 1.0);
    float f = clamp(pool*0.70 + dir*0.38, 0.0, 1.0);
    col *= mix(0.44, 1.20, f);
  }
` : `  /* layer 4: radial-gradient(140% 130% at 50% 42%, 0 40%, .22 100%) */
  {
    float r = rad(p, vec2(cw*0.5, ch*0.42), vec2(cw*1.40, ch*1.30));
    col = mix(col, vec3(0.0), 0.22*clamp((r-0.40)/0.60, 0.0, 1.0));
  }
`}  /* layer 3: the lamp's pool, at night */
  {
    vec2 c = uLamp*vec2(cw,ch);
    float r = rad(p, c, vec2(cw*0.74, ch*0.74));
    col = mix(col, vec3(0.020,0.012,0.004), uSun2.z*0.60*clamp((r-0.20)/0.80, 0.0, 1.0));
  }
  /* ==== LT1: AND THE BANKER'S LAMP, WHICH IS ON BY DAY ===============
     HANDOVER item 2 (Tommy): "Desk banker's lamp ON in daylight AND
     night ... the visible desk light must agree with its newly always-on
     source."

     LAYER 3 ABOVE IS NOT THIS LAMP, which is the thing to know before
     reading the line below. "uLamp" is the survey party's LANTERN - the
     camp at the front of the survey, inside the portal (sky.js lampNow
     returns a point only while the lantern IS the light, so by day there
     is none and uLamp falls back to a bearing) - and layer 3 is that
     lantern taking 60 % of the value away from everywhere its pool does
     not reach after dark. The BANKER'S lamp, the brass thing standing on
     the oak with its own 82 mm base, has never put anything on the wood
     at all. This is it.

     It is the source, not a picture of one: the centre is the lamp's own
     measured base (RoomObjects.lampBase, the same figure the solid and
     its shadow stand on), the height is RoomObjects.lamp.source.headZ
     over the top, the colour is that source's own [1, .72, .38] and the
     falloff is the law place.js's light list strikes for every source in
     the room - 1/(1 + 3.2 (d/reach)^2) at reach 1.6 m, carried here as
     uLampF in css px so the wood and the room cannot disagree about one
     bulb. The head stands 34 mm toward the sheet of the base's centre;
     against 1,600 mm of reach that is nothing, and the base is the
     centre.

     0.052 is struck by measurement, not by eye: it puts about +12 of the
     red channel under the shade and about half that at the desk's far
     corner - a pool you can see on the wood and nowhere near the sunlit
     sheet's own 239, which is what "does not compete with the sun on the
     sheet" has to mean on a desk whose paper is the brightest thing in
     the frame. It is added BEFORE the oak's own layer 1, so the board's
     figure comes up inside the pool as it does inside the daylight.

     "1 - uSun2.z" is the ONE term here that is not physics, and it is an
     honest debt: at night the oak's whole lamp statement is the lantern's
     above, and a second pool beside it moves a night frame that LT1 is
     forbidden to move (the night was already lit - that is the card).
     The end state is one additive pool at all hours with the lantern's
     darkening restruck around it. REPORT-room-LT1.md, OPEN 1. ==== */
  if (uLampD.w > 0.0 && uSun2.z < 0.999) {
    vec3 lv = vec3(p - uLampD.xy, uLampD.z);
    col += vec3(1.0,0.72,0.38)*0.052*uLampD.w*(1.0 - uSun2.z)
         / (1.0 + uLampF*dot(lv,lv));
  }
  /* layer 2: THE WAX CATCHES TOWARD THE LIGHT. With the oak it is
     HALVED: the sheen below is the same statement made properly, from the
     live sun and the real camera, and both at full strength lit the middle
     of the desk twice. */
  {
    vec2 c = uLamp*vec2(cw,ch);
    float r = rad(p, c, vec2(cw*1.18, ch*0.98));
    float a = ` + (wood ? '0.082' : '0.155') + `*(1.0 - uSun2.z*0.35)*(1.0 - clamp(r/0.62, 0.0, 1.0));
    col = mix(col, vec3(1.0,0.906,0.757), a);
  }
` + (wood ? `
  /* layer 1: THE OAK. A multiplier on the board, and a sheen over it. */
  {${EDGE ? '' : `
    float sc = max(cw/2400.0, ch/1400.0);
    vec2 n = (p - vec2(cw,ch)*0.5)/sc + vec2(1200.0,700.0);
    float v, warm, gloss, fleck; vec2 gr, Tw;
    oak(n, v, warm, gr, Tw, gloss, fleck);`}
    col *= v;
    /* the pale figure of E4's third band, kept as a WARMING of the board's
       own colour rather than a wash of near-white over it: the desk's range
       is unchanged and the vignette and the lamp still own the value. */
    col = mix(col, col*vec3(${TIM.warm}), max(0.0, warm)*0.42);
    col = mix(col, col*vec3(${TIM.cool}), max(0.0, -warm)*0.70);

    /* ---- THE SHEEN ------------------------------------------------
       The board's normal is the latewood's gradient (wood units and css
       px are the same slope here: the height is in wood units too, so
       the sc cancels), and the world's y runs the other way to the
       sheet's. */
    vec3 N = normalize(vec3(-gr.x, gr.y, 1.0));
    vec3 wp = vec3(p.x, -p.y, uZ);
    vec3 V = normalize(uEye - wp);
    vec3 T = normalize(vec3(Tw.x, -Tw.y, 0.0));
    T = normalize(T - N*dot(N, T));
    /* the lamp is a POINT over the top, so its band bends round it and
       falls off; the sun is a direction and does not. */
    vec3 lv = vec3(uLampW.xy - wp.xy, uLampW.z);
    float ld = length(lv);
    float latt = uLampW.z*uLampW.z/(ld*ld + 1.0);
    float night = uSun2.z*uLampW.w;
    vec3 L = normalize(mix(uLdir, lv/max(ld,1.0), night));
    vec3 H = normalize(L + V);
    float ndl = max(dot(N, L), 0.0);
    float dth = dot(T, H);
    /* THE LOBE IS NOT A LASER. At 40 the band across the desk was a hard
       bright line; a real board's fibres are not all one direction. */
    float aniso = pow(max(0.0, 1.0 - dth*dth), 18.0);
    vec3 B = cross(N, T);
    float dbh = dot(B, H);
    float rays = pow(max(0.0, 1.0 - dbh*dbh), 16.0);
    float broad = pow(max(dot(N, H), 0.0), 18.0);
    /* wax at a grazing angle is a mirror and face-on it is not */
    float fres = 0.30 + 0.70*pow(1.0 - max(dot(N, V), 0.0), 3.0);
    /* an overcast sky has no highlight, and neither has wet wood much */
    float hard = mix(uSun.w, 1.0, night)*(1.0 - uSun2.w*0.55)*mix(1.0, latt*1.9, night);
    float sheen = (aniso*0.22*gloss + rays*0.55*fleck + broad*0.055)*ndl*fres*hard;
    col += mix(vec3(1.0,0.94,0.82), vec3(1.0,0.84,0.58), night)*sheen;

    /* AND THE SKY IS A LIGHT TOO — at the room pose it is the only one
       that matters. A high sun gives a grazing view no highlight at all
       (the mirror of a camera 15 degrees over the top wants a sun 15
       degrees over the top, and at two in the afternoon it is at 34), so
       a desk lit only by the sun's lobe went dead the moment the camera
       stood up. What makes a waxed top glow along itself is the ROOM
       reflected in it, Fresnel-weighted and broad; the grain modulates
       it, so the figure comes up with the shine and not before it.
       At the desk pose the view is down the normal and this is nothing,
       which is why the flat picture barely moves. */
    float graze = pow(1.0 - max(dot(N, V), 0.0), 3.0);
    vec3 amb = mix(vec3(0.68,0.71,0.76), vec3(0.46,0.33,0.17), night);
    col += amb*graze*(0.26*gloss + 0.48*fleck)*(1.0 - uSun2.w*0.40);${FALL ? `
    /* ==== R17 (b): A LOW SATIN ALONG THE GRAIN =======================
       The sun's own lobe is honest and at the DESK pose it mostly is not
       there at all (R6 OPEN 3: at two in the afternoon the sun is 34 deg
       up and the mirror of a camera looking straight down wants it at
       nought). What actually puts a long soft band down a waxed top in a
       room is the WINDOW — a wide, low sheet of sky at about the desk's
       own height — so this is that source, broad (exponent 7, not 18) and
       weak, modulated by the grain so the figure comes up inside it. */
    {
      vec3 WL = normalize(vec3(-uSun.x, uSun.y, 0.52));
      vec3 HW = normalize(WL + V);
      float dtw = dot(T, HW);
      float band = pow(max(0.0, 1.0 - dtw*dtw), 7.0);
      float nl = max(dot(N, WL), 0.0);
      col += vec3(1.00,0.95,0.86)*band*nl*(0.10*gloss + 0.20*fleck)
           * (1.0 - uSun2.w*0.55)*(1.0 - uSun2.z*0.65);
    }` : ''}
  }
` : `
  /* layer 1: THE GRAIN. Three bands of stretched noise over a 2400x1400
     figure laid cover-wise on the window, as the inline SVG is. */
  {
    float sc = max(cw/2400.0, ch/1400.0);
    vec2 n = (p - vec2(cw,ch)*0.5)/sc + vec2(1200.0,700.0);
    /* THE THREE COLOURS ARE LINEAR, NOT sRGB. An SVG filter works in
       linearRGB unless it is told otherwise, so feColorMatrix's .030 .018
       .011 is not a near-black: converted for the screen it is
       .190 .143 .106, which is the board's own colour, and that is why the
       grain on 8102 shows as figure rather than as soot. Taking them at
       face value cost 8 of luma across the whole desk. */
    /* the broad figure carries further than the other two: measured, the
       sheet's own desk runs to sd 7 of luma where the slow band crosses and
       this reconstruction was flat at 4.1 until the first band was widened */
    float f = 0.5 + (fbm2(n*vec2(0.0009,0.012) + 41.0) - 0.5)*1.9;
    col = mix(col, vec3(0.190,0.143,0.106), clamp(f - 0.40, 0.0, 1.0));
    float g = fbm2(n*vec2(0.0030,0.165) + 7.0);
    col = mix(col, vec3(0.243,0.186,0.102), clamp(1.12*g - 0.50, 0.0, 1.0));
    float hh = fbm2(n*vec2(0.0014,0.072) + 23.0);
    col = mix(col, vec3(0.964,0.896,0.780), clamp(0.66*hh - 0.40, 0.0, 1.0));
  }
`) + `
  /* THE EDGE OF THE TABLE goes off into the room */
  if (uEdged > 0.5) {
    vec2 e = (uEdge.xy + uEdge.zw*0.5 - p)/(uEdge.zw*0.5);
    float k = max(abs(e.x), abs(e.y));
${(wood && EDGE) ? `    /* R17 (c): E4 took the top nearly to black at its own rim, which is
       what makes the room pose read as a table in the dark. At the EDGE fit the
       rim is the thing being looked at: a top that is black where it meets
       its own arris reads as a shadow, not as a slab, so the falloff comes
       off and the front face and the floor below carry the step instead. */
    col *= 1.0 - 0.16*smoothstep(0.84, 1.0, k);` : `    col *= 1.0 - 0.62*smoothstep(0.90, 1.0, k);
    col = mix(col, vec3(0.045,0.030,0.020), smoothstep(0.990, 1.0, k));`}
  }

  /* ---- THE SHADOWS ON THE OAK -------------------------------------
     Everything cast here keeps its own DISTANCE from the thing casting
     it and takes only its direction and its length from the sun
     (survey-map.js Desk :287). At --sun-cast nought every one of them
     collapses to the contact shadow, which is not the light's doing. */
  float cst = uSun.w, ok = 0.52 + 0.48*cst;
  vec3 SH = vec3(0.063,0.039,0.024);              // .sv-shadow #100a06

  /* C0: THE THINGS STANDING ON THE OAK, under the paper's own shadow.
     R22: a thing that has been picked up throws further and softer, and
     lets the light in under its own contact line — the entry's own lift
     is 1 at rest and (1 + rise/thickness) with the hand on it. */
` + shGLSL() + `
  float aa = 0.8;
  vec2 pd = uSunP.xy*(uSheet.z*uSunP.z*(0.22 + 0.78*cst));
  /* ==== JW1 (2026-10-01): AND LIGHT GETS IN UNDER A LIFTED EDGE. The four
     taps are a sheet LYING on the oak, so where a book holds the paper a
     book's thickness up, the wood seen through that gap was shaded as if
     the paper still lay on it: four layers stacked to about a fifth of the
     wood's light, a near-black wedge under the sheet's edge between the
     books with a hard straight foot where the flat sheet would have ended,
     sliding about as a hovered book moved its drape (Tommy: "are you happy
     with the shadow under the sheet between the field guides and the
     journal, and the way it moves when you hover"). Under the held-up
     paper the flat taps ease off by LIP_OPEN of the lip's mask, and R45's
     one tap thrown from the board's height below is what is left. */
  float lf = shLip(p), open = 1.0 - ${LIP_OPEN.toFixed(2)}*lf;
  col = mix(col, SH, open*0.30*ok*paperCov(p - pd*37.0, uPaper, uCurl, aa));
  col = mix(col, SH, open*0.34*ok*paperCov(p - pd*21.0, uPaper, uCurl, aa));
  col = mix(col, SH, open*0.26*ok*paperCov(p - pd*10.0, uPaper, uCurl, aa));
  col = mix(col, SH, open*0.18*ok*paperCov(p - pd*3.6,  uPaper, uCurl, aa));
  /* ==== R45: AND THE SHEET IS NOT ON THE OAK WHERE IT COMES OVER A BOOK.
     The four taps above are struck from a sheet lying flat: its edge is a
     paper's thickness off the wood, and what it keeps off the oak is the
     contact line the last of them ends in. Beside a board that is simply
     not true — the paper there is a whole book's thickness in the air,
     the drape holds it up for a run past the board's own edge, and a lip
     that high throws a soft dark a good centimetre out onto the wood.
     One tap more, thrown from the board's height and masked to the ground
     each board is actually holding the paper over. */
  {
    /* the mask FIRST and the tap only where it is not nought: a board
       lifts about a fortieth of the oak and the other thirty-nine are not
       going to pay for a coverage test they cannot use */
    if (lf > 0.002)
      col = mix(col, SH, 1.0*0.26*ok*lf*paperCov(p - pd*96.0, uPaper, uCurl, aa*3.0));
  }${oakBody()}

` + glowGLSL() + `
  col = mix(col, vec3(filmGrain(gl_FragCoord.xy/uDpr)), 0.12);
  o = vec4(col, 1.0);
}`; }

/* =================================================================
   THE PAPER — a grid with a shape, and R7: with a body
   ================================================================= */
/* ==== R7: THE SHEET DOES NOT LIE FLAT, AND IT HAS AN EDGE ===========

   Tommy, on E4's objects in the room pose: "now we're using vectors we
   should also think about the 3Dness of the paper". E4's sheet is a
   plane — the creases are a normal and nothing else, the deckle is a
   texture alpha with no thickness behind it, and at the room pose the
   paper reads as a decal printed on the oak.

   Three things are added here and all three are GEOMETRY:

     · THE LIE. A long, low height over the sheet — two crossed waves,
       amplitude about two millimetres at this desk's scale — so the
       margins rise and fall off the oak the way a sheet that has been
       folded in eight actually sits. It is held to EXACTLY ZERO inside
       the neat rule by `pmask`: the map is drawn on its own geometry in
       its own pass and a millimetre of paper under it would move every
       letter, every mark and the day the pointer is over. The mask is
       not a compromise, it is free: the plate paints over every pixel
       inside the window, so a crease or a swell under it could never be
       seen (ROOM-REVIEW-1 R7: "keep the undulation under the plate at
       zero and let only the margins lift").

     · THE CREASES ARE TENTS NOW. E4 shaded them per fragment because a
       224-column grid could not carry a seven-unit tent; it could not
       carry one ANYWHERE, but the three long creases sit at .25/.5/.75
       of the sheet and the cross one at .5 — which on 224 x 128 are
       columns 56, 112, 168 and row 64 EXACTLY. The ridge lands on a
       vertex line, so what the grid gives is a clean tent a cell and a
       half wide. E4's per-fragment normal is kept as the fine shading
       on top of it.

     · THE CUT EDGE. A band round the sheet's own perimeter, hanging
       from wherever the lip has risen to down to the oak — one strip of
       174 quads, one extra draw. Where the margin is flat it is the
       half a world pixel the paper already floated above the desk;
       where the margin lifts it is two millimetres of paper seen from
       the side. Inset three units inside the deckle so the torn outline
       in the texture is always the silhouette and this is never it.

   And the turned corner, which was already geometry, gets the two
   things geometry should have given it: a NORMAL (it is a curved
   surface, and E4 shaded its back as if it were flat) and a SHADOW
   marched from its own height instead of a bowed triangle at a fixed
   offset. ==================================================================== */
var PAPER_LIE = `
/* zero inside the neat rule, one out past the feather */
float pmask(vec2 p, vec4 nr, float fe){
  vec2 d = max(nr.xy - p, p - (nr.xy + nr.zw));
  return smoothstep(0.0, fe, max(d.x, d.y));
}
float pwave(vec2 u){
  return sin(6.28318*(u.x*1.30 + 0.21))*cos(6.28318*(u.y*0.78 - 0.13))
       + 0.42*sin(6.28318*(u.x*2.45 - 0.37) + 4.1*u.y)
       + 0.30*cos(6.28318*(u.y*1.90 + 0.08) + 1.7*u.x);
}
/* AND THE EDGES CURL. A sheet that has been folded in eight and carried
   does not lie down at its own perimeter: the outer seventy units come up
   off the wood, which is what makes the cut edge visible all the way
   round instead of only where the wave happens to be high. */
float lieH(vec2 p, vec4 r, vec4 nr, vec3 und){
  vec2 d2 = min(p - r.xy, r.xy + r.zw - p);
  float rim = 1.0 - smoothstep(0.0, 70.0, min(d2.x, d2.y));
  return pmask(p, nr, und.y)*und.x*(pwave((p - r.xy)/r.zw) + 0.52*rim);
}
float creaseH(vec2 p, vec4 r, float sg){
  float t = 0.0;
  for (int i = 0; i < 3 + uZero; i++){
    float d = (p.x - (r.x + r.z*(0.25 + 0.25*float(i))))/sg;
    t += uCreaseOn[i]*exp(-d*d);                 /* CR2: each crease as its own turn bends it */
  }
  float dy = (p.y - (r.y + r.w*0.5))/sg;
  return t + uCreaseOn.w*exp(-dy*dy);
}
/* LF1: the drape and the sheet's height, each with the entries already
   measured (boardOf, above), and each with its own vec2 wrapper for the
   callers that have not measured them. */
float drapeOf(vec2 p, vec4 r, vec4 nr, vec3 und, float top, float lift){
  float lie = lieH(p, r, nr, und);
  /* R45: and a quarter of the lie survives over a board — see LIE_BOARD */
  return mix(lie, top + uOverZ.y + lie*${LIE_BOARD.toFixed(3)}, lift);
}
float drapeH(vec2 p, vec4 r, vec4 nr, vec3 und){
  float top, lift; bookAt(p, top, lift);
  return drapeOf(p, r, nr, und, top, lift);
}
float paperOf(vec2 p, vec4 r, vec4 nr, vec3 und, vec3 fold, float top, float lift){
  return drapeOf(p, r, nr, und, top, lift) + pmask(p, nr, und.y)*und.z*creaseH(p, r, fold.y);
}
float paperH(vec2 p, vec4 r, vec4 nr, vec3 und, vec3 fold){
  float top, lift; bookAt(p, top, lift);
  return paperOf(p, r, nr, und, fold, top, lift);
}
`;
/* R15: THE BOOKS UNDER THE PAPER, in the vertex shader of BOTH sheet
   programs. `bookLift` is the drape — 1 over the board, falling to 0 over
   `run` OUTSIDE it, so the paper reaches the cover's height AT the
   board's edge and comes down off it into the margin. `bookOn` is the
   board itself, hard-edged, which is where the sheet's cut edge has to
   stop: over a book the paper's edge rests on the cloth, and one station
   further out it is over air and hangs to the oak. */
/* R45: the drape's fillet, wanted by the VERTEX shader (the height) and
   by the FRAGMENT shader (its slope), so it lives on its own. */
var BK_FIL = `
/* R45: THE FILLET, AND IT IS BRANCHLESS. t is R22's straight ramp in its
   own parameter — 1 on the board, 0 at the foot of the fall — and the two
   ends of it are rounded over a half-width while the middle is left
   exactly as it was. The rounding is the quadratic soft-max: over the
   half-width e about a corner it is (x + e)^2 / 4e and outside it it is
   max(x, 0) to the bit, which is what makes the plane between the two
   radii R22's plane and not an approximation of it. Written this way it
   is eight instructions with no branch in them, which matters: this is
   evaluated twice for every fragment of the sheet, and the sheet is most
   of the frame.
     bkQ  is that soft max(x, 0)
     bkDQ is its slope
   and the ramp is one of them nested in the other, once for the foot and
   once for the arris. */
float bkQ(float x, float e){ float w = max(e, 1e-5), u = max(1.0 - abs(x)/w, 0.0); return max(x, 0.0) + 0.25*w*u*u; }
float bkDQ(float x, float e){ float w = max(e, 1e-5), u = max(1.0 - abs(x)/w, 0.0); return step(0.0, x) - 0.5*u*sign(x); }
float bkFil(float t, float ei, float eo){
  return 1.0 - bkQ(1.0 - bkQ(t, eo), ei);
}
/* and its slope, in the same parameter */
float bkDFil(float t, float ei, float eo){
  return bkDQ(1.0 - bkQ(t, eo), ei)*bkDQ(t, eo);
}

/* DR1 concept B: WHICH BOARD'S FALL THE FRAGMENT IS IN, and how long that
   fall is. With one run for the whole desk the fragment shader could read
   it off the uniform; with a run per entry it cannot, so bkFall leaves the
   winning entry's run and foot here and the two places that want them read
   them back. Nought in the other two drapes — these lines are not emitted
   at all there, so the wrap's source is the source it was. */
float bkRun = 1.0;
float bkFoot = 1.0;
`;
/* DR2, 2026-09-21: there are no other drapes. The two lines above are
   always emitted now and they are the only run and foot the fragment side
   has. The sentence inside the comment is left as DR1 wrote it BECAUSE IT
   IS SHADER TEXT: this card's proof is that the emitted source does not
   move by a character, so nothing inside a template is reworded here. */
/* DR2, 2026-09-21: and `cardZ` / `cardG` were here — concept C's two
   separable profiles and the slope that made each panel one tone. They
   are deleted with the concept. See the card's derivation above. */

function BOOK_LIFT() { return `
/* THE FALL IS STRAIGHT, NOT EASED. A smoothstep off the board's edge is an
   S and an S has its gradient in the middle of it: the paper came out with
   a soft dark bruise along the margin instead of a fold. Paper does not do
   that — it creases at the board's arris, runs down as a FLAT plane, and
   creases again on the oak. A linear ramp is exactly that plane: one tone
   over the whole fall and a crease at each end, which is what reads. */
float bookM(vec2 p, vec4 r, float run, float fo){
  if (r.z <= 0.0) return 0.0;
  /* THE TRUE DISTANCE TO THE BOARD, so the fall off a CORNER is a cone and
     not a mitre. A mitre puts a 45-degree crease across the corner, and a
     45-degree crease on a grid whose columns and rows are axis-aligned is
     a staircase — six css px of it, plainly visible at 2x over the
     journal. Paper over the corner of a book makes a cone; a cone has no
     crease in it, so there is nothing for the grid to step. */
  vec2 d = abs(p - (r.xy + r.zw*0.5)) - r.zw*0.5;
  float sd = length(max(d, 0.0)) + min(max(d.x, d.y), 0.0) - ${DRAPE_HOLD.toFixed(1)};
  /* R65: and PAST THE FOOT OF THE FALL IT IS NOUGHT, so say so and stop.
     bkRamp (the fragment shader's half of the same ramp) has always had
     this line; the vertex shader's half did not, and R65 (a) asks it of
     every vertex of a folded sheet as well as a flat one. It is exact,
     not an approximation: at t = -eo the outer soft-max is max(t,0) to
     the bit and the inner is 1, so bkFil is already 0 there and beyond
     (the fit is diff 0 with this line in). */
  if (sd >= run + fo) return 0.0;
  return bkFil(1.0 - sd/run, uOverF.x/run, fo/run);
}
float bookOn(vec2 p, vec4 r){
  if (r.z <= 0.0) return 0.0;
  vec2 d = min(p - r.xy, r.xy + r.zw - p);
  return step(0.0, min(d.x, d.y));
}
` + undLiftGLSL() + `
/* ==== R65 (a): THE DESK UNDER THE PAPER IS NOT A PLANE ================
   Tommy, of the fold: "the moment you start folding at all the bottom
   left paper disappears under the top of the field guide currently".
   It did, and for one line: R58 presses the paper's own displacement out
   as the first crease turns (foldFlat) — and the drape over the books
   was inside that displacement, so the first degree of fold dropped the
   sheet's south-west corner off the field book onto the oak and the
   board came up through the map.
   The lie IS pressed out (a sheet being folded is held taut); the BOARD
   is not, because it is not the paper's line at all — it is what the
   paper is lying ON. So it comes out of paperH here on its own: nought
   over the oak, the cover's own height over a board, and R22's straight
   fall with R45's two radii between the two. sheetPos adds it back
   AFTER the fold, at the station the paper has LANDED at — which is the
   whole of (a): the packet is the quarter of the sheet that was lying on
   the field book, so the packet lies on the field book too, and every
   leaf folded on to it takes the same shoulder. ==================== */
/* LF1: the support, with the entries ALREADY measured. Every formula
   that wants the drape is written once, here, in terms of bookAt's two
   answers, and the vec2 form under it is the wrapper that measures them
   — so a caller that wants two of these formulas at one point (paperSurf
   wants this one and paperOf) pays for one pass over the entries and not
   one per formula. Nothing about the arithmetic moves. */
float boardOf(float top, float lift){ return (top + uOverZ.y)*lift; }
float boardH(vec2 p){ float t, l; bookAt(p, t, l); return boardOf(t, l); }
`; }
/* DR2, 2026-09-21: concept C's own boardH — the max() of the panel plane
   and the board's own plateau — and its cardLift stood at the foot of
   that template, behind `DRAPE_MODE === 'card'`. Both are deleted. The
   drape is paper, so the support is the plateau alone, which is the line
   R65 (a) wrote and the only line the template has now. */
/* ==== R22 (b): THE FALL IS A CREASE, NOT A BRUISE ====================
   R15 put the drape's shading in the VERTEX shader: a central difference
   of the height field at six units, interpolated across the cell. A fold
   in paper is a KINK — the surface turns through thirty degrees over
   nothing at all at the board's arris and back again where it meets the
   oak — and a kink is exactly what a linear interpolation across a
   nine-unit cell cannot carry. What came out was a soft grey wash lying
   across the south margin with no edge anywhere in it: Tommy, of the
   journal's corner, "a weird effect … on the paper over it".

   So the fall's gradient is taken HERE, per fragment, in closed form. The
   ramp is `1 - sd/run` off a rounded-box distance, so its gradient is
   just the box's own outward direction over the run — four lines, exact
   at any zoom, and the two kinks come out as the two creases a folded
   sheet has: the arris on the board's edge and the crease where the paper
   reaches the wood. R15's ease (uOverZ.w, .40 of the honest slope) is kept
   — the tone was right, it was the edges that were missing. */
function BOOK_FALL() { return `
float bkRamp(vec2 p, vec4 r, float run, float fo, out vec2 g, out float sd){
  g = vec2(0.0); sd = 1e6;
  if (r.z <= 0.0) return 0.0;
  vec2 c = r.xy + r.zw*0.5, d = abs(p - c) - r.zw*0.5;
  vec2 dir = vec2(0.0);
  if (max(d.x, d.y) > 0.0) {
    vec2 q = max(d, 0.0); sd = length(q);
    dir = sign(p - c)*q/max(sd, 1e-4);
  } else { sd = max(d.x, d.y); }
  /* the fall starts where the hold ends: the caller is handed the distance
     from the arris on */
  sd -= ${DRAPE_HOLD.toFixed(1)};
  /* R45: and it ENDS a foot's radius past the nominal foot, because the
     paper is still coming down there. Outside that the ramp is nought and
     so is its gradient, which is what lets the caller take the nearer
     board and be right. */
  if (sd >= run + fo) { g = vec2(0.0); return 0.0; }
  float ei = uOverF.x/run, eo = fo/run;
  float t = 1.0 - sd/run;
  /* R45: the gradient is the fillet's own slope, exact at every zoom — the
     two kinks R22 struck have become two radii, so what the light finds
     over a board's edge is a curve a few millimetres across and not a
     line with nothing either side of it. */
  g = -dir*bkDFil(t, ei, eo)/run;
  return bkFil(t, ei, eo);
}
/* R45: how far into one of the two radii a fragment is — 0 on the flat and
   on the plane between them, 1 at the middle of a radius. The top of a
   bend catches the light and the trough at its foot loses it, and both are
   a few millimetres wide; this is what carries them. */
vec2 bkCurve(float sd, float run){
  /* DR1 concept B: the run and the foot are the WINNING ENTRY's, which bkFall has just left behind it; the argument is kept so the two call sites are the two call sites. */
  run = bkRun;
  float ei = uOverF.x/run, eo = bkFoot/run;
  float t = 1.0 - sd/run;
  return vec2(1.0 - clamp(abs(t - 1.0)/max(ei, 1e-4), 0.0, 1.0),
              1.0 - clamp(abs(t)/max(eo, 1e-4), 0.0, 1.0));
}
/* which board the paper is coming off, its ramp, its gradient and how far
   the fragment is out from the board's edge — C0: generated over the
   UNDER list, by the NEARER entry, first wins a tie, exactly as the two
   named books resolved it */
` + undFallGLSL() + `
`; }
/* ====================================================================
   R23 — THE SHEET'S OWN CORNER IS THE WAY HOME

   Tommy, choosing this over the red drawing pin: "the sheet's own corner
   is decent. how would we indicate it's clickable on hover?"

   R14 put a pin in the north-east margin because v32 had one there. A pin
   is a drawn SYMBOL of an action — the one piece of chrome on a sheet that
   is otherwise all paper — and the answer to "how does it say it can be
   pressed" was a red head and a 2.5 px lift, which is a button's answer.
   The paper can answer it itself: a sheet that has been picked up and
   moved does not lie down flat again at the corner you lifted it by. So
   the NORTH-EAST corner LIFTS AND CURLS the moment the view leaves the
   fit, and lies down when it comes back. Nothing is drawn that is not
   paper; nothing is lettered; the only thing that says it can be pushed is
   that it is already off the desk and wants smoothing.

   IT IS R7'S SOUTH-EAST DOG-EAR, MIRRORED IN Y AND DRIVEN BY A LIFT.
   R7's construction, to the decimal: the triangle past the chamfer is
   reflected back across the fold line (`p -= q*0.62` along the fold's own
   normal), it rises on `0.20*c*sin(pi*t*0.92)`, its normal is the tangent
   of those two turned a quarter turn, and its shadow is MARCHED from its
   own height along the sun. Three things are new here and all three are
   the lift:

     · THE FOLD IS SCALED BY u. At u = 0 the reflection is the identity
       and the rise is zero, so the sheet at the fit is the sheet R7 drew,
       vertex for vertex — that is what makes the flat picture provable.
       At u = 1 it is R7's dog-ear exactly.
     · IT IS ITS OWN MESH, not the sheet's grid. The paper's grid is
       224 x 128, so its cells are 10.6 x 9.5 sheet units and a 45-degree
       fold line across them is a staircase with six steps in it — which
       the south-east corner has and which reads at 3x. This corner is a
       21 x 21 fan whose FIRST ROW IS THE FOLD LINE and whose two outer
       columns are the sheet's own north and east edges, so every boundary
       it has is exact at any zoom; and the face it came off is cut by a
       per-fragment `discard` on the same line, so the crease is a clean
       edge and not a row of cells.
     · THE DECKLE COMES WITH IT. The sheet was torn, not guillotined, and
       the torn outline is the furniture texture's own alpha: the curl
       samples it at the uv it was cut from, so the corner that lifts has
       the same rough edge it had lying down.
   ==================================================================== */
var NE_CURL = `
/* ==== S-B2: THE CORNER IS PEELED, NOT CHAMFERED =====================
   Tommy, review of 2026-09-19: "Currently it's a texture stuck on top --
   you can see a diagonal line where the graphic that represents the fold
   begins... I'd like it to be more like the actual paper itself starts
   coming up."

   The diagonal was real geometry, not a texture. R23's corner was a
   CHAMFER: a straight crease struck across the corner at 45 degrees with
   the strip past it rolled through a constant angle. A crease is a LINE,
   the eye finds a line instantly, and a line across a corner reads as
   something printed on the paper rather than as the paper.

   A finger under a corner makes no line at all. The paper comes up in a
   soft dome whose lift dies away with DISTANCE FROM THE CORNER'S OWN
   POINT, so what bounds it is a circular arc on which the paper is flat
   AND flat-sloped -- nothing to see in the shading, nothing to see in
   the silhouette. That is the whole of the change: q is radial now.

   neQ keeps its sign and its meaning, so every reader of it is unchanged:
   positive inside the peel, nought on its boundary, the radius itself at
   the corner's own point. ne.x is that radius. */
float neQ(vec2 p, vec4 r, float rad){
  return rad - length(p - vec2(r.x + r.z, r.y));
}
/* THE PROFILE, walked from the boundary (u = 0) to the corner's own point
   (u = 1). Three things are asked of it and each of them picks a term:

   NO CREASE -- the lift and both its derivatives are nought at u = 0, so
   the peel leaves the desk tangentially and there is nothing at all to
   see where it begins. u cubed is the cheapest curve that does it.

   IT DRAWS IN -- paper that stands up has to come from somewhere, so the
   station slides back towards the sheet by ne.w of the radius on the same
   cubic. Past a third of it the tip rolls back over itself, which is what
   a corner does once it is lifted far enough to show its own back.

   AND IT IS STILL RISING AT THE TIP -- the slope there is 3A and never
   nought, so the normal is defined at the one point where every row of
   the fan meets and there is no degenerate tangent to normalize.

   Returned: the drawn radius, the height, and the two components of the
   surface normal in the (radial, up) plane, unnormalized. */
vec4 neAt(float u, vec4 ne){
  float w = u*u*u, wp = 3.0*u*u;
  return vec4(ne.x*(1.0 - u) + ne.x*ne.w*w, ne.x*ne.y*w, ne.y*wp, 1.0 - ne.w*wp);
}
`;

var SE_CURL = `
/* ==== R45 (b): THE SOUTH-EAST CORNER IS A ROLL, NOT A DOG-EAR ========
   Tommy: "are we happy with how the curled up paper over the journal
   appears?"

   R7's corner is E4's SVG made solid: the triangle past the chamfer is
   REFLECTED back across the fold line and bowed on a sine. That is a
   dog-ear — a corner somebody folded over and pressed — and it shows as
   one: a flat triangle of one grey tone with a straight fold at its base
   and a plain underside. A sheet whose corner has lifted off a book has
   not been folded; it has CURLED, and a curl is a roll.

   THE SAME CONSTRUCTION AS R23'S NORTH-EAST CORNER, with one number more.
   The strip past the crease keeps its own length and bends through uSE.x
   radians, so it is an arc of radius (length / angle) and the paper never
   stretches. The number R23 did not need is the HOLD: a corner that
   curls does not begin to bend at the crease — the first part of it is
   still lying flat on what is under it and the roll is at the TIP, which
   is what makes a curl read as a curl and a fold read as a fold. So the
   first uSE.y of the strip is flat and the whole of the turn is taken
   over what is left, which gives a small tight roll standing off the
   paper instead of a long slow banana.

   At uSE.x nought this is not reached at all. */
vec3 seAt(float d, float len, float phi, float hold){
  float d0 = len*hold;
  if (d <= d0) return vec3(d, 0.0, 0.0);
  float ln = len - d0, R = ln/max(phi, 0.00001), f = (d - d0)/R;
  return vec3(d0 + R*sin(f), R*(1.0 - cos(f)), f);
}
`;

var CREASE_GLSL_BODY = `
/* THE SHEET WAS FOLDED IN EIGHT (survey-map.js :4676): three creases the
   long way and one across. A crease is a tent, not a line — the SVG drew a
   seven-unit lit band and a seven-unit dark one either side of each, which
   is a fold seen under a light nailed to the north-west. Here it is a
   shape, and the light that finds it is the sun.

   IT IS SHADED PER FRAGMENT, NOT PER VERTEX. A crease is seven sheet units
   wide and a grid fine enough to carry it would be 700 columns across the
   paper; at 128 the tent fell between vertices and the creases came out as
   a smudge or as nothing. The tent itself is a couple of units of height,
   which is under a pixel at every pose the desk has, so the mesh stays
   flat and the NORMAL is what is computed here — exactly, from the closed
   form, at every fragment.

   R7 gives the ridge a tent in the mesh as well (the grid's own columns
   fall on the creases, see above), but this stays exactly as E4 wrote it:
   the tent the mesh can carry is a cell and a half wide and this is the
   seven units the paper actually folds over. */
vec2 creaseGrad(vec2 p, vec4 r, float sg, float h){
  vec2 g = vec2(0.0);
  for (int i = 0; i < 3 + uZero; i++){
    float d = (p.x - (r.x + r.z*(0.25 + 0.25*float(i))))/sg;
    g.x += -2.0*d*h*uCreaseOn[i]*exp(-d*d)/sg;   /* CR2 */
  }
  float dy = (p.y - (r.y + r.w*0.5))/sg;
  g.y += -2.0*dy*h*uCreaseOn.w*exp(-dy*dy)/sg;
  return g;
}
vec3 creaseNormal(vec2 p, vec4 r, float sg, float h){
  vec2 g = creaseGrad(p, r, sg, h);
  return normalize(vec3(-g.x, -g.y, 1.0));
}
`;

/* THE SHEET'S TWO PROGRAMS. R7's paper costs about twelve more
   milliseconds to compile than E4's, and frame 1 draws the sheet — so
   frame 1 gets E4's and the R7 one is issued with the other eleven,
   after the picture, and taken up the moment the driver has it
   (ROOM-REVIEW-1 R7: "split as R6 does if it grows frame 1"). Nothing is
   drawn and thrown away by this: at the desk pose the two paint the SAME
   PIXELS — measured, 0 of 1920 x 620 differ at tilt 0 — because at that
   pose the lie, the ridge and the cut edge are all under a pixel. They
   part at tilt, and a deep link into the room fetches place.js first,
   which is longer than the compile. */

/* =====================================================================
   X2 — THE SHEET FOLDS ALONG ITS OWN CREASES

   The paper here has been folded in eight since E4: three creases the long
   way, at a quarter, a half and three quarters of its width, and one
   across. Those creases are not decoration — they are the record of how
   this sheet was folded, and folding it again is nothing more than
   putting it back the way those creases say.

   A map on a desk is folded in three moves, and only two of the four
   creases are hinges: you bring the east half over on the middle crease
   (which lays the quarter crease exactly on the three-quarter one — which
   is WHY a sheet folded this way has creases at both), you fold the pair
   again on the quarter crease, and then you bring the north half down on
   the crease across. Four panels wide by two deep: eight, which is the
   number of panels this sheet's creases have always described.

   EACH FOLD IS A RIGID TURN, not a shape. A point past the crease is
   rotated a half turn about the crease line, in the plane at right angles
   to it — so the paper keeps its length, arrives face down on its
   neighbour, and passes through vertical on the way exactly as paper
   does. Nothing is interpolated towards a target shape and nothing is
   scaled: at a third of a turn the flap really is standing at sixty
   degrees, which is what makes it read as paper and not as a transition.

   THE GATE IS THE POSITION AS IT STANDS, not the station it started at.
   The second fold takes everything that is still east of the quarter
   crease AFTER the first has landed — which is the second and third
   panels, and not the fourth, because the fourth has already been carried
   west of it. That one line is the difference between a fold and a shear.

   AND THE PACKET HAS A THICKNESS. Each fold lifts what it moves clear of
   what is already lying there: one leaf for the first (two deep), three
   for the second (four deep), seven for the third (eight deep) — the
   arithmetic of a stack that turns over every time it is folded, since a
   turn reverses the order of what it carries and the rotation has already
   done that to the z of every point in it. The lift comes in on
   (1 - cos) so it is nought while the flap is still standing up and full
   only when it has lain down.

   The leaf is 0.9 sheet units thick, about 0.6 mm — a packet of eight is
   four and a half millimetres, which is a folded map.
   ===================================================================== */
/* ==== R47: AND THE THREE TURNS ARE ONE FUNCTION, `foldPt` ============
   X2 wrote the gates inside `sheetPos`, which is the sheet's own; the
   RULER is print on the same paper and has to be folded by exactly the
   same arithmetic, or the month names walk off the panel they are
   printed on. So the gates, the frame and the bight are one function
   here, and `RoomObjects.foldGLSL` hands this whole string to ruler.js —
   there is ONE copy of the fold in the build and both shaders compile it.

   THE BIGHT (R47). X2 lifted each panel clear of the stack under it by
   the leaves it has to clear, and lifted ALL of it by the same amount —
   so at the crease itself the moved paper stood a leaf (or seven) clear
   of the paper it had just been folded on to, and you could see straight
   into the gap. Paper does not do that: it turns through the fold over a
   bight as round as the stack is thick. The lift is ramped in over that
   run instead, which closes the gap, tapers the packet's folded edges
   and costs one smoothstep. At the first fold the run is 1.4 sheet units
   (under a css pixel — nothing to see, which is right for two leaves);
   at the last it is ten, which is the rounded spine of a folded map.
   ==== */
var FOLD_GLSL = `
  uniform vec4 uFoldC;       // X2: the two long creases, the one across, a leaf's thickness
  uniform vec3 uFoldA;       // X2: the three turns, in radians
  uniform float uFoldOn;     // X2: how far the sheet has been folded, 0..1
  uniform float uFoldS;      // CF3: the paper held taut after the last leaf has landed, 1..0
  /* the pile's lift WITHOUT the bight, and whether this turn is in the air */
  vec2 foldPile(float th, float leaves){
    return vec2(uFoldC.w*leaves*(1.0 - cos(th))*0.5,
                (th > 0.0001 && th < 3.14149) ? 1.0 : 0.0);
  }
  /* ==== R65 (b): THE BIGHT IS A ROLL, AND EVERY LEAF HAS ITS OWN RADIUS
     Tommy: "when the last horizontal fold is done you can see a sliver of
     map to the right of it poking through".

     WHAT IT WAS. R47's bight was ONE ramp for the whole stack: the turn
     was a rigid rotation about the crease AT THE DESK (q.z*k, which at
     a half turn is -q.z) plus a lift of the stack's whole thickness
     eased in over the bight's run. Away from a crease that is exactly
     right — it gives the true pile, 0, 3, 2, 1 west to east and
     B, C, D, A bottom to top after the third turn, which is how a map
     folded in eight really stacks. AT the crease it is upside down: the
     lift is nought there, so every leaf comes back to MINUS the height it
     was at, and the leaf that was lowest (the outermost, which has to get
     round the whole stack) dives deepest while the leaf that was highest
     (the innermost, which only has to get round itself) stays up. Within
     the bight's run the eight leaves are in REVERSE order, and the one
     that surfaces is the second (B north), which has taken two half turns
     and is therefore printed side up. That is the sliver: four device
     pixels of map along the packet's north edge and down its east, at
     his dpr — 972 of them, measured.

     WHAT IT IS NOW. A bight is a roll, and its radius is not the stack's:
     it is THIS leaf's own distance from the turn's middle. The outermost
     leaf wraps a cylinder of half the stack's thickness and its spine
     bulges that far past the crease; the innermost wraps its own hair's
     breadth and hugs the leaf under it. Nested, by construction — the
     outer leaf's bight encloses every inner one, so the packet's edge is
     the outermost leaf's rounded spine and nothing beneath it can show.
     The paper is inextensible: the first r*a of it is ON the cylinder
     and the rest is straight and tangent, which is R45's own roll
     (seAt) written for a crease instead of a corner.

     pile is the leaf's own height, carried apart from q.z so the
     radius is the LEAF's and never the paper's — a sheet draped over the
     journal is 84 sheet units off the desk at the first degree of fold
     and that is the paper's line, not the stack's. The paper's own line
     rides the roll on the rotated normal, so it mirrors exactly as R58
     says it does and eases out exactly as R58 eases it. ============= */
  void foldRoll(inout vec3 q, inout vec3 n, inout vec3 fn, inout vec3 ft,
                inout float pile, float c, float th, float leaves, bool longWay){
    float zc = uFoldC.w*leaves*0.5;              // the middle of this turn
    float r  = max(zc - pile, uFoldC.w*0.25);    // this leaf's own bight
    float lo = q.z - pile;                       // the paper's own line off it
    float d  = longWay ? (q.x - c) : (c - q.y);  // into the moving side, >= 0
    float a  = min(d/r, th);                     // how far this station has wrapped
    float sa = sin(a), ka = cos(a);
    float L  = d - r*a;                          // what is left straight past it
    float out1 = r*sa + L*ka;                    // out from the crease, in plane
    float z  = pile + r - r*ka + L*sa + lo*ka;   // and up off the desk
    if (longWay) { q = vec3(c + out1 - lo*sa, q.y, z);
                   n  = vec3(n.x*ka - n.z*sa, n.y, n.x*sa + n.z*ka);
                   fn = vec3(fn.x*ka - fn.z*sa, fn.y, fn.x*sa + fn.z*ka);
                   ft = vec3(ft.x*ka - ft.z*sa, ft.y, ft.x*sa + ft.z*ka); }
    else         { q = vec3(q.x, c - out1 + lo*sa, z);
                   n  = vec3(n.x, n.y*ka + n.z*sa, -n.y*sa + n.z*ka);
                   fn = vec3(fn.x, fn.y*ka + fn.z*sa, -fn.y*sa + fn.z*ka);
                   ft = vec3(ft.x, ft.y*ka + ft.z*sa, -ft.y*sa + ft.z*ka); }
    pile += 2.0*r;                               // where this leaf has landed
  }
  /* the point, its normal, and the panel's own frame — the ONE place the
     three creases are gated, and the gate is the position AS IT STANDS */
  vec3 foldPt(vec3 q, inout vec3 n, out vec3 fn, out vec3 ft){
    fn = vec3(0.0, 0.0, 1.0); ft = vec3(1.0, 0.0, 0.0);
    if (uFoldOn > 0.0) {
      float pile = 0.0;
      if (q.x > uFoldC.x) foldRoll(q, n, fn, ft, pile, uFoldC.x, uFoldA.x, 1.0, true);
      if (q.x > uFoldC.y) foldRoll(q, n, fn, ft, pile, uFoldC.y, uFoldA.y, 3.0, true);
      if (q.y < uFoldC.z) foldRoll(q, n, fn, ft, pile, uFoldC.z, uFoldA.z, 7.0, false);
    }
    return q;
  }
  /* ==== R58: A SHEET BEING FOLDED IS A SHEET BEING PRESSED ===========
     Tommy, 2026-09-14: "the right hand side of paper goes UNDER some of
     the left hand side". It did, and the reason is a number: the paper's
     LIE (objects.js LIE[0]) is 3.1 sheet units of undulation, and a leaf
     of this paper is 0.9. A fold MIRRORS the z it is given (foldX at a
     half turn takes q.z to -q.z), so every hill in the east half became a
     trough under the west half's own hills, three units deep against a
     nine-tenths lift — and the half that had just been folded over sank
     through the half it was lying on, in exactly the soft organic patches
     the lie is shaped in. No leaf thickness a sheet of paper could have
     would win that; the lie has to go.
     And it should: a map lying loose on a desk undulates, a map being
     folded is held taut and pressed flat along its creases. So the whole
     of the paper's own displacement — the lie, the crease ridges, the
     drape over the books, the south-east roll — eases out as the first
     crease turns, and is gone by the time that crease is past the
     vertical. At uFoldOn nought this is not reached (the flat sheet's
     line is its own line, and the fit is diff 0). ==== */
  /* ==== CF3 (2026-09-23): AND IT COMES BACK AFTER THE LANDING, NOT
     DURING THE DESCENT. Held paper is taut; released paper relaxes. On
     the unfold the leaves fall on their own (engine.js Room.unfold) and
     the first crease reaching nought is the last leaf touching down, so
     the angle term alone put the lie and the drape back while that leaf
     was still in the air. uFoldS is the settle objects.js drives from
     the unfold (O.foldSettle): one for the whole of the unfold, easing to
     nought over the 450 ms after the last leaf lands. On the FOLD it is
     nought and this is R58 to the bit. ==== */
  float foldFlat(){
    return (uFoldOn <= 0.0) ? 0.0 : max(clamp(uFoldA.x/1.75, 0.0, 1.0), uFoldS);
  }
  /* R47: HAS THIS POINT'S PANEL LEFT THE DESK? Nought on the paper that
     is still lying flat, one the moment its own crease has any angle —
     the cut edge asks it (a sheet picked up is not resting on the wood)
     and so does the ruler (print on a flap is in front of everything). */
  float foldUp(vec2 p){
    if (uFoldOn <= 0.0) return 0.0;
    float a = 0.0;
    if (p.x > uFoldC.x) a = max(a, uFoldA.x);
    if (p.x > uFoldC.y) a = max(a, uFoldA.y);
    if (p.y < uFoldC.z) a = max(a, uFoldA.z);
    return clamp(a*6.0, 0.0, 1.0);
  }
`;
/* R47: and the instanced quads' own gate, which ONLY ruler.js compiles —
   the sheet's program is not given a function it cannot call. */
var FOLD_INST_GLSL = `
  /* R47: THE SAME THREE TURNS, GATED ON A POINT THAT IS NOT THE ONE BEING
     MOVED. The sheet is a mesh laid out so that no triangle straddles a
     crease, so every vertex can be asked for itself. An INSTANCED quad —
     a ruler's block, a month's name — is one rectangle that belongs to
     one panel however its corners fall, so its own centre is asked once
     and all four corners go where that answer sends them. The gate point
     is carried through the same turns (it is at z = 0, so a turn is one
     multiply on it), and foldX, foldY, foldF and the bight below are the
     sheet's own — there is one fold in this build. */
  /* AND WHERE THE INSTANCE ENDS UP IN THE STACK. st.x is the panel's
     own place in the pile, in leaves — the lift without the bight's ramp
     on it, so a graduation set within a few units of a crease is not
     mistaken for a leaf below. st.y is one while any of the turns this
     instance is riding is still SOMEWHERE BETWEEN nought and a half turn:
     a panel in the air is above everything on the desk by construction,
     and a panel that has landed has to earn its place (ruler.js). */
  vec3 foldAt(vec3 q, vec2 g, inout vec3 n, out vec3 fn, out vec3 ft, out vec2 st){
    fn = vec3(0.0, 0.0, 1.0); ft = vec3(1.0, 0.0, 0.0); st = vec2(0.0);
    if (uFoldOn > 0.0) {
      float pile = 0.0;
      if (g.x > uFoldC.x) { foldRoll(q, n, fn, ft, pile, uFoldC.x, uFoldA.x, 1.0, true);
                            g.x = uFoldC.x + (g.x - uFoldC.x)*cos(uFoldA.x);
                            st += foldPile(uFoldA.x, 1.0); }
      if (g.x > uFoldC.y) { foldRoll(q, n, fn, ft, pile, uFoldC.y, uFoldA.y, 3.0, true);
                            st += foldPile(uFoldA.y, 3.0); }
      if (g.y < uFoldC.z) { foldRoll(q, n, fn, ft, pile, uFoldC.z, uFoldA.z, 7.0, false);
                            st += foldPile(uFoldA.z, 7.0); }
    }
    return q;
  }
`;
var FOLD_SHEET_GLSL = `
  vec4 sheetPos(vec2 p, float z, inout vec3 n){
    vFN = vec3(0.0, 0.0, 1.0); vFT = vec3(1.0, 0.0, 0.0);
    /* R47: the flat sheet's own line, whole, so a page with nothing
       folded compiles to the arithmetic it compiled to before the fold
       was ever written (the fit is diff 0, not diff small) */
    if (uFoldOn <= 0.0)
      return uVP * vec4(uSheet.x + p.x*uSheet.z, -(uSheet.y + p.y*uSheet.z), z*uSheet.z, 1.0);
    vec3 fn, ft;
    vec3 q = foldPt(vec3(p, z), n, fn, ft);
    vFN = fn; vFT = ft;
    /* R65 (a): AND THE DESK IT LANDS ON. The caller took the board out of
       the paper's own height before the fold (it is not the paper's line,
       it is what the paper rests on, and R58's press must not reach it);
       it goes back HERE, at the station the paper has landed at. So the
       quarter of the sheet that was lying on the field book is still
       lying on it when it is the packet, and every leaf folded on to that
       quarter takes the board's height over the board's footprint with
       the flat sheet's own shoulder, and lies on the oak everywhere else. */
    /* FD1, 2026-09-20. Tommy: "it's annoying that the map doesn't deform
       over the journal whilst the sheet itself does ... it's like there's
       a final unfolded state that ... the paper locks onto the field
       guide + journal". R80 took this support out for the whole of the
       fold and put a single plane 32 mm over the oak in its place; that
       plane is what locked on and off at either end of the gesture. It is
       gone. The sheet is on what it is lying on at every t, which is what
       the line above has always said. */
    /* ==== DR1 (2), 2026-09-21: AND A LIFTED LEAF CARRIES NO BOARD ====
       Tommy: "not to keep fussing about the journal coming through the
       page but once the map has folded up enough (a very small amount)
       then the distortion from the journal underneath should go?"

       It did not. The line above adds the board's height at the station
       the leaf has LANDED at for the whole of the gesture, so a leaf
       standing in the air was still carrying the shape of whatever its
       landing station happens to be over — the journal's shoulder drifted
       across a panel that was nowhere near the journal.

       A leaf only rests on something while it is LYING on the desk, so
       the support is scaled by how flat that leaf is: fn is the leaf's
       own normal, already in hand, and abs(fn.z) is one at rest, one
       again once a panel has turned the whole way over on to the packet,
       and falls away over the first few degrees of that leaf's own turn.
       So the packet at the end still lies on the field book (R65 a), the
       sheet at rest is the sheet at rest to the bit — smoothstep is
       exactly 1 at fn.z = 1 — and nothing in between is holding up a
       book it is not touching. 0.94 is twenty degrees and 0.995 is six:
       the shape is gone by the first quarter turn. */
    q.z += boardH(q.xy)*smoothstep(0.94, 0.995, abs(fn.z));
    return uVP * vec4(uSheet.x + q.x*uSheet.z, -(uSheet.y + q.y*uSheet.z), q.z*uSheet.z, 1.0);
  }
`;

function SHEET_SRC(R7) {
  var SHEET_VS = `#version 300 es
  precision highp float;
  ` + R7 + `
  layout(location = 0) in vec2 a;
  #ifdef R7
  layout(location = 1) in vec3 b;    // the cut edge: its outward xy, and 1 at the oak
  #endif
  out vec2 vUv; out vec2 vSheet; out float vBack; out float vQ;
  /* FD2, 2026-09-20: WHERE THIS STATION'S PAPER WAS WHEN THE PRINT WAS
     STRUCK. R58's raster is a picture of the flat sheet taken through the
     reader's own camera, so a fragment that asks it for its ink has to ask
     at the point of the PAPER it was printed on — not at the plane under
     it. The sheet's own print rides the lie and the drape over the two
     books, and asking at z = 0 read it back a parallax off: Tommy, "the
     raised parts of the sheet suddenly get much higher as witnessed by
     the deformation around the field guide in the 'section along the
     line'" — the fall over the field book was being drawn twice. This is
     the rest height, before R58 presses it out, and the fragment shader
     hands it to uPrintM. */
  out float vPH;
  /* X2: THE FOLD'S OWN FRAME. The face's normal is not a vertex normal —
     it is struck per fragment from the creases' and the lie's gradient, in
     the SHEET's axes — so a panel that has been turned over needs those
     axes turned with it or it is lit as though it were still lying flat
     (which comes out as a panel that has gone grey). Two rotated axes are
     the whole of it: the sheet's own up and its own east, carried through
     exactly the same turns the positions take. Inside a panel the turn is
     constant and no triangle straddles a crease, so these interpolate
     exactly and cost the fragment shader one cross product. */
  out vec3 vFN; out vec3 vFT;
  #ifdef R7
  out vec3 vN; out vec3 vG;      // the lie's gradient, and its mask
  #endif
  uniform vec4 uRect;        // the sheet, in sheet units
  uniform vec3 uSheet; uniform mat4 uVP;
  uniform vec3 uFold;        // curl, crease sigma, crease height
  uniform float uWarp;
` + undDecl() + `  uniform vec2 uSE;          // R45: the south-east roll: its angle in radians, and how much of the strip stays flat
  /* R65 (a): the books come FIRST now — sheetPos asks boardH for the
     desk the folded paper lands on, and GLSL wants it declared by then. */
  ` + BK_FIL + BOOK_LIFT() + `
  ` + FOLD_GLSL + FOLD_SHEET_GLSL + SE_CURL + `
  #ifdef R7
  uniform vec4 uNeat;        // the neat rule, in sheet units — nothing lifts inside it
  uniform vec3 uUnd;         // the lie: amplitude, feather, crease ridge
  uniform float uSkirt;      // this draw is the cut edge
  uniform float uDeskZ;      // the oak, in sheet units (world -0.5)
  uniform vec4 uNE;          // S-B2: the peel: its radius, its height as a fraction of it, the lift 0..1, its draw-in
  uniform float uCorner;     // R23: this draw is the curled corner itself
  ` + PAPER_LIE + NE_CURL + `
  #endif
  void main(){
    vec2 p = uRect.xy + a*uRect.zw;
    vBack = 0.0; vQ = 0.0;
    float curl = uFold.x;
    float z = 0.0;
    /* R45: a rolled flap samples the sheet at the station it was CUT
       from, not at the one it has rolled back to — otherwise the print
       and the deckle slide over the paper as the corner turns */
    bool rolled = false;
  #ifdef R7
    vN = vec3(0.0, 0.0, 1.0); vG = vec3(0.0);
    if (uCorner > 0.5) {
      /* R23: THE NORTH-EAST CORNER, ON ITS OWN MESH. The attribute b.x is
         q/c, walked from 0 at the crease to 2 at the corner's own point.
         The strip is rolled, not folded: it keeps its length, it leans over
         and its free edge — the deckle the sheet was torn with — comes with
         it, so the sheet is never cut and there is never a hole in it. */
      /* S-B2: the fan is POLAR now. b.x walks 0 at the peel's boundary to
         1 at the corner's own point; b.y walks the quarter turn from the
         sheet's north edge round to its east edge, so both of the edges
         the peel bends are exact and its free edge is still the deckle
         the sheet was torn with. The station is built here rather than
         carried in the attribute because the RADIUS breathes with the
         tug, and a mesh cut for one radius would leave a ring of paper
         drawn twice at every other one.

         R51's rule stands and is now explicit: the print is sampled at
         the uv the paper was CUT from, not the one it has drawn in to,
         so the survey, the rules and the torn edge all ride the peel. */
      float tq = b.x, th = b.y*1.5707963;
      vec2 cp = vec2(uRect.x + uRect.z, uRect.y);
      vec2 er = vec2(-cos(th), sin(th));        /* away from the corner, along the paper */
      vec4 rl = neAt(tq, uNE);
      vec2 cut = cp + er*(uNE.x*(1.0 - tq));    /* the station it was cut from */
      p = cp + er*rl.x;                         /* and where that paper has drawn in to */
      vN = normalize(vec3(er*rl.z, rl.w));
      vBack = 3.0; vQ = tq*0.5; vSheet = p; vG = vec3(0.0);
      vUv = (cut - uRect.xy)/uRect.zw;
      float zc = rl.y + paperH(p, uRect, uNeat, uUnd, uFold);
      vPH = zc;                                    /* FD2: the print was struck here */
      if (uFoldOn > 0.0) zc -= boardH(p);          /* R65 (a): sheetPos adds it */
      gl_Position = sheetPos(p, zc, vN);
      return;
    }
    if (uSkirt > 0.5) {
      /* THE CUT EDGE hangs from the lip to the oak. Its foot is the desk's
         own plane, so the sheet meets the wood and does not hover over it. */
      float lip = paperH(p, uRect, uNeat, uUnd, uFold);
      /* R15: and where the paper's edge is over a board it stops ON the
         cloth — the band is the paper's own thickness there and the whole
         two millimetres only where it is over the oak. */
      float foot = mix(uDeskZ, bookTop(p), undOn(p));
      /* JW1 (2026-10-01): BUT IT IS PAPER, NOT A WALL. Where a book's drape
         holds the edge up a book's thickness, a band hung all the way to
         the oak was a dark wall standing under the sheet: the near-black
         wedge between the field book and the journal, growing to the
         journal, with a hard straight foot. A sheet held up shows the wood
         under it. The band reaches down the lie's own height at most
         (2.47 mm at its highest, 7 sheet units) so the flat sheet still
         meets the wood and a held-up one has air under it. */
      foot = max(foot, lip - 7.5);
      /* R65 (a): both of them are measured from the OAK, and sheetPos
         is about to add the board under this edge; take it off both so the
         band is the same band it was and the cut edge follows its own
         panel on to the book instead of standing a cover's height over it. */
      if (uFoldOn > 0.0) { float bh = boardH(p); lip -= bh; foot -= bh; }
      vSheet = p; vBack = 2.0; vQ = b.z; vN = normalize(vec3(b.xy, 0.26)); vUv = a;
      /* X2: AND A FOLDED SHEET'S CUT EDGE IS NOT ON THE OAK ANY MORE.
         The band hangs from the lip to the wood; the moment the paper is
         picked up to be folded it comes off the desk.

         R47: AND WHAT IT COMES OFF THE DESK WITH IS ITS OWN THICKNESS.
         X2 meant to say that and did not: mix(lip, foot, b.z*0.0) is
         lip at BOTH ends of the band, so every edge of a sheet that had
         begun to fold was a degenerate strip nought units deep — which is
         why the folded packet came out as a flat cream rectangle with no
         side to it at all, and why a panel standing on its edge had
         nothing but its own back to show. The foot of the band goes to a
         LEAF below the lip instead, and the whole band is turned with the
         panel it belongs to, so it is the paper's underside where the
         panel is still the right way up and its topside where the panel
         has turned over. Eight of them stacked a leaf apart tile into the
         edge of a folded map, which is the one thing on this desk that
         says the packet is eight leaves and not one.

         And it is taken panel by panel, not sheet by sheet: the west half
         is still lying on the wood while the east half is in the air, and
         its edge must still meet the wood. foldUp is that question. */
      float up = foldUp(p);
      vG.x = up;
      /* ==== SH1, 2026-09-22: AND WHERE THE LIP IS HELD UP BY THE DRAPE,
         THIS BAND IS THE HOLLOW UNDER IT, NOT A CUT EDGE ================
         Off a board (undOn nought) but inside its drape (bookLift above
         nought) the lip is still up at the cover's height while the foot
         is on the oak, so this band stands a book's thickness tall — and
         what it stands for there is not two millimetres of rag core, it is
         the gap between a hanging sheet and the wood, which the sheet's own
         plateau keeps out of the light. Tommy, of the south edge west of
         the journal: "It's darker than the sheets, but it's substantially
         lighter than the table. If it was darker like the table, I
         wouldn't have a problem." vG.y carries how much of this station is
         that hollow; it is exactly nought on every edge that is not beside
         a board (the flat sheet's rim, the edge resting ON a cover), so
         those are painted as they were. */
      vG.y = smoothstep(0.0, 0.35, bookLift(p))*(1.0 - undOn(p));
      float zz = mix(lip, mix(foot, lip - uFoldC.w, up), b.z);
      vPH = zz;                     /* FD2: the cut edge reads no print, but say it */
      gl_Position = sheetPos(p, zz, vN);
      return;
    }
  #endif
    if (uWarp > 0.5) {
      /* THE TURNED CORNER. The triangle past the chamfer is the paper that
         is not there any more: it is folded back over the face. Reflecting
         it across the fold line IS the fold — the same triangle, the same
         size, the wrong way round, which is what a turned corner is. The
         .62 is the control point the SVG's own curl-back is bowed to. */
      float q = (p.x - (uRect.x + uRect.z - curl)) + (p.y - (uRect.y + uRect.w - curl));
      /* R22 (c): AND THE FACE STOPS EXACTLY ON THE FOLD. uWarp 2 is the
         sheet's own grid: every station past the fold slides back down the
         diagonal until q is zero, so the triangles that straddle it end
         flush with the fold instead of dragging a cell of paper over it.
         The boundary is then the line itself at any zoom, and the flap is
         drawn from its own mesh (uWarp 1, buildCurl). */
      if (uWarp > 1.5) { p -= vec2(max(q, 0.0), max(q, 0.0))*0.5; q = 0.0; }
      if (q > 0.0 && uSE.x > 0.0) {
        /* R45: THE ROLL. q is the sum of the two distances past the
           chamfer, so the perpendicular distance along the strip is that
           over root two and every length below is a real length. The
           station is carried round the arc: it draws IN to seAt.x from
           the crease, it stands seAt.y off the paper, and the paper it
           is made of is neither stretched nor cut. */
        float len = 2.0*curl*0.70711, d = q*0.70711;
        vec3 rl = seAt(d, len, uSE.x, uSE.y);
        p -= vec2(0.70711, 0.70711)*(d - rl.x);
        z = rl.y;
        vBack = 1.0; vQ = clamp(rl.z/uSE.x, 0.0, 1.0); rolled = true;
  #ifdef R7
        /* the normal is the tangent turned a quarter turn, which for a
           roll is the bend angle itself: flat where the strip is still
           lying down, over the vertical at a right angle, and facing the
           desk once the paper has turned past one */
        vN = normalize(vec3(-sin(rl.z)*0.70711, -sin(rl.z)*0.70711, cos(rl.z)));
  #endif
      } else if (q > 0.0) {
        float t = clamp(q/curl, 0.0, 1.0);
        p -= vec2(q, q)*0.62;
        z = curl*0.20*sin(3.14159*t*0.92);
        vBack = 1.0; vQ = t;
  #ifdef R7
        /* R7: AND IT IS CURVED, so it has a normal. The surface runs back
           along the diagonal at -.24 of the station it came from while it
           rises on that sine: the tangent of those two, turned a quarter
           turn, is what the light meets. E4 shaded the back flat, which is
           why it read as a paper triangle laid on the paper. */
        float c = cos(3.14159*t*0.92);
        vec2 nz = normalize(vec2(0.8174*c, 0.24));
        vN = normalize(vec3(nz.x*0.7071, nz.x*0.7071, nz.y));
  #endif
      }
    }
  #ifdef R7
    z += paperH(p, uRect, uNeat, uUnd, uFold);
    vPH = z;                        /* FD2: the print was struck on this face */
    /* R58: and the fold presses it out — R65 (a): all of it EXCEPT the
       board. foldFlat is nought and this whole line is not reached
       until there is a crease in the paper, so the flat sheet's z is the
       float it always was, to the bit (the fit is diff 0, not diff
       small); once there IS a crease the board comes off here and
       sheetPos puts it back at the station the paper has landed at. */
    if (uFoldOn > 0.0) z = (z - boardH(p))*(1.0 - foldFlat());
    /* the lie's own gradient, taken here and interpolated: it is one
       wavelength in eighteen hundred units and the grid carries it exactly,
       so the fragment shader pays nothing for it */
    /* R15: SIX, not sixteen. The central difference is what shades the
       fold, and at sixteen units it straddled three of the grid's cells
       and smeared the crease at the board's edge over twenty css px. Six
       is inside one cell, so the crease is a cell wide — which is all the
       mesh can carry anyway — and the lie, one wavelength in eighteen
       hundred units, does not notice the difference. */
    float e = 6.0;
    /* R22 (b): the LIE's gradient only. The drape's is a kink and belongs
       in the fragment shader (BOOK_FALL); taken here it was smeared over
       a cell and read as a stain. */
    vG = vec3(lieH(p + vec2(e,0.0), uRect, uNeat, uUnd) - lieH(p - vec2(e,0.0), uRect, uNeat, uUnd),
              lieH(p + vec2(0.0,e), uRect, uNeat, uUnd) - lieH(p - vec2(0.0,e), uRect, uNeat, uUnd),
              0.0)/(2.0*e);
    vG.z = pmask(p, uNeat, uUnd.y);
  #else
    /* R15 on E4's paper as well. Frame 1 is drawn with this program and
       R7's is taken up a few frames later (O.precompile): if only R7's
       carried the drape the two books would be ON the sheet for the
       first picture and UNDER it for the second. The shading of the
       drape is R7's — the E4 sheet has no normal to give it — but the
       stacking is the same in both. */
    /* R65 (a): and only while the paper is flat — with a crease in it
       sheetPos adds the board at the station the paper has landed at. */
    if (uFoldOn <= 0.0) z += boardH(p);
    vPH = z;
  #endif
    vSheet = p;
    /* R22 (c): the texture rides the COLLAPSED station, not the one the
       grid started at — otherwise the sliver triangles left flush with the
       fold would sample the paper path from past it, where its alpha is
       nought, and discard the very edge that was just made straight. */
    vUv = rolled ? a : (p - uRect.xy)/uRect.zw;
  #ifdef R7
    gl_Position = sheetPos(p, z, vN);
  #else
    vec3 nDummy = vec3(0.0, 0.0, 1.0);
    gl_Position = sheetPos(p, z, nDummy);
  #endif
  }`;

  var SHEET_FS = `#version 300 es
  precision highp float; precision highp sampler2D;
  ` + R7 + `
  in vec2 vUv; in vec2 vSheet; in float vBack; in float vQ;
  in float vPH;                  // FD2: the paper's own face, where the print was struck
  in vec3 vFN; in vec3 vFT;      // X2: the fold's own frame
  #ifdef R7
  in vec3 vN; in vec3 vG;
  #endif
  out vec4 o;
  uniform sampler2D uFurn;
  uniform float uHasFurn;
  uniform vec4 uRect;
  uniform vec3 uLight;       // the sun as a direction in sheet space
  uniform vec4 uLampS;       // R42 (a): the lantern in sheet units: x, y, and the pool's two radii
  uniform float uLampN;      // and the night, which is nought by day
  uniform vec4 uSun;         // dx, dy, len, cast
  uniform vec3 uFold;
  uniform float uPxPerSheet;
  uniform float uFoldOn;     // X2: how far the sheet has been folded, 0..1
  /* ==== R58: THE PRINT, AS ONE RASTER =================================
     While there is a fold in the sheet the face's ink does not come out
     of uFurn any more: it comes out of ONE picture of the whole flat
     sheet — the paper, the furniture, the rulers AND the map's own
     picture — struck at the moment the fold began and glued to the paper
     ever since. uPrintM carries a point in SHEET units straight to that
     picture's clip space (it is the flat sheet's own view-projection with
     the sheet's placement folded into it), so a fragment asks for the ink
     that was printed AT ITS OWN STATION and gets it wherever the panel it
     is on has since been turned to. That is what print on paper does, and
     it is why the map goes up with the flap instead of being scissored
     off it. uUnlit is the one frame that MAKES the picture: the sheet
     draws its albedo and nothing else, so the light is not in the raster
     twice. ==== */
  uniform sampler2D uPrint;
  uniform mat4 uPrintM;
  uniform float uPrintOn;
  uniform vec4 uScr;         // PR1: gl_FragCoord to the raster's uv, as scale xy and offset zw
  uniform float uScrW;       // PR1: how far every panel has gone over to its station
  float gPrinted = 0.0;      // LB1: this fragment's ink came off the print (the lantern is in it)
  uniform float uUnlit;
${COVER_ON ? `  /* ==== CV1: THE COVER PANEL, PRINTED ON THE BACK OF THE PAPER ======
     A folded survey sheet is not blank on top. The panel that ends up
     uppermost in the packet carries the sheet's name on its BACK, printed
     there when the sheet was printed, because that is the side of the
     paper the reader is left holding. uCover is that one strike; uCoverOn
     is nought until it has landed and on the frame that makes the print
     raster. See THE COVER below for which panel it is and why. ==== */
  uniform sampler2D uCover;
  uniform float uCoverOn;` : ''}
  /* R79: the live date mark is ink in this pass, not a DOM plane. x0/x1,
     rule/top are the bracket; the second vec4 begins with the contiguous
     marked-year span. The printed glyphs themselves remain in uFurn. */
  uniform vec4 uDate;
  uniform vec4 uDateYear;
  uniform vec4 uFoldC;       // the two long creases, the one across, a leaf
  uniform vec3 uFoldA;       // the three turns, in radians
  #ifdef R7
  uniform vec3 uEye;         // where the camera is, in sheet units
  uniform float uSpec;       // how much sheen the sky has to give
` + undDecl() + `  uniform vec4 uNE;          // S-B2: the peel: its radius, its height fraction, the lift, its draw-in
  ` + BK_FIL + BOOK_FALL() + `
  #endif
  uniform vec2 uSE;          // R45: the south-east roll: its angle in radians, and its flat hold
  ` + SE_CURL + `
  #ifndef R7
  uniform vec4 uCreaseOn;    // CR2: E4's arm has no undDecl(), and the crease body below reads the gates
  #endif
  ` + CREASE_GLSL_BODY + `
  #ifdef R7
  float hsh(vec2 p){ p = fract(p*vec2(0.1031,0.1030)); p += dot(p, p.yx+33.33); return fract((p.x+p.y)*p.x); }
  /* THE SHEEN. Rag paper is matt but it is not Lambert: at the room pose the
     light rakes the sheet and the ridges of the folds carry a low, wide
     highlight, and that highlight is most of what says the paper is a solid.
     Blinn, exponent 18, and the whole term is under a twentieth.

     IT IS THE RIDGE'S, NOT THE SHEET'S. Given to every fragment it laid
     one or two levels of luma over the whole page — 132,526 px of the
     neat-line band differed at tilt 0 against E4, which is the one thing
     R7 may not touch, and it breaks E3's own measurement besides (8102's
     bare paper is 241,231,209 at a standard deviation of ZERO). So it is
     scaled by how far the surface has turned from flat: a flat sheet
     returns exactly --paper and only what is bent catches anything. */
  float sheen(vec3 n, vec3 L, vec3 V, float k, float e){
    vec3 h = normalize(L + V);
    return k*pow(clamp(dot(n, h), 0.0, 1.0), e);
  }
  /* THE TURNED CORNER'S OWN SHADOW. E4 drew a bowed triangle at a fixed
     fraction of the chamfer; the corner is a lifted surface, so what it
     keeps the light off is found from its own height — every station along
     the fold throws its shadow to q' = -.24u - (Lx+Ly)/Lz * z(u), and the
     shadow on the face is the span those stations cover. */
  ` + NE_CURL + `
  /* ==== P2: THE PEEL'S SHADOW IS THROWN, NOT DRAWN ROUND IT ==========
     Tommy, 2026-09-20: "What's with the black semicircle? What is that
     meant to be? Is it meant to be a shadow? It just looks like you've
     added a little image on top of the corner with it having a border."
     And: "Are you happy about the authenticity of how much it looks like
     a curved corner, because I'm not convinced I am?"

     It was a shadow, and it was drawn as a border. What stood here was
     R23's neReach: eight stations up the peel, each thrown along the
     sun, and the deepest landing set how far IN from the boundary a band
     ran -- one depth for the whole arc, darkest ON the arc, the same
     whichever way the light came, and "held to at least a fifth" of the
     radius even when the march said the shadow fell the other way ("a
     sheet lifted off a table is dark under its own lip whatever the sun
     is doing"). An even band hugging a shape is an outline, and an
     outline round a curl is a picture of a curl pasted on the paper.

     And it was drawn in the one place a shadow cannot be. The peel
     leaves the desk tangentially (neAt: the lift and both its
     derivatives are nought at the boundary), so ON the arc the paper is
     flat on the desk with nothing above it -- the "dark under its own
     lip" is real but it is under the lifted paper, on the oak, which is
     the desk's pass (uSunP: the flat sheet's shadow) and not this one.
     What a lifted corner throws on to THIS surface comes from the paper
     that is up, along the light, and lands on the lee side: on the flat
     sheet when the light is low enough to carry it past the arc (the
     lantern), on the peel's own skirt when it is not (the day sun).

     So: the same eight stations, and a ray. A fragment at p standing hf
     high looks towards the light; at the height of station i the ray is
     over w = p - s(z_i - hf), where s is where one unit of height lands
     on the paper (-L.xy/L.z). The paper stands at least z_i high over
     every radius from the tip's own out to rho_i, and nowhere inside the
     tip's -- the peel has drawn in, and that quarter-disc is open air
     over the oak -- so the ray is under paper when |w| is in that
     annulus and w is over the sheet at all. Any station that blocks is
     shadow and the deepest wins. On the lit side w moves OUTWARD and is
     never under anything: there is no side test, the geometry has it,
     and it moves with the light for the same reason.

     Two softenings, both named. A station that is barely off the paper
     is a contact, not a caster, and eight stations cannot tell a station
     from its neighbour there: the shadow comes in over the first six
     units of height above the fragment (three millimetres at this desk),
     which is also what keeps the peel's own steep side from shading
     itself station by station. And the edge: a penumbra that widens with
     the height of the paper throwing it (PEEL_HARD is the one variant,
     see the top of the file), scaled by the same 1 + 2(1 - cast) the
     south-east roll's shadow already softens under cloud with. */
  float neShadow(vec2 p, float hf, vec4 r, vec4 ne, vec3 L){
    vec2 cp = vec2(r.x + r.z, r.y);
    vec2 s = -L.xy/max(L.z, 0.14);
    float tipR = ne.x*ne.w;                        /* the drawn-in tip: nothing inside it */
    /* and nothing at all for the paper the tip cannot reach: the highest
       station is the tip at ne.x*ne.y, so a fragment further from the
       corner's point than the arc plus that height's throw is under open
       sky whatever the bearing, and the whole sheet is that fragment */
    float far = length(p - cp) - ne.x - length(s)*max(ne.x*ne.y - hf, 0.0) - 2.0;
    if (far > 0.0) return 0.0;
    float soft = ${PEEL_HARD ? '0.0' : '0.06'}*(1.0 + (1.0 - uSun.w)*2.0);
    float sh = 0.0;
    for (int i = 1; i <= 8 + uZero; i++){
      vec4 rl = neAt(float(i)/8.0, ne);
      float dz = rl.y - hf;
      if (dz <= 0.0) continue;
      vec2 w = p - cp - s*dz;
      float pen = 1.6 + dz*soft;
      float m = smoothstep(-pen, pen, rl.x - length(w))     /* under this station's paper */
              * smoothstep(-pen, pen, length(w) - tipR)     /* and not over the open air inside the tip */
              * smoothstep(-pen, pen, -w.x)*smoothstep(-pen, pen, w.y); /* and over the sheet at all */
      sh = max(sh, m*smoothstep(0.0, 6.0, dz));
    }
    return sh;
  }
  vec2 curlShade(float curl, vec3 L){
    float k = (L.x + L.y)/max(L.z, 0.14);
    float lo = 0.0, hi = 0.0;
    /* R45: THE ROLL'S OWN SHADOW, marched from its own shape exactly as
       R23 marches the north-east corner's. Eight stations along the arc,
       each thrown along the sun from the height it has reached; a station
       at q and height z lands at q - k*z, and the band on the paper is
       the span those landings cover. The whole of it swings with the sun
       and with the sky, and none of it is a drawn triangle.
       AND IT NEVER VANISHES: with the sun behind the corner every landing
       is out past the crease and the paper under the lip would have
       nothing on it at all — but a lip standing off a sheet is dark under
       itself whatever the sun is doing, so the reach is held to a quarter
       of the chamfer of contact, R23's own rule. */
    if (uSE.x > 0.0) {
      float len = 2.0*curl*0.70711;
      for (int i = 1; i <= 8 + uZero; i++){
        vec3 rl = seAt(float(i)/8.0*len, len, uSE.x, uSE.y);
        float qs = rl.x*1.41421 - k*rl.y;
        lo = min(lo, qs); hi = max(hi, qs);
      }
      return vec2(min(lo, -curl*0.25), hi);
    }
    for (int i = 1; i <= 8 + uZero; i++){
      float t = float(i)/8.0;
      float qs = -0.24*curl*t*1.41421 - k*curl*0.20*sin(3.14159*t*0.92);
      lo = min(lo, qs); hi = max(hi, qs);
    }
    return vec2(lo, hi);
  }
  #endif
  /* ==== R42 (a): THE LANTERN'S OWN COLOUR, ON THE PAPER ===============
     At night the desk is lit by the survey party's lantern, and the OAK
     already takes its temperature — the sheen above mixes toward
     (1,.84,.58) by the night and bends the band round the lamp as a point.
     The paper did not take it at all: the sheet and the index card were
     daylight paper turned DOWN, which is grey, and Tommy asked why the
     card had gone grey (STATE.md, R23's merge: 202/199/188 at 23:00
     against 239/234/221 by day).

     The lantern is a POINT a lamp's height over the top, so the share of
     the light it gives a place on the paper falls off as h^2/(d^2 + h^2):
     one under the lamp, a half at a lamp's height away, and almost
     nothing at the far corner. The paper is warmed by that share and
     cooled by what is left of it — so the pool is warm paper and the far
     corner is cool — and the whole term is scaled by the NIGHT, which is
     nought by day, so the daylight picture is the one it was, exactly.

     FIVE more copies of these five lines: day.js (the card is the same
     paper under the same lamp), engine.js's frame-1 sheet, — R48 —
     engine.js's GROUND fragment, which is the plate INSIDE the neat line,
     and — R52 — marks.js (one copy for its three programs) and
     lettering.js, which are the plate's PRINT drawn over that ground.
     Until R48 gave the ground the rule the neat line was a temperature
     seam of about 10 of r-b at night; until R52 gave the print the rule a
     name inside the plate gained 1 of r-b where the paper under it gained
     16. It is one piece of paper, printed once, under one lamp. ==== */
  vec3 lampTemp(vec3 c, vec2 wp){
    if (uLampN <= 0.0) return c;
    vec2 d = (wp - uLampS.xy)/uLampS.zw;
    float pool = 1.0 - clamp((length(d) - 0.20)/0.80, 0.0, 1.0);
    vec3 t = mix(vec3(0.985,0.999,1.022), vec3(1.018,0.998,0.930), pool);
    /* LK1: and the lamp is the light. The paper outside the pool falls into
       shadow at night (to half), and into a dusk dimness before it. */
    float rr = length(d); float lit = 0.40 + 0.58*(1.0 - smoothstep(0.12, 0.95, rr));
    return c*mix(vec3(1.0), t*lit, uLampN);
  }
  void main(){
    vec3 col = vec3(0.94509804,0.90588235,0.81960784);       // --paper
    /* FP1: nought the moment there is a crease in the paper. The chamfer
       the face is cut back to, the roll's own shadow on the face and the
       band under it are all measured from this one number, so a nought
       corner is a square corner with nothing standing over it — which is
       what the folding hand leaves. The flat sheet is untouched. */
    /* FP1 + FD2, 2026-09-20: the chamfer is eased to nothing over the
       first turn on the JS side (uFold.x), so there is one number and no
       step at the first degree — a folding hand presses a dog-ear out, it
       does not make it vanish. */
    float curl = uFold.x;
    float oa = 1.0;            // R23: coverage, and 1 for everything but the curl's torn edge
    /* R80 (f), kept: A NESTED BIGHT HIDES THE LEAVES INSIDE IT.
       The face grid stops at each crease but has no sidewall there. Once
       a turn has landed, perspective can therefore see two or three
       device pixels of a lower face beyond the outer bight: the broken
       line along fold two and down the packet's east edge. Cull only
       those short leaf ends which are geometrically inside the landed
       top leaf. The moving turns are untouched, and so is the fit.

       FD1, 2026-09-20: this is a fact about the LANDED PACKET and not
       about R80's lifting plane, which is gone — it was written under
       uLift > 0.0 only because that was what "a fold is happening"
       meant at the time. It asks the fold itself now. The line came
       straight back in the t = 1.0 picture when it was taken out. */
    if (uFoldOn > 0.0 && vBack < 0.5) {
      float hidden = uFoldC.w*6.0;             // 1.1 mm behind the folded edge
      if (uFoldA.y > 3.10 && uFoldA.z < 0.10) {
        if (vSheet.x < hidden ||
            (vSheet.x > 2.0*uFoldC.x - uFoldC.y - hidden &&
             vSheet.x < 2.0*uFoldC.x - uFoldC.y)) discard;
      } else if (uFoldA.z > 3.10) {
        if (vSheet.x > uFoldC.y && vSheet.x < uFoldC.y + hidden) discard;
        /* ==== FD2 (b), 2026-09-20: AND A SETTLED PACKET HAS ONE FACE ===
           Tommy: "the folded map has the guide book glitching through
           it". Over the field book's north-east corner the cover panel
           was broken by specks of the map's own ink — the compass rose
           among them — which is a leaf of the packet painting THROUGH the
           leaf on top of it.

           IT IS THE MESH, NOT THE PAPER. Every leaf is given the desk it
           has landed on (R65 a) at its own vertices, and its vertices are
           its own: R65 (b)'s bight sets each leaf's landed grid a few
           units from the next one's. On the oak and on a book's flat
           cover that is nothing; over the FALL off a book's arris — a
           cone at a corner, with a 6-unit radius at the top and a
           12-unit one at the foot, sampled by a mesh whose cells are
           10.6 x 9.5 — two leaves' piecewise-linear supports differ by
           more than the 0.9 units of a leaf, and the lower one wins the
           depth test. The paper never intersects; its approximation
           does, and no leaf thickness this paper could have would cover
           the gap.

           WHAT A SETTLED PACKET IS. Eight leaves pressed together: you
           see the top one's face and, at the fore-edge, the stack itself.
           An inner leaf's FACE in the MIDDLE of the packet is paper seen
           through the paper on top of it and cannot be right at any
           depth. So once the third turn has landed, an inner leaf is
           discarded wherever it is well inside the packet's own
           rectangle — which is the rule R80 (f) states for the short leaf
           ends, asked of the middle of the leaf instead of a band beside
           a crease.

           AND NOT AT THE FORE-EDGE, where the leaves are staggered by
           their own bights and the stack is what a folded map looks like:
           the last sixteen leaves' thickness of the packet's rectangle is
           left to the depth buffer, which is right there because the
           leaves are in their own order and nothing is asking the mesh to
           resolve a fall it cannot carry. The station is the LANDED one —
           the three turns are mirrors, so it is four comparisons. ==== */
        if (vSheet.x > uFoldC.y || vSheet.y > uFoldC.z) {
          float lx = vSheet.x < uFoldC.y ? vSheet.x
                   : (vSheet.x < uFoldC.x ? uFoldC.x - vSheet.x
                   : (vSheet.x < 2.0*uFoldC.x - uFoldC.y ? vSheet.x - uFoldC.x
                                                        : 2.0*uFoldC.x - vSheet.x));
          float ly = vSheet.y > uFoldC.z ? vSheet.y : 2.0*uFoldC.z - vSheet.y;
          float edge = uFoldC.w*16.0;
          if (lx > edge && lx < uFoldC.y - edge &&
              ly > uFoldC.z + edge && ly < 2.0*uFoldC.z - edge) discard;
        }
      }
    }
  #ifdef R7
    vec3 V = normalize(uEye - vec3(vSheet, 0.0));
    /* R23: AND THE PAPER PAST THE CREASE IS THE CURL'S, NOT THE FACE'S.
       The face and the cut edge are cut on the crease PER FRAGMENT, so it
       is a straight line at any zoom instead of the six-step staircase the
       sheet's own 10.6 x 9.5 cells would give a 45-degree edge; the strip
       past it is redrawn, rolled, by the corner's own mesh, which keeps the
       sheet's own torn outline as its free edge. At the fit the lift is
       exactly zero and nothing is cut at all. */
    if (uNE.z > 0.0 && vBack < 2.5 && neQ(vSheet, uRect, uNE.x) > 0.0) discard;
    if (vBack > 1.5 && vBack < 2.5) {
      /* THE CUT EDGE OF THE SHEET. Rag paper cut is its own core — duller
         and a shade warmer than the sized face — and it takes the light
         full on the side, which is the whole reason it is here. */
      /* ==== CL2: AND YOU CANNOT SEE THE FAR SIDE OF A CUT EDGE =========
         The band is a wall standing on the sheet's own perimeter, three
         units inside the deckle, and it is drawn on both of its faces.
         The head's wall faces NORTH, away from the chair; what was being
         painted along the head was therefore its INSIDE — the paper's own
         interior, which the three units of deckle in front of it are much
         too short to hide once the rim's curl has lifted the lip a few
         units off the oak. It came out as a row of dark dashes just under
         the head edge, broken wherever the lie's wave dropped the lip far
         enough for the overhang to cover the wall again.

         Tommy, of the brass clip: "the clip has some slightly odd
         distortion, see the thin black lines either side of it on the
         paper? ... the right hand one is longer and broken, together they
         feel like a glitch."

         A sheet clipped at its head lies against the board and shows no
         cut edge: those pixels are the paper's, so the wall gets out of
         the way and the face paints them. The foot and the chamfer are
         turned TOWARDS the chair and are untouched, which is the whole of
         the visible band at the fit. Only the sheet lying on the desk is
         asked this — a sheet that has been picked up is R47's (R80's lift
         went on 2026-09-20) and its edges were settled there. ============================== */
      /* FD2, 2026-09-20: and it is the PANEL that decides, not the
         sheet. A sheet at the first degree of a fold is still lying on
         the wood and still hides the far side of its own cut edge; vG.x
         is foldUp, this edge's own panel, so the dashes come back only
         when the panel they are on has been picked up. */
      if (vG.x < 0.5 && dot(vN.xy, V.xy) < 0.0) discard;
      vec3 e = vec3(0.882, 0.835, 0.722);
      float f = hsh(floor(vec2(vSheet.x + vSheet.y, vQ*9.0)*1.7));
      e *= 0.93 + 0.14*f;
      float lam = clamp(dot(vN, uLight), 0.0, 1.0);
      e *= 0.46 + 0.78*lam;
      e += sheen(vN, uLight, V, uSpec*0.6, 10.0);
      /* R47: and paper is not opaque on its edge either — a cut edge that
         has the sun behind it is the brightest thing on the sheet. Only
         ever asked of a sheet that is being folded: the flat sheet's own
         edge is E4's and R7's and is not this item's to change (the fit
         is diff 0). */
      /* FD2: with the panel's own turn on it, so an edge still lying on
         the desk is lit exactly as it was (the fit is diff 0) */
      e *= 1.0 + 0.45*clamp(-dot(vN, uLight), 0.0, 1.0)*uSun.w*vG.x;
      /* SH1: the hollow under a lip the drape holds up takes the oak's
         own shadow tone (.sv-shadow #100a06, the SH the desk pass mixes the
         wood to under the paper), not the lit core — and not on a panel
         that has been picked up, whose edges R47 settled */
      if (vG.y > 0.0) e = mix(e, vec3(0.063,0.039,0.024), 0.85*vG.y*(1.0 - vG.x));
      /* into the contact with the oak — and there is no contact with the
         oak on an edge that has been picked up (R47: vG.x is foldUp) */
      e *= mix(1.0, 0.30, smoothstep(0.18, 1.0, vQ)*(1.0 - vG.x));
      o = vec4(lampTemp(e, vSheet), 1.0);
      return;
    }
  #endif

  #ifdef R7
    if (vBack > 2.5) {
      /* R23: THE CURLED CORNER IS THE SAME PAPER, SEEN FROM WHICHEVER SIDE
         IT HAS TURNED TOWARDS. There is no second material and no decision
         to make: the recto is the sheet's own furniture, sampled at the uv
         the strip was cut from — so the print, the sheet number and the
         torn edge all ride the curl — and once the paper has turned past
         the camera the verso shows, which is the same rag a shade colder
         with no print on it (.sv-curl-back #e2d4b6). The deckle comes with
         it: the texture's own alpha is what the free edge is, so a corner
         that has lifted is torn exactly where it was torn lying down. */
      vec4 fa = texture(uFurn, vUv);
      if (uHasFurn > 0.5 && fa.a < 0.06) discard;
      /* AND THE TORN EDGE IS COVERAGE, NOT A TEST. Everywhere else on this
         sheet the deckle is an alpha test, which is exact and free because
         the sheet's own edges are axis-aligned: a hard test on a vertical
         line is a vertical line. The curl's free edge is a CURVE across the
         same texels, and a hard test on a curve is a staircase — measured,
         two device pixels of it at dpr 1.75, which is the one thing at this
         corner that looks computed. So the curl alone is drawn blended, with
         the raster's own antialiased alpha as its coverage. */
      oa = uHasFurn > 0.5 ? smoothstep(0.10, 0.62, fa.a) : 1.0;
      vec3 recto = uHasFurn > 0.5 ? mix(col, fa.rgb/max(fa.a, 0.001), fa.a) : col;
      vec3 verso = vec3(0.886,0.831,0.714)*(1.0 - 0.10*vQ);
      col = dot(vN, V) < 0.0 ? verso : recto;
      /* and the cut edge of the paper is a hairline along the free edge */
      col = mix(col, vec3(0.722,0.643,0.510), smoothstep(0.93, 1.0, vQ)*0.35);
      /* P2: AND THE PEEL SHADES ITS OWN SKIRT. With the sun at forty
         degrees the tip's shadow lands inside the arc, on the low paper
         of the peel's lee side, not on the flat sheet at all -- so the
         same ray is asked here, from this station's own height (vQ is
         half the station, vertex shader), on the face that is up. The
         verso is the underside and takes nothing: what is under it is
         the oak's business. */
      if (dot(vN, V) >= 0.0) {
        float sh = neShadow(vSheet, neAt(vQ*2.0, uNE).y, uRect, uNE, uLight);
        col = mix(col, vec3(0.082,0.055,0.031), 0.34*sh*(0.45 + 0.55*uSun.w));
      }
    } else
  #endif
    if (vBack > 0.5 && uSE.x > 0.0) {
      /* ==== R45 (b): THE ROLLED CORNER, SEEN FROM WHICHEVER SIDE IT HAS
         TURNED TOWARDS. R23's rule for the north-east corner, and the same
         paper: the recto is the sheet's own furniture sampled at the uv
         the strip was cut from — so the print, the sheet's own rules and
         the torn edge all ride the curl — and once the paper has turned
         past the camera the VERSO shows, which is the same rag with
         nothing printed on it, darker than the face.
         DARKER, not merely colder. E4's .sv-curl-back is #e2d4b6 against
         an #f1e7d1 face; a flat dog-ear gets away with four levels of
         luma because nothing else in it says which side is which, but a
         roll has a lit crown and a shaded belly and its back has to be
         able to hold both. The curvature does the rest. */
      vec4 fa = texture(uFurn, vUv);
      /* AND THE TORN EDGE IS COVERAGE, NOT A TEST — R23's finding at the
         north-east corner, and it is the same edge: a hard alpha test is
         exact and free on the sheet's own axis-aligned edges, but this
         strip has been rolled and its free edge is now a curve across the
         same texels, where a hard test is a staircase (two device pixels
         of it at dpr 1.75, measured). So the flap alone is drawn blended
         with the raster's own antialiased alpha as its coverage. */
      oa = uHasFurn > 0.5 ? smoothstep(0.10, 0.62, fa.a) : 1.0;
      if (oa <= 0.004) discard;
      vec3 recto = uHasFurn > 0.5 ? mix(col, fa.rgb/max(fa.a,0.001), fa.a) : col;
      vec3 verso = vec3(0.886,0.831,0.714);
      float f = vQ*uSE.x;
      bool bk = cos(f) < 0.0;
  #ifdef R7
      bk = dot(vN, V) < 0.0;
  #endif
      col = bk ? verso : recto;
      /* THE BELLY OF THE ROLL IS ITS OWN SHADOW. The inside of a curl sees
         the paper it has turned over and very little of the sky, and what
         it loses is not lambert — it is how far round the roll has closed
         on itself. Nothing until the paper is past the vertical, and a
         third of the light gone by the time the tip looks back down at
         the sheet. */
      col *= 1.0 - 0.34*clamp((f - 1.35)/1.70, 0.0, 1.0)*(bk ? 1.0 : 0.35);
      /* and the cut edge of the paper is a hairline along the free edge */
      col = mix(col, vec3(0.722,0.643,0.510), smoothstep(0.94, 1.0, vQ)*0.30);
    } else if (vBack > 0.5) {
      /* the back of the paper: no print on it, a shade colder than the face
         (.sv-curl-back #e2d4b6), with the cut edge along the fold */
      col = vec3(0.886,0.831,0.714);
      col *= 1.0 - 0.18*vQ;
      float fold = smoothstep(0.045, 0.0, vQ);
      col = mix(col, vec3(0.612,0.525,0.388), fold*0.9);      // .sv-curl-fold
      float tip = smoothstep(0.955, 1.0, vQ);
      col = mix(col, vec3(0.722,0.643,0.510), tip*0.8);       // .sv-curl-back's stroke
    } else {
      if (uHasFurn > 0.5) {
        vec4 f = texture(uFurn, vUv);
        /* THE DECKLE IS THE PAPER PATH'S OWN ALPHA. The sheet was torn, not
           guillotined, and that outline is already in the texture — E1 drew
           a solid rectangle over it and lost the edge. */
        if (f.a < 0.25) discard;
        col = mix(col, f.rgb/max(f.a,0.001), f.a);
        /* R58: and the whole of the print, off the raster, on the face.
           The deckle is still the texture's own alpha above — the raster
           is the ink, not the outline. Outside the raster (a station that
           was off the glass when the fold began) the furniture's own
           print stands, which is what was drawn before this item. */
        if (uPrintOn > 0.5 && gl_FrontFacing) {
          /* FD2, 2026-09-20: AT THE STATION IT WAS PRINTED AT, AND AT THE
             HEIGHT IT WAS PRINTED AT. The raster is a screen picture, so
             the ink of a station that stood a book's height off the oak
             is at that station's RAISED place on the glass; read at z = 0
             it came back shifted by the whole parallax, and the shift was
             the paper's own deformation applied a second time. */
          vec4 cp = uPrintM * vec4(vSheet, vPH, 1.0);
          vec2 puv = cp.xy/max(cp.w, 1e-6)*0.5 + 0.5;
          /* ==== PR1, 2026-10-09: AND A PANEL THAT HAS NOT MOVED READS ITS
             OWN PIXEL. Tommy, at #1182,431,2120,14,2: "Why does the
             colouring/shadowing/shading change slightly still when I grab
             the corner, even if I move it the tiniest part?" Tilted, the
             map's relief stands off the paper, so the raster has each hill
             and lake where its RAISED surface was on the glass; read at the
             paper's own station it came back shifted by that parallax (the
             water 8% darker at his view). Where the panel still lies as it
             lay, the texel on this very pixel IS what was there, so it is
             read there; as the panel turns (or the camera moves, uScrW) the
             read slides over to the paper's station and the relief settles
             on to the page with it. ==== */
          vec2 suv = gl_FragCoord.xy*uScr.xy + uScr.zw;
          float sw = max(uScrW, smoothstep(0.0, 0.12, 1.0 - abs(vFN.z)));
          /* ==== FS1, 2026-10-21: AND A PANEL THAT HAS BEGUN TO TURN HAS
             MOVED, however little it has tilted. The normal alone let the
             rising half keep reading this pixel's texel for its first few
             degrees, and its far edge, a whole half-sheet from the crease,
             had already left the place on the glass it lay at: past the
             old outline that texel is the raster's empty dark, so a near-
             black slab ran round the top and the east of the rising leaf
             (Tommy, on a Mac, zoomed in: "a weird effect when you start
             folding the map"). Any turn under this station sends the read
             to the paper's own station. ==== */
          {
            float ta = 0.0;
            if (vSheet.x > uFoldC.x) ta = max(ta, uFoldA.x);
            if (vSheet.x > uFoldC.y) ta = max(ta, uFoldA.y);
            if (vSheet.y < uFoldC.z) ta = max(ta, uFoldA.z);
            sw = max(sw, smoothstep(0.0, 0.0015, ta));
          }
          puv = mix(suv, puv, sw);
          if (cp.w > 0.0 && puv.x > 0.0 && puv.x < 1.0 && puv.y > 0.0 && puv.y < 1.0)
            { col = texture(uPrint, puv).rgb; gPrinted = 1.0; }
        }
        /* X2: AND A PANEL THAT HAS BEEN FOLDED BACK SHOWS ITS OTHER SIDE.
           A fold mirrors the paper, which reverses the triangle's winding,
           so the one thing that has to be asked is which way this fragment
           is facing — no varying, no second mesh and nothing to keep in
           step. The verso is the same rag a shade colder with nothing
           printed on it (.sv-curl-back #e2d4b6), which is the colour the
           turned corner's own back has used since R23. The deckle is still
           the texture's alpha above: a sheet torn at the edge is torn on
           both of its sides. */
        /* R47: and the verso of a torn rag sheet is not a flat swatch. The
           recto's grain, foxing and print are all in the texture; the back
           had one constant colour, so a panel standing on its edge was one
           constant tone over a third of the screen — which is most of what
           made it read as a slab. The same hash the cut edge is broken up
           with, two sheet units across, at a fortieth: under the eye at the
           fit, and the difference between paper and card at the room pose. */
        if (uFoldOn > 0.0 && !gl_FrontFacing) {
          col = vec3(0.886,0.831,0.714);
  #ifdef R7
          col *= 0.982 + 0.036*hsh(floor(vSheet*0.5));
  #endif
${COVER_ON ? `          /* CV1: AND THE COVER IS INK ON THIS SIDE OF THE PAPER.
             Inside the north-west panel only (x under the quarter crease,
             y north of the crease across) — the one panel whose back is
             uppermost once the three turns have landed. The station is
             vSheet, the flat sheet's own coordinate, so the print rides
             the leaf through the turn exactly as the recto's does: it is
             ON the paper, not laid over a pose. y is taken the other way
             round because that last turn MIRRORS this panel through its
             crease, and a cover is printed to read once it is folded.
             The mix is the recto's (a premultiplied strike, divided out
             and taken by its own alpha) at ${COVER_INK} of it: ink on the
             back of a sheet is the same ink and a shade lighter. */
          if (uCoverOn > 0.5 && vSheet.x <= uFoldC.y && vSheet.y <= uFoldC.z) {
            vec4 ck = texture(uCover, vec2(vSheet.x/uFoldC.y, 1.0 - vSheet.y/uFoldC.z));
            col = mix(col, ck.rgb/max(ck.a, 0.001), ck.a*${COVER_INK});
          }
` : ''}        }
      } else {
        vec2 d = max(uRect.xy - vSheet, vSheet - (uRect.xy + uRect.zw));
        float ch = ((vSheet.x - (uRect.x + uRect.z - curl)) + (vSheet.y - (uRect.y + uRect.w - curl)))*0.7071;
        if (max(max(d.x,d.y), ch) > 0.0) discard;
      }

      /* ==== R79: THE DATE BOX FOLLOWS THE PAPER =========================
         This is evaluated in vSheet, the same station that paperH, the book
         support and foldPt carry. At rest the analytic rule bends with the
         mesh; during R80's take-up it stays on that rest path; the flat
         capture then bakes it into R58's print raster, so from lift through
         every fold it is ordinary ink on the leaf. The DOM path is only its
         invisible grip now.

         The five figures are already in uFurn. Within the marked cell's
         narrow baseline band, recover the original glyph coverage from its
         known ink/paper mix and exchange only that ink for the revision red.
         The band contains no rule or tick, and may cover two cells across a
         New Year without colouring the space between them. */
      if (uHasFurn > 0.5 && uPrintOn < 0.5 && gl_FrontFacing && vBack < 0.5 && uDate.y > uDate.x) {
        float halfInk = 0.7*1.297476;
        vec3 accent = vec3(0.58823529, 0.19607843, 0.16470588); /* #96322a */
        float aa = 0.55/max(uPxPerSheet, 0.01);
        if (vSheet.x >= uDate.x - halfInk - aa && vSheet.x <= uDate.y + halfInk + aa &&
            vSheet.y >= uDate.w - halfInk - aa && vSheet.y <= uDate.z + aa) {
          float dh = max(abs(vSheet.y - uDate.w) - halfInk,
                         max(uDate.x - vSheet.x, vSheet.x - uDate.y));
          float dl = max(abs(vSheet.x - uDate.x) - halfInk,
                         max(uDate.w - vSheet.y, vSheet.y - uDate.z));
          float dr = max(abs(vSheet.x - uDate.y) - halfInk,
                         max(uDate.w - vSheet.y, vSheet.y - uDate.z));
          float ds = min(dh, min(dl, dr));
          float ba = 1.0 - smoothstep(-aa, aa, ds);
          col = mix(col, accent, ba);
        }

        float yy0 = uDate.z + 5.0*1.297476, yy1 = uDate.z + 17.5*1.297476;
        float all0 = uDateYear.z, all1 = uDateYear.w;
        if (vSheet.x >= all0 && vSheet.x <= all1 && vSheet.y >= yy0 && vSheet.y <= yy1) {
          vec3 fi = texture(uFurn, vUv).rgb;
          vec3 paper = vec3(0.94509804,0.90588235,0.81960784);
          vec3 oldInk = vec3(0.33333333,0.28235294,0.22745098); /* --ink-2 */
          /* The serialised SVG happened to freeze 2026 red. First exchange
             that chromatic coverage for black across every figure, then
             exchange black for red only inside the live marked cell(s). */
          float ra = clamp(((fi.r - fi.g) - (paper.r - paper.g)) /
                           ((accent.r - accent.g) - (paper.r - paper.g)), 0.0, 1.0);
          vec3 norm = fi + ra*(oldInk - accent);
          col += ra*(oldInk - accent);
          bool inMarked = vSheet.x >= uDateYear.x && vSheet.x <= uDateYear.y;
          if (inMarked) {
            float ga = clamp(dot((paper - norm)/max(paper - oldInk, vec3(0.01)), vec3(0.33333333)), 0.0, 1.0);
            col += ga*(accent - oldInk);
          }
        }
      }
      /* THE TURNED CORNER THROWS A SHADOW ON THE FACE, along the sun. The
         SVG drew it as one bowed triangle at .34 (.sv-curl-shadow); it is
         the same triangle here, laid where the light puts it. */
      float qf = (vSheet.x - (uRect.x + uRect.z - curl)) + (vSheet.y - (uRect.y + uRect.w - curl));
  #ifdef R7
      vec2 sp2 = curlShade(curl, uLight);
      /* R45: AND IT FALLS OFF, it is not a slab. E4's dog-ear lands its
         whole shadow in one band because a flat triangle a fixed height
         off the paper does exactly that; a ROLL is touching at the crease
         and further off the sheet at every station after it, so what it
         throws is darkest where it is nearest and fades as the square out
         to wherever the march says the tip lands — R23's own rule for the
         north-east corner, and the same paper. */
      float fw = curl*0.07*(1.0 + (1.0 - uSun.w)*2.0);
      float band;
      if (uSE.x > 0.0) {
        float nbs = clamp(1.0 - qf/min(sp2.x, -0.001), 0.0, 1.0);
        band = nbs*nbs*step(qf, 0.0);
      } else {
        band = smoothstep(sp2.x - fw, sp2.x + fw, qf)*(1.0 - smoothstep(sp2.y - fw*0.5, sp2.y + fw*0.5, qf));
      }
      col = mix(col, vec3(0.082,0.055,0.031), 0.34*band*(0.45 + 0.55*uSun.w));
      /* R23: AND THE CURLED CORNER SHADES THE PAPER IT LEANS OVER. It
         was a band from the crease in; P2: it is the lifted paper's own
         shadow thrown along the light (neShadow above), the same ink and
         the same strength as the crease shading at most, and nought at
         the arc itself and on the whole of the lit side. The flat sheet
         stands nought high, so hf is nought. */
      if (uNE.z > 0.0) {
        float sh = neShadow(vSheet, 0.0, uRect, uNE, uLight);
        col = mix(col, vec3(0.082,0.055,0.031), 0.34*sh*(0.45 + 0.55*uSun.w));
      }
  #else
      vec2 sp = vSheet - vec2(uSun.x, uSun.y)*(curl*0.16*uSun.z*(0.22 + 0.78*uSun.w));
      float qs = ((sp.x - (uRect.x + uRect.z - curl)) + (sp.y - (uRect.y + uRect.w - curl)))*0.7071;
      float band = smoothstep(-curl*0.34, 0.0, qs);
      col = mix(col, vec3(0.082,0.055,0.031), 0.26*band*(0.45 + 0.55*uSun.w));
  #endif
    }

    /* THE LIGHT ON THE PAPER, MEASURED FROM FLAT. uLight.z is what a flat
       sheet returns, so bare paper comes out at exactly --paper and only the
       creases and the turned corner carry any shading at all. Measured on
       8102: its bare paper is 241,231,209 with a standard deviation of
       ZERO — the page's grain film lies UNDER the sheet (.canvas::after has
       no z-index; .sheet-frame has 3), so the paper is not grained and the
       oak is. Only the shape is lit: the print, the foxing and the section
       are in the texture and stay flat (ROOM-PLAN §7). */
  #ifdef R7
    /* R7: the crease's own gradient and the lie's, added — one surface, one
       normal. Inside the neat rule vG is exactly zero and this is E4's. */
    vec3 nrm; float bend = 1.0;
    /* R23: a curled corner turned past the camera is showing its BACK, and
       the back of a surface is lit by the back of its normal */
    /* R45: and the same for the rolled south-east flap, which turns past
       the camera exactly as R23's corner does */
    if (vBack > 0.5) nrm = (dot(vN, V) < 0.0 && (vBack > 2.5 || uSE.x > 0.0)) ? -vN : vN;
    else {
      /* R22 (b): the fall off a board, in closed form, right here: fall
         is 1 on the cover and 0 at the foot of the drape, gd is the
         ramp's own gradient, which is a step function at each end of the
         run — so the surface turns at the arris and turns back at the
         oak, and the light finds two creases instead of one wash. */
      vec2 gd; float sd;
      float fall = bkFall(vSheet, gd, sd);
      vec2 g = (creaseGrad(vSheet, uRect, uFold.y, uFold.z)
             + vG.xy*(1.0 - fall*${(1 - LIE_BOARD).toFixed(2)}) + gd*(uOverZ.w*6.0*fall*(1.0 - fall)));
      nrm = normalize(vec3(-g.x, -g.y, 1.0));
      /* ==== X2: AND THE SAME SURFACE, TURNED WITH THE PANEL IT IS ON.
         The normal above is struck in the sheet's own axes; on a folded panel
         those axes have been turned, so the normal is rebuilt in the
         fold's frame — the sheet's up, its east and the third of the three
         — which is exactly this normal when nothing has been folded and
         costs one cross product when something has. Then the back of a
         surface is lit by the back of its normal, which is R23's own rule
         for the turned corner and the same paper. ==== */
      if (uFoldOn > 0.0) {
        nrm = normalize(-g.x*vFT - g.y*cross(vFN, vFT) + vFN);
        if (!gl_FrontFacing) nrm = -nrm;
      }
      /* and NOTHING inside the neat rule: the same mask the lie is held to */
      bend = clamp(length(g)*20.0, 0.0, 1.0)*vG.z;
    }
    float lam = clamp(dot(nrm, uLight), 0.0, 1.0);
    vec3 fgl = vec3(1.0);
    /* ==== R47: A PANEL WITH ITS BACK TO THE SUN IS STILL PAPER =========
       X2's own OPEN 3: "a panel seen almost edge-on mid-turn reads as a
       flat grey slab … it is correctly lit". It was correctly lit for an
       opaque solid, and paper is not one. Two terms, and neither of them
       is a fudge:

       THE SHEET IS LIT THROUGH. Rag at eighty grams passes a good third
       of what falls on it, so the face turned AWAY from the sun is lit by
       the sun coming through the face turned towards it — which is why a
       held-up sheet of paper glows and a held-up piece of slate does not.
       The term is the back of the same Lambert, warmed (what comes
       through has been through the size and the fibre), and it is scaled
       by the sun's own strength: under cloud there is nothing to come
       through and the whole thing is nought.

       AND THE ROOM IS NOT A VOID. The oak, the walls and the sky off the
       window put a floor under any surface in here; the desk pose's own
       shading has never had to show it because nothing in the picture
       faced away from the sun until the paper stood up. The floor is
       .74 of flat paper — measured against the oak it stands on, which
       is darker than that everywhere on this desk.
       ==== */
    if (uFoldOn > 0.0) {
      /* ==== FD2, 2026-09-20: AND BOTH OF THEM COME IN WITH THE TURN ====
         Tommy: "the raised parts of the sheet suddenly get much higher as
         witnessed by the deformation around the field guide". They did
         not get higher — they got BRIGHTER, in one step, at the first
         degree: these two terms were gated on "is there a fold in this
         sheet" and both of them are properties of a panel that has been
         PICKED UP. A sheet lying on the desk has the oak behind it, so
         nothing comes through it and there is no floor under it but its
         own light; the paper draped over the field book was reading both
         the moment the gesture began and the fall off the book's arris
         stood up with them. The turn is nought on a panel that is still
         lying flat and one on a panel standing on its edge, so at the
         first degree this whole block is exactly one — to the bit — and
         R47's answer to a panel seen edge-on is untouched. ==== */
      float turn = 1.0 - abs(vFN.z);
      float thr = clamp(-dot(nrm, uLight), 0.0, 1.0)*turn;
      float ill = 1.0 + 0.80*(lam - uLight.z);
      fgl = (max(ill + 0.34*thr*uSun.w, mix(ill, 0.74, turn)) / max(ill, 0.02))
          * (1.0 + vec3(0.10, 0.07, 0.02)*thr*uSun.w);   // and what comes through is warm
      /* AND A PANEL STANDING UP IS RAKED. The sheen is held to the
         creases and the drape on a flat sheet (bend is their gradient, and
         nought on bare paper — E4's rule, so the desk pose stays matt);
         a panel that has turned is at a grazing angle to the whole room
         and the sky off the window is the only thing that says it is a
         surface and not a shape. It comes in with the turn and is the
         same Blinn at the same twentieth. */
      bend = max(bend, 0.55*(1.0 - abs(vFN.z)));
    }
    /* the flat sheet's own line, and then a factor that is EXACTLY one
       whenever nothing is folded — x*1.0 is x, to the bit */
    /* R58: and NONE of it on the frame that is making the print raster —
       what is wanted there is the ink, so that the folded paper can be
       lit once rather than lit at the fit and lit again in the air. */
    if (uUnlit < 0.5) {
    col *= 1.0 + 0.80*(lam - uLight.z);
    col *= fgl;
    col += sheen(nrm, uLight, V, uSpec*bend, 18.0);
    }
  #else
    vec3 nrm = vBack > 0.5 ? vec3(0.0,0.0,1.0) : creaseNormal(vSheet, uRect, uFold.y, uFold.z);
    float lam = clamp(dot(nrm, uLight), 0.0, 1.0);
    vec3 fgl = vec3(1.0);
  #endif

    /* ==== R22 (b): AND SO HAS THE FALL OFF A BOARD ===================
       The same rule the sheet's own folds are under: a tent alone reads as
       a smudge, so the arris gets a line. Two of them here, because a
       sheet over a book creases twice — over the board's edge, where the
       paper is bent AWAY from the eye and the ridge catches (a hair
       lighter), and where it reaches the wood, where it is bent toward it
       (a hair of the fold's own colour). Held to .6 of a DEVICE pixel like
       every other stroke on this sheet, so they stay hairlines at any
       zoom and vanish at none. */
  #ifdef R7
    if (vBack < 0.5 && vG.z > 0.0 && uUnlit < 0.5) {
      vec2 gdl; float sdl;
      float fl = bkFall(vSheet, gdl, sdl);
      if (fl > 0.0 || sdl < bkRun + bkFoot + 2.0) {
        /* R45: R22's two hairlines were the answer to two CREASES — a kink
           has no width, so a line had to be drawn where the shading could
           not reach. The two kinks are radii now and a radius is a few
           millimetres across, so the light finds them by itself; and a
           ruled line down a rounded fold is a crease drawn on paper that
           has not been folded. What is left is what a radius does: the
           crown of the bend turns toward the sky and catches, the trough
           at its foot turns away and loses, and each is as wide as its own
           radius. */
        vec2 cv = bkCurve(sdl, uOverZ.z);
        col *= 1.0 + 0.055*cv.x*cv.x;
        col = mix(col, vec3(0.553,0.447,0.282), 0.115*cv.y*cv.y);
      }
    }
  #endif
    /* AND THE CREASE HAS A LINE. A fold in paper is a tent with a scored
       line down it (.sv-fold, #8d7248 at .26), and the tent alone reads as
       a smudge. */
    if (vBack < 0.5 && uUnlit < 0.5) {
      /* .sv-fold is vector-effect: non-scaling-stroke at .6, so it is .6 of
         a DEVICE pixel wherever the sheet is laid — the same rule E2 put
         every stroke on the plate under. */
      /* DR1 (3): the line rides the fold with the tent it is drawn down.
         CR2: and each crease's line comes with ITS OWN turn, gated as the
         tent is (uCreaseOn), so no line is drawn down a crease the paper
         has not yet been bent along. */
      float ln = uCreaseOn.w*clamp(0.8 - abs(vSheet.y - (uRect.y + uRect.w*0.5))*uPxPerSheet, 0.0, 0.6);
      for (int i = 0; i < 3 + uZero; i++)
        ln = max(ln, uCreaseOn[i]*clamp(0.8 - abs(vSheet.x - (uRect.x + uRect.z*(0.25 + 0.25*float(i))))*uPxPerSheet, 0.0, 0.6));
      col = mix(col, vec3(0.553,0.447,0.282), 0.26*ln);
    }
    /* ==== LB1, 2026-10-09: THE LANTERN IS STRUCK INTO THE PRINT. R58 left it
       out of the raster and lit the raster here, at the paper's station.
       Tilted, the water and the ground below the paper stand at their own
       stations, so the pool was read at two places and the sea came out
       4% off the moment the corner was taken (Tommy, #1182,431,2120,14,2).
       The pool is a function of the station alone, never of the turn, so
       it is the same light struck once into the print as applied after:
       every pass keeps it on the print's frame, the sheet's own albedo
       takes it there (uUnlit), and ink read off the print is not lit by it
       a second time. ==== */
    if (uUnlit > 0.5 || gPrinted < 0.5) col = lampTemp(col, vSheet);
    o = vec4(col*oa, oa);
  }`;
  return [SHEET_VS, SHEET_FS];
}


/* =================================================================
   THE MODULE
   ================================================================= */
var O = {
  ready: false, pDesk: null, pSheet: null, pBook: null,
  vaoSheet: null, vaoBook: null, sheetN: 0, S: null,
  /* R17: the desk's rect, and the switches, for place.js and the report */
  NEAR: O_NEAR, R17: { timber: 'amber', fall: true, edge: true, tells: true },
  /* DR1, 2026-09-21: which of the three drapes this page is on — the
     default wrap, concept B's long fall, or concept C's eight plates.
     `Room.state().drape` reads it.
     DR2, 2026-09-21: `DRAPE: DRAPE_MODE` was here and is gone with the
     switch. There is one drape, so there is nothing to report. */
  __fs: function (w) { return deskFS(w !== false); },
  /* C0: the two generated sources, in text, so the proof that the lists
     emit what the named uniforms emitted can be read and diffed without a
     GPU. `RoomObjects.__src.desk(true)` and `.sheet('#define R7 1\n')`. */
  __src: { desk: function (w) { return deskFS(w !== false); },
           sheet: function (d) { return SHEET_SRC(d || ''); } }
};

/* =====================================================================
   C0 — THE DESK IS COMPOSED: ONE HOOK AND TWO LISTS

   Tommy, 2026-09-18: "I do think we should break the desk down where
   practical. What I'd like you to do is always ask yourself the question,
   'Knowing what I know now, how would I approach it from scratch?'
   Because remember that code is incredibly cheap with LLMs! What's hard
   is verification, running tools, waiting for tools, and waiting for the
   user, but code is cheap."

   WHAT WAS HERE. One file of six thousand lines holding the oak, the
   sheet, the fold raster, the corner, the two books and their opening,
   the laptop and its decal, the pencil, and the lamp's shadow — tangled
   on purpose, because each of those things knew the others by NAME. The
   sheet's vertex shader hard-coded `uBkA` and `uBkB`, "the two books";
   the oak's fragment shader painted `uBookA` and `uBookB`'s shadows
   itself; the laptop painted its own quad over the oak. Adding a third
   thing to the desk meant editing three shaders that had no business
   knowing about it.

   WHAT IS HERE NOW is the shape the ROOM already has (place.js, R71 f
   and R76): a hook and two lists, so that cross-knowledge is REGISTERED
   instead of written down.

   ---------------------------------------------------------------
   `RoomObjects.piece(name, fn, hooks)`        the desk-piece hook
   ---------------------------------------------------------------
   A desk card adds ONE file, `src/js/room/desk-<name>.js`, and touches
   nothing else. The file registers itself:

       RoomObjects.piece('books', function (gl, ctx) { ... }, { ... });

   `fn(gl, ctx)` is called ONCE A FRAME in the desk pass — after the oak
   it stands on and BEFORE the sheet that lies over it — in registration
   order, which is the order the files are listed in base.njk. It must
   draw and return; it must leave the depth state as it found it
   (`ctx.depthFore(gl)` turns it on, and the pass puts it back).

   WHY A `<script defer>` IN base.njk AND NOT A FETCH, which is what
   `RoomPlace.piece` does. The room is a one-per-cent Easter egg reached
   by a camera move, so its pieces can land late and cost a 404 if they
   never land at all. THE DESK IS THE FRONT PAGE. The two books are in
   the first picture the reader ever sees, and a fetch cannot be in frame
   one: the pieces are listed beside objects.js, in document order, so
   every registration is complete before engine.js boots at the bottom of
   the same list and the first draw is the draw it always was. What a
   piece must NOT do is compile on that path: new programs go in `late`
   (the idle slot after the first picture, `O.precompile`) or `warm` (the
   room's own staged lane), never in `init`, which exists only to carry
   what already compiled on frame 1 and must not grow.

   `hooks` are all optional:
     init(gl, ctx)   frame 1, inside `O.init` — EXISTING compiles only
     late(H)         the idle slot after the first picture (`O.precompile`)
     place(S)        the DOM lay, from `O.place`
     oak             { decl, body, uniforms(gl, p, ctx) } — GLSL compiled
                     INTO the oak's own fragment shader, for a thing that
                     is painted on the wood rather than drawn as a solid
                     (the pencil, the lamp's base). `decl` goes with the
                     uniforms, `body` inside `main` where the tells were.
     smoke()         whatever the piece wants printed by `O.figures()`

   `ctx` is the shared desk context, the same object every frame:
     gl, S           the context and the engine's state
     L, f            S.layout and S.fld, the two every piece asks for
     mm              world css px per millimetre on this desk
     oak             the oak's plane in mm: DESK_MM, and its own z
     sun             `sunOf()` this frame — dir, length, cast, night
     eye             the camera in SHEET units
     spec            how much sheen the sky has to give
     paperHeight(x,y)  the sheet's own surface height, sheet units
     mk              engine.js's program cache: prog(gl, vs, fs, name)
     depthFore/Back  the shared depth-buffer discipline
     mm2s(x, y)      desk millimetres -> css px on this desk

   ---------------------------------------------------------------
   `RoomObjects.under(name, fn)`      what is UNDER THE PAPER  (list 1)
   ---------------------------------------------------------------
   Exactly as `RoomPlace.light` replaced each furniture piece's private
   lighting, this replaces "the two books" in the sheet's own shaders.
   `fn(ctx)` returns, once a frame:

       { box: [x, y, w, h],   the footprint in SHEET UNITS
         rise: <sheet units> }  how far its top stands above the datum

   and the sheet drapes over it: flat across the box and for `DRAPE_HOLD`
   past its arris, then R22's straight fall over `DRAPE_RUN`, filleted at
   both ends by R45's two radii. Those four numbers are the PAPER's, not
   the thing's — a sheet folded in eight has one stiffness whatever it is
   lying on — so they stay with the sheet and an entry supplies only its
   box and its height. The cap is FOUR. The generated GLSL is unrolled at
   compile time to exactly the number registered, so two entries cost what
   the two hard-coded books cost and not a loop.

   THE DATUM. `uOverZ.x` is the rest height of entry ZERO and `uOverR[i]` is
   each entry's own offset from it, which is how the two books were
   written (`uOverZ.x` + `uBkR.x|y`) and is kept because the arithmetic has
   to land on the same float: this card's whole proof is that nothing
   moves. An entry's `rise` is that offset.

   ---------------------------------------------------------------
   `RoomObjects.shadow(name, fn)`   what stands ON THE OAK  (list 2)
   ---------------------------------------------------------------
   `fn(ctx)` returns, once a frame:

       { box: [x, y, w, h],   the footprint in CSS PIXELS
         height: <css px>,    how far its top is off the wood
         lift: <1..>,         1 at rest; higher when a hand has raised it
         cast: <0..1>,        the weight of the shadow the sun throws
         contact: <0..1> }    the weight of the line that never goes

   and the OAK's own pass paints both from the light — the throw struck
   along the sun at that box's own station, softened and lengthened by
   `lift`, and the contact line under it. An entry that paints its own
   (the lamp's turned disc, the laptop's quad in its own twelve-degree
   frame) asks for `cast: 0, contact: 0` and is in the list for its box
   and its height: what stands where is now ONE published fact and not
   three private ones. `RoomObjects.figures()` prints the whole list.
   ===================================================================== */
/* THE SHARED GLSL, published the way `RoomPlace.GLSL` is: a piece that
   draws a solid on this desk wants the same value noise, the same film
   over the window and the same CSS radial-gradient shape the oak uses,
   and it must not carry a second copy of them. */
O.GLSL = { COMMON: DESK_COMMON };

var OAK_PREAMBLE = `\n  /* ==== R17 (d): THE TELLS ==========================================
     Two things only a desk has, struck in css pixels in the pass that was
     already running: no mesh, no draw, no byte on the wire. The LAMP's
     base stands where place.js stands it (82 mm of turned bronze) and the
     PENCIL is a 175 x 7 mm hexagon lying just below the sheet's foot, not
     quite square to it. Nothing is labelled and nothing is lit that was
     not lit before — they are objects on the oak and that is the whole
     statement: a background does not have things standing on it. */
  {
    float mm = max(0.6, uFull.z/840.0);
    vec2 lit2 = normalize(vec2(-uSun.x, -uSun.y) + vec2(1e-5,1e-5));
`;

var PIECES = [], UNDER = [], SHADOW = [];
var UNDER_CAP = 4, SHADOW_CAP = 4;

/* LZ1: AND A PIECE MAY NOW LAND AFTER THE IDLE SLOT HAS RUN. C0's OPEN 5
   ends "a card that wants a thing to appear at run time needs a rebuild
   path"; for the LISTS it does, and the anchor above explains why the
   laptop needs none. What it does need is its `late` hook called, because
   `O.precompile` — the idle slot after the first picture — has already
   been and gone by the time a fetched file registers. So the slot
   remembers the helper bag it was given and a piece registering after it
   takes its turn at once. `late` is idempotent by contract, so a piece
   that was already there is not built twice, and a piece whose file never
   lands costs nothing. */
var lateH = null;
O.piece = function (name, fn, hooks) {
  if (typeof fn !== 'function') return;
  hooks = hooks || {};
  for (var i = 0; i < PIECES.length; i++) if (PIECES[i].name === name) {
    PIECES[i].fn = fn; PIECES[i].hooks = hooks; pieceLate(PIECES[i]); return;
  }
  var pc = { name: name, fn: fn, hooks: hooks, drew: 0, err: 0, dead: 0 };
  PIECES.push(pc);
  pieceLate(pc);
};
function pieceLate(pc) {
  if (!lateH || pc.dead || !pc.hooks.late) return;
  try { pc.hooks.late(lateH); }
  catch (e) { pc.dead = 1; pc.err++; console.warn('desk late ' + pc.name, e); }
}
O.pieces = PIECES;

function listAdd(list, cap, name, fn) {
  if (typeof fn !== 'function') return;
  for (var i = 0; i < list.length; i++) if (list[i].name === name) { list[i].fn = fn; return; }
  if (list.length >= cap) { console.warn('desk list full, dropped ' + name); return; }
  list.push({ name: name, fn: fn, err: 0 });
}
/* A REGISTRATION AFTER THE PROGRAMS ARE BUILT IS NOT READ. The generated
   GLSL is unrolled to the length of the list at compile time, which is
   what keeps two entries at two entries' cost; every desk piece is a
   deferred script in front of engine.js's boot, so the lists are whole
   before a program exists. A late one is warned about once and carried in
   the uniforms, where the next program build will pick it up. */
var UNDER_N = 0, SHADOW_N = 0;
O.under = function (name, fn) {
  listAdd(UNDER, UNDER_CAP, name, fn);
  if (UNDER_N && UNDER.length > UNDER_N) console.warn('under(' + name + ') after the sheet was built');
};
/* `opts.lip` says this thing is one the SHEET is draped over, so R45's
   extra tap — the soft dark a paper lip a whole book's thickness in the
   air throws out onto the wood — is struck from its box too. It is a
   STATIC property of the registration, not of the frame, because the
   oak's source is generated once. */
O.shadow = function (name, fn, opts) {
  listAdd(SHADOW, SHADOW_CAP, name, fn);
  for (var i = 0; i < SHADOW.length; i++) if (SHADOW[i].name === name) SHADOW[i].opts = opts || {};
  if (SHADOW_N && SHADOW.length > SHADOW_N) console.warn('shadow(' + name + ') after the oak was built');
};
O.under.list = UNDER; O.shadow.list = SHADOW;

/* the shared desk context, built once and refreshed each frame so a piece
   never allocates and the fields are the same object every time */
var CTX = { gl: null, S: null, L: null, f: null, mm: 1, sun: null, eye: null, spec: 0,
            oak: DESK_MM, mk: null, depthFore: depthFore, depthBack: depthBack,
            paperHeight: ctxPaperHeight, mm2s: ctxMm2s, sunAt: ctxSunAt, NO_BOOKS: NO_BOOKS,
            /* the SHEET's own numbers, so a piece never copies one */
            /* DR2, 2026-09-21: RUN, FILL_FOOT and DRAPE are gone from
               here with the constants and the switch. RUN_K and FOOT_K
               are what a piece asks now: the fall off a thing is 3.5 of
               that thing's own height and the foot a quarter of the fall.
               (desk-books.js reads FLOAT and seTip; nothing has ever read
               the other five.) */
            paper: { FLOAT: PAPER_FLOAT, FILL_TOP: DRAPE_FILL_TOP,
                     HOLD: DRAPE_HOLD, SHADE: DRAPE_SHADE,
                     RUN_K: DRAPE_RUN_K, FOOT_K: DRAPE_FOOT_K,
                     seTip: seTip, neat: neatRect } };
function ctxPaperHeight(x, y) { return O.paperHeight(CTX.S, x, y); }
function ctxMm2s(x, y) {
  var L = CTX.L, f = CTX.f;
  return [L.ox + (f.sheet.w * 0.5 + x) * L.k, L.oy + (f.sheet.h * 0.5 + y) * L.k];
}
/* ONE object, refreshed: a piece is handed the same ctx every frame and
   nothing here allocates — this runs per draw and, through paperHeight,
   per pointer move. */
function ctxSunAt(x, y) { return sunAt(CTX.sun, x, y); }
function deskCtx(gl, S, s) {
  var L = S.layout, f = S.fld;
  CTX.gl = gl; CTX.S = S; CTX.L = L; CTX.f = f;
  CTX.mm = f.sheet.w * L.k / 840;
  CTX.sun = s || sunOf();
  CTX.eye = eyeInSheet(S); CTX.spec = specOf(CTX.sun);
  CTX.mk = O.mk;
  return CTX;
}

/* ---- the two lists, evaluated ------------------------------------- */
var UND_BOX = new Float32Array(UNDER_CAP * 4), UND_RISE = new Float32Array(UNDER_CAP);
var SH_BOX = new Float32Array(SHADOW_CAP * 4), SH_K = new Float32Array(SHADOW_CAP * 4);
var SH_SUN = new Float32Array(SHADOW_CAP * 3);
function evalList(list, ctx, each) {
  for (var i = 0; i < list.length; i++) {
    var reg = list[i], q = null;
    try { q = reg.fn(ctx); }
    catch (e) { reg.err++; if (reg.err === 1) console.warn('desk list ' + reg.name, e); }
    each(i, q && q.box ? q : null);
  }
}

/* ---- THE UNDER LIST'S OWN GLSL, generated ---------------------------
   The four functions that used to name "the two books" are written out
   here from whatever registered, UNROLLED — a loop with a break in it
   does not unroll and this is evaluated per VERTEX on a 224x128 grid and
   per FRAGMENT over most of the window (the same argument DESK_COMMON's
   fbm2 is written out under). With the shipped two entries the emitted
   source is the two-book source it replaces, arm for arm, which is why
   the sheet is diff 0. */
function undFold(op, arms) {                    /* max(a, max(b, c)) */
  var s = arms[arms.length - 1];
  for (var i = arms.length - 2; i >= 0; i--) s = op + '(' + arms[i] + ', ' + s + ')';
  return s;
}
function undN() { return Math.max(1, Math.min(UNDER_CAP, UNDER.length)); }
/* DR1 concept B: an entry's OWN fall and its own foot radius, as GLSL
   text. No new uniform is wanted for either — the entry's absolute top is
   already `uOverZ.x + uOverR[i]` (THE DATUM, below), and both numbers are
   that height times a constant, so the whole of concept B's geometry is
   two multiplies on uniforms the shader already has. In the other two
   drapes these are never called and the arms are the arms they were.
   DR2, 2026-09-21: there are no other arms. */
function undRun(i) { return '(' + DRAPE_RUN_K.toFixed(2) + '*(uOverZ.x + uOverR[' + i + ']))'; }
function undFoot(i) { return '(' + (DRAPE_RUN_K * DRAPE_FOOT_K).toFixed(4) + '*(uOverZ.x + uOverR[' + i + ']))'; }
/* ==== LF1, 2026-09-22: THE ENTRIES ARE MEASURED AT ONE CALL SITE =====
   `bookTop` and `bookLift` ask the SAME question of the SAME entries at
   the same point — which board is under p, and how much of the fall off
   it the paper has taken — and every place that wants the drape wants
   both answers (boardH multiplies them; drapeH mixes on one and lifts to
   the other). Written as two functions with an arm per entry, D3D's
   inliner put `bookM` into the lettering's vertex shader SIXTEEN times
   and `bkQ` thirty-two: 9.3 KB of the 21.5 KB that program expands to,
   and — bisected on Tommy's card, stubbing one function at a time — 440
   ms of its 1.05 s link, the single largest term in it.

   So the arms are measured ONCE, here, and both answers come out of that
   one pass — the entry loop the sheet's other folds use (LP1's `uZero`)
   was tried first and timed the same on his card to within the run's own
   noise (938 ms against 920 over medians of three), so the shape that
   ships is the one that leaves the ARITHMETIC exactly where it was: the
   same n calls to bookM with the same constant arguments, `best` holding
   the running winner the old `l1 > l0` chain held (strictly greater, so
   an equal later entry does not displace an earlier one) and `lift`
   undFold's own nested max of the same values in the same order. There is
   no re-association and no dynamic index into uOver anywhere in it.
   ==================================================================== */
function undLiftGLSL() {
  var n = undN(); UNDER_N = n;
  var lift = [], on = [], i;
  for (i = 0; i < n; i++) {
    lift.push('l' + i);
    on.push('bookOn(p, uOver[' + i + '])');
  }
  var at = 'void bookAt(vec2 p, out float top, out float lift){\n' +
           '  float l0 = bookM(p, uOver[0], ' + undRun(0) + ', ' + undFoot(0) + ');\n' +
           '  float best = l0;\n  float r = uOverR[0];\n';
  for (i = 1; i < n; i++)
    at += '  float l' + i + ' = bookM(p, uOver[' + i + '], ' + undRun(i) + ', ' + undFoot(i) +
          '); if (l' + i + ' > best) { best = l' + i + '; r = uOverR[' + i + ']; }\n';
  at += '  top = uOverZ.x + r;\n' +
        '  lift = ' + undFold('max', lift) + ';\n' +
        '}';
  return at + '\n' +
         'float bookLift(vec2 p){ float t, l; bookAt(p, t, l); return l; }\n' +
         '/* R22: AND THE TWO BOARDS ARE NOT AT THE SAME HEIGHT ANY MORE. A hovered\n' +
         '   book rises 1.8 css px; if the paper keeps lying at the resting cover\'s\n' +
         '   height the board comes straight up THROUGH it and the cloth is drawn\n' +
         '   over the map. The paper takes whichever board it is actually over.\n' +
         '   C0: whichever ENTRY, and the first of equals, as `la >= lb` had it. */\n' +
         'float bookTop(vec2 p){ float t, l; bookAt(p, t, l); return t; }\n' +
         '/* the hard-edged footprint: where the sheet\'s own cut edge rests ON a\n' +
         '   thing instead of hanging past it to the oak */\n' +
         'float undOn(vec2 p){ return ' + undFold('max', on) + '; }';
}
function undFallGLSL() {
  var n = undN(), s = 'float bkFall(vec2 p, out vec2 g, out float sd){\n', i;
  for (i = 0; i < n; i++)
    s += '  vec2 g' + i + '; float s' + i + ';\n  float l' + i +
         ' = bkRamp(p, uOver[' + i + '], ' + undRun(i) + ', ' + undFoot(i) +
         ', g' + i + ', s' + i + ');\n';
  s += '  g = g0*(uOverZ.x + uOverR[0] + uOverZ.y); sd = s0; float l = l0;\n';
  /* DR1 concept B: and the winning entry's own run, for the two places
     that shade the fall — its crown and the trough at its foot are each
     as wide as their own radius, and both radii are this entry's. */
  s += '  bkRun = ' + undRun(0) + '; bkFoot = ' + undFoot(0) + ';\n';
  for (i = 1; i < n; i++)
    s += '  if (s' + i + ' < sd) { g = g' + i + '*(uOverZ.x + uOverR[' + i +
         '] + uOverZ.y); sd = s' + i + '; l = l' + i +
         '; bkRun = ' + undRun(i) + '; bkFoot = ' + undFoot(i) + '; }\n';
  return s + '  return l;\n}';
}
/* the declarations the two sheet programs share */
function undDecl() {
  return '  uniform vec4 uOver[' + UNDER_CAP + '];   // C0: what is under the paper, sheet units: x y w h\n' +
         '  uniform vec4 uCreaseOn;     // CR2: each crease\'s own gate — x: the one at w/4, y: w/2, z: 3w/4, w: the one across\n' +
         '  uniform float uOverR[' + UNDER_CAP + '];  // each entry\'s own rise above the datum\n' +
         '  uniform vec4 uOverZ;        // R15: the datum, the paper\'s float, the drape run, how much of the fall is shaded\n' +
         /* DR2: and the `uOverS` line concept C added is gone with it.
            uOverZ.z and uOverF.y are DEAD SLOTS now — the run and the foot
            are per entry, written into the source by undRun/undFoot — but
            the two declaration lines are left exactly as they are, because
            this text IS the emitted shader and this card's proof is that
            the emitted shader does not move. undUniforms writes them
            nought and the one place uOverZ.z still appears (bkCurve's
            argument) overwrites it on its first line. */
         '  uniform vec2 uOverF;        // R45: the two radii, sheet units: the arris\'s and the foot\'s\n';
}
/* and the uniforms, written on BOTH sheet programs every frame */
/* THE DATUM. An entry returns the ABSOLUTE top the paper rides at over
   it; the shader is handed entry ZERO's as `uOverZ.x` and every entry's
   OFFSET from it in `uOverR[i]`, which is how the two named books were
   written (`uBkZ.x` plus `uBkR.x|y`) and is kept to the letter because
   this card's whole proof is that nothing moves: entry zero's offset is
   exactly nought, so its sum is exactly its own number. No entry has to
   know another entry's height to say its own. */
var UND_TOP = [0, 0, 0, 0];
function undUniforms(gl, p, ctx) {
  UND_BOX.fill(0); UND_RISE.fill(0);
  evalList(UNDER, ctx, function (i, q) {
    UND_TOP[i] = q ? +q.top || 0 : 0;
    if (!q) return;
    var o = i * 4, b = q.box;
    UND_BOX[o] = b[0]; UND_BOX[o + 1] = b[1]; UND_BOX[o + 2] = b[2]; UND_BOX[o + 3] = b[3];
  });
  var z0 = UND_TOP[0];
  for (var j = 1; j < UNDER.length; j++) UND_RISE[j] = UND_TOP[j] - z0;
  if (p.u.uOver) gl.uniform4fv(p.u.uOver, UND_BOX);
  if (p.u.uOverR) gl.uniform1fv(p.u.uOverR, UND_RISE);
  /* DR2, 2026-09-21: uOverZ.z and uOverF.y are DEAD and are written
     NOUGHT. They carried DRAPE_RUN and DRAPE_FILL_FOOT — one run and one
     foot for the whole desk — and the drape is per entry now, so the two
     numbers are in the emitted source (undRun / undFoot) and not in a
     uniform at all. The two components are still SENT, and the two
     declaration lines still name them, because the declarations are
     shader text and the shader text does not move on this card. Checked
     against the generated source: uOverF.y does not appear anywhere, and
     uOverZ.z appears exactly once — as bkCurve's `run` argument, which
     bkCurve overwrites with bkRun before it is read. Anything that ever
     wants a per-desk run again should take a component of its own rather
     than these two back. */
  if (p.u.uOverZ) gl.uniform4f(p.u.uOverZ, z0, PAPER_FLOAT, 0, DRAPE_SHADE);
  if (p.u.uOverF) gl.uniform2f(p.u.uOverF, DRAPE_FILL_TOP / ctx.L.k, 0);
  /* DR1 concept C wrote the sheet's own rectangle into uOverS here, which
     is where the eight plates were hinged. DR2: deleted with the plates —
     no program declares uOverS any more. */
}
/* the same list, for the JS height field (`O.paperHeight`) */
var UND_JS = [];
function undJS(ctx) {
  UND_JS.length = 0;
  evalList(UNDER, ctx, function (i, q) { if (q) UND_JS.push(q); });
  return UND_JS;
}

/* ---- THE GLOW LIST: what SHINES on the oak -------------------------
   `RoomObjects.glow(name, fn)`. The third thing a solid standing on this
   desk can do to the wood, after lifting the paper and casting a shadow:
   EMIT. `fn(ctx)` returns, once a frame:

       { at: [x, y],        the pool's centre, css px
         turn: [cos, sin],  the thing's own yaw, so an oriented box works
         half: [hx, hy],    the flat of the pool, css px
         feather: <css px>, how far it falls to nothing past that flat
         col: [r, g, b],    the emitter's OWN colour
         on: 0..1 }         and nothing when it is not emitting

   and the oak's own pass adds it. HANDOVER item 10: the laptop's ground
   quad used to carry its night pool as premultiplied colour AND to eat
   its own shadow's alpha where the pool was strong, which is how a broad
   grey-blue slab with a visible quad edge came to lie beside the journal
   on a desk whose screen was dark. A pool of light is a LIGHT on the
   wood; it is not a decal, it has no border, and it never lightens the
   shadow of the thing throwing it.

   Every entry is nought by DAY (the emitters here are all night-gated),
   so the day oak is the oak it was to the bit: `col += vec3(0.0)` is
   exact. Cap TWO. */
var GLOW = [], GLOW_CAP = 2, GLOW_N = 0;
var GL_AT = new Float32Array(GLOW_CAP * 4), GL_BOX = new Float32Array(GLOW_CAP * 4);
var GL_COL = new Float32Array(GLOW_CAP * 3);
O.glow = function (name, fn) {
  listAdd(GLOW, GLOW_CAP, name, fn);
  if (GLOW_N && GLOW.length > GLOW_N) console.warn('glow(' + name + ') after the oak was built');
};
O.glow.list = GLOW;
function glowDecl() {
  if (!GLOW.length) return '';
  return 'uniform vec4 uGlowAt[' + GLOW_CAP + '];  // C0: the pool\'s centre in css px, and its emitter\'s cos/sin\n' +
         'uniform vec4 uGlowBox[' + GLOW_CAP + ']; // the flat of the pool, its feather, and how much is on\n' +
         'uniform vec3 uGlowCol[' + GLOW_CAP + ']; // the emitter\'s own colour\n';
}
function glowGLSL() {
  var n = Math.min(GLOW_CAP, GLOW.length); GLOW_N = n;
  if (!n) return '';
  var s = '  /* C0: the pools of light on the wood, from RoomObjects.glow */\n';
  for (var i = 0; i < n; i++) {
    var A = 'uGlowAt[' + i + ']', B = 'uGlowBox[' + i + ']';
    s += '  if (' + B + '.w > 0.0) {\n' +
         '    vec2 gd = p - ' + A + '.xy;\n' +
         '    vec2 gq = vec2(gd.x*' + A + '.z - gd.y*' + A + '.w, gd.x*' + A + '.w + gd.y*' + A + '.z);\n' +
         '    vec2 ge = abs(gq) - ' + B + '.xy;\n' +
         '    float gs = length(max(ge, 0.0)) + min(max(ge.x, ge.y), 0.0);\n' +
         '    col += uGlowCol[' + i + ']*' + B + '.w*exp(-max(gs, 0.0)/' + B + '.z);\n' +
         '  }\n';
  }
  return s;
}
function glowUniforms(gl, p, ctx) {
  if (!GLOW.length || !p.u.uGlowBox) return;
  GL_AT.fill(0); GL_BOX.fill(0); GL_COL.fill(0);
  evalGlow(ctx);
  gl.uniform4fv(p.u.uGlowAt, GL_AT);
  gl.uniform4fv(p.u.uGlowBox, GL_BOX);
  gl.uniform3fv(p.u.uGlowCol, GL_COL);
}
function evalGlow(ctx) {
  for (var i = 0; i < GLOW.length; i++) {
    var reg = GLOW[i], q = null;
    try { q = reg.fn(ctx); }
    catch (e) { reg.err++; if (reg.err === 1) console.warn('desk glow ' + reg.name, e); }
    if (!q || !q.at || !(q.on > 0)) continue;
    var o = i * 4, t = i * 3;
    GL_AT[o] = q.at[0]; GL_AT[o + 1] = q.at[1];
    GL_AT[o + 2] = q.turn ? q.turn[0] : 1; GL_AT[o + 3] = q.turn ? q.turn[1] : 0;
    GL_BOX[o] = q.half[0]; GL_BOX[o + 1] = q.half[1];
    GL_BOX[o + 2] = Math.max(1e-3, q.feather); GL_BOX[o + 3] = q.on;
    GL_COL[t] = q.col[0]; GL_COL[t + 1] = q.col[1]; GL_COL[t + 2] = q.col[2];
  }
}

/* ==== LZ1: THE LAPTOP'S ANCHOR, WHICH IS A DESK FACT =================
   C0's OPEN 2. desk-laptop.js was 21 KB gz of COLD LOAD — a `<script
   defer>` in front of engine.js's boot, downloaded, parsed and run before
   the first picture, for a machine that stands outside the fit's frame
   and cannot be in that picture whatever we do. It could not simply be
   fetched later, because three callers ask `RoomObjects.laptop(S)` the
   instant a `,l1` link is opened and long before any mesh exists:
   place.js `P.lapPose` (the camera), engine.js `Room.laptopQuad` (the
   hand) and `Room.screenQuad` (the terminal's glass).

   THE SPLIT THIS DRAWS. Where the machine stands, how big it is and what
   its screen faces are facts about THIS DESK, of the same kind as
   `DESK_MM` — arithmetic on the sheet's own layout, no GL, no mesh, no
   atlas, about a fifth of a kilobyte of code. The MACHINE — the two
   programs, the UK keyboard, the legend atlas, the mesh, the draw — is
   twenty-one kilobytes and is fetched in the idle slot after the first
   picture (engine.js `needLaptop`).

   AND C0's OPEN 5, WHICH IS THE TRAP: "a piece registered after a program
   is built is not read", because the desk's generated GLSL is UNROLLED to
   the length of the lists at compile time. Reading the lists against a
   late laptop, entry by entry:
     · UNDER — the laptop is not in it and never was (the paper does not
       lie over the machine). Nothing.
     · SHADOW — its entry is `own`, so `shGLSL` SKIPS it: it emits no arm.
       Its only effect on the source is `shN()`, and the arms it does emit
       are gated on their own index, so the books at 1 and 2 stay at 1 and
       2. Identical source either way.
     · GLOW — `laptop-screen` is the ONLY entry in the list. Without it
       `glowDecl()` and `glowGLSL()` emit NOTHING, and with it one arm and
       three uniforms. THIS is the one the oak's fragment shader would
       have to be rebuilt for.
   So the answer is (a), the reserved slot, and it costs no rebuild at
   all: both entries are registered HERE, from the anchor, at `O.init`
   time — which is after every desk script has run, so the lists are in
   exactly the order they were (lamp, book-field, book-journal, laptop)
   and the generated source is the source it was, to the byte. The piece's
   own slot is reserved the same way and filled in when the file lands,
   which keeps the machine's draw where it was, after the books.

   `?lap=0` (no laptop) and `?lp=` (the pose) are read here now, because
   the file that used to read them may never be asked for.
   ==================================================================== */
/* millimetres, and they are Apple's own: a 14-inch MacBook Pro of the M3
   generation (X7). x runs across the machine, y runs from the HINGE (0)
   to the FRONT EDGE (D), z is up off the desk. */
var LP = {
  W: 312.6, D: 221.2,          // the 14-inch's footprint
  FOOT: 1.2, T: 11.2,          // the feet, and the top case above them
  LT: 4.3, LH: 221.2,          // the lid: 4.3 thick, as deep as the base
  LEAN: 15 * Math.PI / 180,    // opened to 105 degrees
  R: 7.5,                      // the corner radius of base and lid
  BEZ: 5.1, TOP: 5.1, CHIN: 19.7,
  NW: 32, NH: 8.6,             // the camera notch
  KU: 19.0, KV: 19.0,          // key pitch, across and down
  KGAP: 2.9, KRISE: 1.15,      // the gap between caps, and the cap's height
  KBX: 14.5,                   // the keyboard's width, in units
  KBY: 11,                     // the first row, from the hinge
  TPW: 130, TPH: 82, TPY: 134, // the trackpad, from the hinge
  GRW: 10.5, GRX: 5            // the speaker grilles
};
/* PH5 (superseded by TM1, below): ON A PHONE THE LID WAS SHUT. There is no terminal there, so the
   machine lies closed on the desk: the lid turned down flat over the
   deck (v runs from the hinge to the front edge) and lifted to sit on
   the caps, the glass facing down. LIDZ is that lift; at the desk it is
   0 and lidPt is the number it was. */
LP.LIDZ = 0;
/* TM1 (2026-10-02): and it is open again. The terminal is on the phone
   now (a portrait page, phone.js), so the machine stands as it does at a
   desk; LIDZ stays for the shut pose's arithmetic, at 0. */
LP.TOPZ = LP.FOOT + LP.T;                       // the top case's own surface
LP.KBW = LP.KBX * LP.KU;                        // 275.5
LP.KBX0 = (LP.W - LP.KBW) / 2;                  // 18.55
LP.KBH = 6 * LP.KV;                             // 114
/* THE SCREEN THE TERMINAL IS LAID ON is the display MINUS the notch's
   own strip — which is exactly what a Mac does with a full-screen
   window, and is why the terminal can never be under the notch. */
LP.DX0 = LP.BEZ; LP.DX1 = LP.W - LP.BEZ;        // 5.1 .. 307.5  (302.4 wide)
LP.DV0 = LP.CHIN; LP.DV1 = LP.LH - LP.TOP;      // 19.7 .. 216.1 (196.4 tall)
LP.NV = LP.DV1 - LP.NH;                         // 207.5: the notch's foot
LP.SW = LP.DX1 - LP.DX0;                        // 302.4
LP.SH = LP.NV - LP.DV0;                         // 187.8, clear of the notch
/* the DOM's own box for the screen, in css px. The terminal is laid on
   the four projected corners of this rectangle, so the number decides
   how big a line of type is on the glass and nothing else. */
LP.PX = [620, Math.round(620 * LP.SH / LP.SW)];

var LAP_OFF = Q.get('lap') === '0';
var LAP = (function () {
  var v = (Q.get('lap') || '').split(',').map(Number);
  /* MEASURED at the room pose, not guessed (X1-probe-p250-room.png,
     X1-probe-p150.png, X1-probe-p90.png): at 250 mm east of the sheet
     only the screen's corner is in the standing frame; at 90 the base is
     against the journal's fore-edge. The measured top needs the OPEN lid,
     not just the deck, inside it. At 102 east and twelve degrees round,
     the journal has 24 mm at rest (about 16 mm during its hover), the
     deck has 48 mm at the front, and the lid's far corner retains 10 mm
     at the east edge. It remains clean outside the fit's own right edge. */
  return (v.length === 3 && v.every(isFinite)) ? v : [102, -65, -12];
})();

/* a point of the lid, in local mm: `v` up the lid from the hinge, `t`
   out along its face normal. The mesh builder walks the whole lid with
   it, so it is published rather than copied. */
function lidPt(x, v, t) {
  var sa = Math.sin(LP.LEAN), ca = Math.cos(LP.LEAN);
  return [x, -v * sa + t * ca, LP.TOPZ + v * ca + t * sa + LP.LIDZ];
}

/* WHERE IT STANDS, and what that makes of the screen. Everything any
   other module needs to know about the laptop comes out of here, in
   WORLD css pixels (x east, y north, z up) — the units `Room.scenePoint`
   takes — so nothing outside this file has to know a millimetre.
   LZ1: and `machine` says whether the file that DRAWS it has landed. No
   caller needs it (every field below is the anchor's own arithmetic); it
   is here so a smoke test can tell an anchor from a machine. */
O.laptop = function (S) {
  if (LAP_OFF) return null;
  var L = S.layout, f = S.fld, mm = f.sheet.w * L.k / 840;
  var x0 = L.ox + f.sheet.w * L.k + LAP[0] * mm;
  var y0 = L.oy + f.sheet.h * L.k * 0.5 + LAP[1] * mm;
  var th = LAP[2] * Math.PI / 180, c = Math.cos(th), s = Math.sin(th);
  function W(p) {                                   // local mm -> world css px
    return [x0 + (p[0] * c + p[1] * s) * mm, -(y0 + (-p[0] * s + p[1] * c) * mm), p[2] * mm];
  }
  var t = LP.LT * 0.5 + 0.07;
  return {
    mm: mm, x: x0, y: y0, yaw: th, W: W, box: LP, machine: !!O.vaoLap,
    /* the screen's four corners, walked the way `Room.quadMat` walks a
       box: top-left, top-right, bottom-right, bottom-left as the reader
       sees them. The lid's `v` runs UP from the hinge, so the top of the
       picture is the HIGH v — and the high v here is the NOTCH's foot,
       not the panel's top, so nothing the terminal draws can ever be
       under the camera. */
    screen: [W(lidPt(LP.DX0, LP.NV, t)),
             W(lidPt(LP.DX1, LP.NV, t)),
             W(lidPt(LP.DX1, LP.DV0, t)),
             W(lidPt(LP.DX0, LP.DV0, t))],
    px: LP.PX,
    /* ==== R62: AND THE MACHINE'S OWN TWO FACES, for a hand that wants to
       take hold of it. `screen` above is the DISPLAY, which is what the
       terminal is laid on; a reader reaching for a laptop reaches for the
       laptop. So: the whole LID front (display, bezel and chin, one flat
       face because that is what it is) and the DECK, the top case the
       keys are set into — walked in the same order `screen` is and in the
       same world css pixels, so engine.js can hit-test them with the
       `inQuad` it tests the sheet with. The two are the silhouette from
       any pose that can see it: the lid leans back OVER the deck's hinge
       edge, so nothing between them is uncovered. ==== */
    lid: [W(lidPt(0, LP.LH, t)), W(lidPt(LP.W, LP.LH, t)),
          W(lidPt(LP.W, 0, t)), W(lidPt(0, 0, t))],
    deck: [W([0, 0, LP.TOPZ]), W([LP.W, 0, LP.TOPZ]),
           W([LP.W, LP.D, LP.TOPZ]), W([0, LP.D, LP.TOPZ])],
    /* the point a reader's eye would be at, and the way the screen looks:
       place.js builds the laptop pose off these two and nothing else */
    face: W(lidPt(LP.W / 2, (LP.DV0 + LP.NV) / 2, t)),
    normal: (function () {
      var ny = Math.cos(LP.LEAN), nz = Math.sin(LP.LEAN);
      return [(ny * s), -(ny * c), nz];       // rotated, world
    })(),
    /* X7: and the way the SCREEN'S OWN DOWN runs, in world — the lid's
       -v, turned by the yaw. Slide the pose's aim along it and the aim
       walks down the display, over the chin and onto the key deck,
       which is how `?lp=`'s fifth number gets a picture of the keys
       without anything in place.js knowing a millimetre. */
    down: (function () {
      var sa = Math.sin(LP.LEAN), ca = Math.cos(LP.LEAN);
      return [sa * s, -sa * c, -ca];
    })()
  };
};

/* ---- the two list entries, struck from the anchor -------------------
   Both were in desk-laptop.js and neither needed a byte of it: each is
   the anchor's own box turned into the frame the oak's pass works in.
   Registered at `O.init` (see `lapReserve` below), which is after every
   desk script has run — so the lists keep the order they had and the
   generated GLSL is unchanged. */
var LAP_SHD = { box: [0, 0, 0, 0], height: 0, lift: 1, cast: 0, contact: 0 };
var LAP_GLOW = { at: [0, 0], turn: [1, 0], half: [0, 0], feather: 1,
                 col: [0, 0, 0], on: 0 };
/* THE PANEL'S OWN EMITTED COLOUR, one place. desk-laptop.js's `LAP_FS`
   adds exactly this to the screen (`col += vec3(0.10,0.14,0.20)*(0.30 +
   0.70*uNight)*uOn`), so the pool it throws is the same three numbers and
   not a second guess at them; the file reads them back off `O.LAP_RGB`
   rather than keeping a copy. `POOL_K` is struck so the pool's peak is
   the peak the old decal had — 0.40*(0.30,0.42,0.62) = (0.120,0.168,
   0.248) against 1.2*(0.10,0.14,0.20) = (0.120,0.168,0.240) — because
   what HANDOVER item 10 is about is the SLAB and the EDGE, not the
   brightness. */
var LAP_RGB = [0.10, 0.14, 0.20], LAP_POOL_K = 1.2;
/* the pool's own flat and its falloff, in the machine's LOCAL millimetres
   — the same box and the same 94 mm the decal threw it from */
var LAP_POOL_AT = [LP.W * 0.5, 150.0], LAP_POOL_HALF = [118.0, 34.0], LAP_POOL_FALL = 94.0;

/* WHERE IT STANDS ON THE OAK. Its own cast stays in its own quad (the
   machine is at twelve degrees to the desk and a shadow struck in the
   desk's axis-aligned frame crabs off its own feet), so the oak's pass is
   asked for nothing — `own` — and this entry is in the list for its BOX
   and its HEIGHT: what stands on this desk is one published fact, and it
   is true before the machine's script has landed because the machine is
   standing there either way. */
function lapShadow(ctx) {
  var lp = O.laptop(ctx.S), bx = LAP_SHD.box;
  if (!lp) { bx[0] = bx[1] = bx[2] = bx[3] = 0; return LAP_SHD; }
  var c = Math.cos(lp.yaw), sn = Math.sin(lp.yaw), mm = lp.mm;
  /* the base's four corners in local mm, turned into css px, and the
     box that holds them: the list's boxes are axis-aligned and this
     machine is not, so what it publishes is its FOOTPRINT. */
  var x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9;
  var pts = [[0, 0], [LP.W, 0], [LP.W, LP.D], [0, LP.D]];
  for (var i = 0; i < 4; i++) {
    var px = lp.x + (pts[i][0] * c + pts[i][1] * sn) * mm;
    var py = lp.y + (-pts[i][0] * sn + pts[i][1] * c) * mm;
    if (px < x0) x0 = px; if (px > x1) x1 = px;
    if (py < y0) y0 = py; if (py > y1) y1 = py;
  }
  bx[0] = x0; bx[1] = y0; bx[2] = x1 - x0; bx[3] = y1 - y0;
  LAP_SHD.height = LP.TOPZ * mm;
  return LAP_SHD;
}
/* AND AT NIGHT THE SCREEN LIGHTS THE OAK — HANDOVER item 10, as a light
   and not a decal. `on` is the screen's own emission: night, and the
   machine awake. `O.lapOn = false` puts it to sleep (the hook X4 will
   want when the agent's session ends) and the pool goes out with the
   panel. */
function lapGlow(ctx) {
  var lp = O.laptop(ctx.S);
  LAP_GLOW.on = 0;
  if (!lp || !ctx.sun) return LAP_GLOW;
  var night = ctx.sun.night || 0, on = O.lapOn === false ? 0 : 1;
  if (!(night * on > 0)) return LAP_GLOW;
  var c = Math.cos(lp.yaw), sn = Math.sin(lp.yaw), mm = lp.mm;
  LAP_GLOW.at[0] = lp.x + (LAP_POOL_AT[0] * c + LAP_POOL_AT[1] * sn) * mm;
  LAP_GLOW.at[1] = lp.y + (-LAP_POOL_AT[0] * sn + LAP_POOL_AT[1] * c) * mm;
  /* the oak's pass turns a css-px offset back into this frame with
     (cos, -sin); local -> css uses (cos, +sin) down the second row, so
     the turn it wants is the machine's own, unnegated. */
  LAP_GLOW.turn[0] = c; LAP_GLOW.turn[1] = sn;
  LAP_GLOW.half[0] = LAP_POOL_HALF[0] * mm; LAP_GLOW.half[1] = LAP_POOL_HALF[1] * mm;
  LAP_GLOW.feather = LAP_POOL_FALL * mm;
  LAP_GLOW.col[0] = LAP_RGB[0] * LAP_POOL_K;
  LAP_GLOW.col[1] = LAP_RGB[1] * LAP_POOL_K;
  LAP_GLOW.col[2] = LAP_RGB[2] * LAP_POOL_K;
  LAP_GLOW.on = night * on;
  return LAP_GLOW;
}
/* THE RESERVED SLOTS, taken at `O.init` — the last moment before the oak
   and the sheet are built, and the first at which every desk script has
   had its say. The PIECE's slot is a draw that does nothing until the
   machine's file lands and `O.piece('laptop', ...)` replaces it in place
   (`O.piece` replaces by name, so the position in the draw order is the
   position this reserves). Idempotent, because `O.init` can be called
   twice on a warm path. */
var lapReserved = false;
function lapReserve() {
  if (lapReserved) return;
  lapReserved = true;
  /* A RESERVATION TAKES ONLY WHAT IS NOT THERE. `O.piece` replaces by
     name, which is exactly how the machine's file fills this slot when it
     lands — and it is also how a reservation made after the file has
     already landed would wipe the draw out. So the slot is taken only if
     nobody holds it: on the shipped page nobody does (the file is
     fetched), and on a page that has somehow run desk-laptop.js first the
     machine keeps its own place. */
  for (var i = 0; i < PIECES.length; i++) if (PIECES[i].name === 'laptop') return;
  O.piece('laptop', function () { /* LZ1: the machine's own file draws it */ }, {});
}
/* the two LIST entries are the anchor's outright — desk-laptop.js does
   not register them at all — so they are taken unconditionally, and here
   rather than at the top of this file so the lists keep their order. */
function lapReserveLists() {
  O.shadow('laptop', lapShadow, { own: 1 });
  O.glow('laptop-screen', lapGlow);
}
/* R71 (a): the laptop's measured anchor and its box, for place.js's
   camera and for scripts/test-room-measurements.js; and, LZ1, for
   desk-laptop.js, which reads all four back instead of owning them. */
O.LAP_MM = LAP;
O.LAP_BOX_MM = LP;
O.LAP_RGB = LAP_RGB;
O.lapOff = function () { return LAP_OFF; };
O.lapLidPt = lidPt;
/* and the reservation itself, published beside `__src` for the same reason
   `__src` is published: so the claim "the generated source does not change
   when the machine lands late" can be checked with no GPU and no browser
   (room-rev10/lz1-specs/glslid.js), which is where C0's `uUnd` collision
   would have been caught. `O.init` calls it; nothing else needs to. */
O.__lapReserve = function () { lapReserveLists(); lapReserve(); };

/* ---- THE OAK HOOK: GLSL COMPILED INTO THE OAK'S OWN SHADER ---------
   Not everything on this desk is a solid with a mesh. The lamp's base and
   the pencil are PAINTED ON THE WOOD, in css pixels, in the pass that was
   already running — no mesh, no draw, no byte on the wire (R17 d) — so
   their piece hands the oak a `decl` (its uniforms) and a `body` (its
   arithmetic), and the oak's own shader is assembled from them in
   registration order. The preamble below is the block's shared frame: the
   millimetre and the direction the light comes from. With no piece
   registering a body, none of it is compiled. */
function oakDecl() {
  var s = '';
  for (var i = 0; i < PIECES.length; i++) {
    var o = PIECES[i].hooks.oak;
    if (o && o.decl) s += o.decl;
  }
  return s;
}
function oakBody() {
  var body = '', i;
  for (i = 0; i < PIECES.length; i++) {
    var o = PIECES[i].hooks.oak;
    /* trimmed and joined with exactly one newline, so the emitted source is
       the source it replaced and not the source plus the seams */
    if (o && o.body) body += o.body.replace(/^\n+|\n+$/g, '') + '\n';
  }
  return body ? OAK_PREAMBLE + '\n' + body + '  }' : '';
}
function oakUniforms(gl, p, ctx) {
  for (var i = 0; i < PIECES.length; i++) {
    var pc = PIECES[i], o = pc.hooks.oak;
    if (pc.dead || !o || !o.uniforms) continue;
    try { o.uniforms(gl, p, ctx); }
    catch (e) { pc.err++; if (pc.err === 1) console.warn('desk oak ' + pc.name, e); }
  }
}

/* ---- THE SHADOW LIST'S OWN GLSL, generated -------------------------- */
function shN() { return Math.max(1, Math.min(SHADOW_CAP, SHADOW.length)); }
function shDecl() {
  return 'uniform vec4 uShBox[' + SHADOW_CAP + '];  // C0: what stands on the oak, css px: x y w h\n' +
         'uniform vec4 uShK[' + SHADOW_CAP + '];    // its cast weight, its contact weight, its lift, spare\n' +
         'uniform vec3 uShSun[' + SHADOW_CAP + '];  // the sun at its own station: direction and length\n';
}
function shOwn(i) { return !!(SHADOW[i] && SHADOW[i].opts && SHADOW[i].opts.own); }
function shGLSL() {
  var n = shN(), i; SHADOW_N = n;
  var s = '  {\n    float blur = 10.0*(1.0 + (uSun2.x - 1.0)*0.5);\n    vec3 BSH = vec3(0.0);\n';
  /* THE THROWS FIRST AND THE CONTACT LINES AFTER, which is the order the
     two named books were written in. Every one of these is `col*(1-a)`
     (BSH is black), and a product of four floats taken in another order
     can differ in its last bit — the one thing this card may not spend. */
  for (i = 0; i < n; i++) {
    if (shOwn(i)) continue;
    var B = 'uShBox[' + i + ']', K = 'uShK[' + i + ']', U = 'uShSun[' + i + ']';
    s += '    if (' + B + '.z > 0.0)\n      col = mix(col, BSH, ' + K + '.x*uSun2.y*cst*boxCov(p - ' +
         U + '.xy*' + U + '.z*cst*8.0*' + K + '.z, ' + B + ', blur*' + K + '.z));\n';
  }
  for (i = 0; i < n; i++) {
    if (shOwn(i)) continue;
    var B2 = 'uShBox[' + i + ']', K2 = 'uShK[' + i + ']';
    s += '    if (' + B2 + '.z > 0.0)\n      col = mix(col, BSH, ' + K2 + '.y*(2.0 - ' + K2 +
         '.z)*boxCov(p - vec2(0.0,1.0), ' + B2 + ', 2.0*' + K2 + '.z));\n';
  }
  return s + '  }\n';
}
/* R45: AND THE SHEET IS NOT ON THE OAK WHERE IT COMES OVER A BOOK. The
   mask of the things the paper is actually held up over — `opts.lip` at
   registration — with each arm gated on its own box, so a thing that has
   gone (an open book draws its footprint in to nothing) takes its tap
   with it instead of leaving a zero box for `boxCov` to find. */
/* DR1, 2026-09-21: the tap follows each thing's OWN fall, not one number
   for the desk. This was the only place outside the height field that
   read DRAPE_RUN, and the run is per entry — the journal's fall is 177
   css px against the field book's 64 — so one 41.6 px tap would be the
   wrong size for both. uShK[i].w is the thing's height in css px
   (shUniforms), which is what the fall is proportional to, so the tap is
   DRAPE_RUN_K * 1.6 of it.
   DR2, 2026-09-21: and it takes no argument now. The `run` parameter was
   the literal the wrap and the card were handed; both are deleted, so the
   only arm left is the one below and the oak's emitted FS is unchanged. */
function shLipGLSL() {
  var n = shN(), s = 'float shLip(vec2 p){\n  float m = 0.0;\n', any = 0;
  for (var i = 0; i < n; i++) {
    if (!(SHADOW[i] && SHADOW[i].opts && SHADOW[i].opts.lip)) continue;
    any = 1;
    var r = 'max(4.0, uShK[' + i + '].w*' + (DRAPE_RUN_K * 1.6).toFixed(2) + ')';
    s += '  if (uShBox[' + i + '].z > 0.0) m = max(m, boxCov(p, uShBox[' + i + '], ' + r + '));\n';
  }
  return any ? s + '  return m;\n}' : 'float shLip(vec2 p){ return 0.0; }';
}
function shUniforms(gl, p, ctx, sunAtFn) {
  SH_BOX.fill(0); SH_K.fill(0); SH_SUN.fill(0);
  evalList(SHADOW, ctx, function (i, q) {
    if (!q) return;
    var o = i * 4, b = q.box, lift = q.lift === undefined ? 1 : +q.lift;
    SH_BOX[o] = b[0]; SH_BOX[o + 1] = b[1]; SH_BOX[o + 2] = b[2]; SH_BOX[o + 3] = b[3];
    SH_K[o] = +q.cast || 0; SH_K[o + 1] = +q.contact || 0; SH_K[o + 2] = lift; SH_K[o + 3] = +q.height || 0;
    var u = sunAtFn(b[0] + b[2] / 2, b[1] + b[3] / 2), t = i * 3;
    SH_SUN[t] = u[0]; SH_SUN[t + 1] = u[1]; SH_SUN[t + 2] = u[2];
  });
  if (p.u.uShBox) gl.uniform4fv(p.u.uShBox, SH_BOX);
  if (p.u.uShK) gl.uniform4fv(p.u.uShK, SH_K);
  if (p.u.uShSun) gl.uniform3fv(p.u.uShSun, SH_SUN);
}
/* the oak's own pass also wants to know whether ANYTHING is standing on it,
   for R45's one extra tap from the lifted paper's lip */
function shBoxes() { return SH_BOX; }

/* THE FURNITURE SVG IS NOT THE PAPER ANY MORE. Three of its groups are
   objects now and have to come out of the raster or they are drawn twice:
   the four shadows (baked with a fixed translate from whatever sun was up
   when the file was serialised), the fold bands (a fixed north-west light)
   and the flat turned corner. Done as a string edit at raster time rather
   than in the file, so E3's own use of it merges clean. */
/* issued after frame 1, with the ground's eleven — or compiled outright
   if the field was already there and there was no second frame to wait
   for. Idempotent: both callers ask, the first one wins. */
O.precompile = function (H) {
  /* C0: and each piece's own idle slot. THIS is where a new thing on the
     desk compiles — after the first picture, in the same staged slot the
     laptop has always taken, so nothing new ever compiles on the path to
     frame 1. Idempotent: the warm path asks again, so a piece must make
     its own `late` idempotent too. */
  lateH = H;                        /* LZ1: for a piece that lands later */
  for (var pi = 0; pi < PIECES.length; pi++) {
    var pc = PIECES[pi];
    if (pc.dead || !pc.hooks.late) continue;
    try { pc.hooks.late(H); }
    catch (e) { pc.dead = 1; pc.err++; console.warn('desk late ' + pc.name, e); }
  }
  if (O.pLate || !O.S) return;
  var r7 = SHEET_SRC('#define R7 1\n');
  O.pLate = H.prog(H.gl, r7[0], r7[1], 'sheet-r7');
};

O.prepFurniture = function (s) {
  s = s.replace(/<path\b[^>]*class="sv-shadow"[^>]*>\s*<\/path>/g, '');
  s = s.replace(/<path\b[^>]*class="sv-shadow"[^>]*\/>/g, '');
  s = s.replace(/<g class="sv-folds"[\s\S]*?<\/g>/, '');
  s = s.replace(/<g class="sv-curl"[\s\S]*?<\/g>/, '');
  return s;
};

O.init = function (gl, S, mk) {
  O.S = S; O.mk = mk;
  /* LZ1: THE LAPTOP'S THREE SLOTS, TAKEN HERE AND NOT WHERE THE MACHINE
     IS. Every desk script has run by now and nothing has been compiled
     yet, so this is the one place a reservation can be made that keeps
     the lists in the order they were (lamp, book-field, book-journal,
     laptop) AND is in front of the generated GLSL — C0's OPEN 5, which
     says a registration after a program is built is not read. See the
     anchor's own head for why the glow is the entry that needs it. */
  lapReserveLists();
  lapReserve();
  /* THE DESK'S PROGRAM. The oak is built here on frame 1: a desk that
     changes its grain after it appears is worse than the compile cost.
     P3 (P2's plan item 2): THERE USED TO BE TWO. A `desk-plain` was
     compiled on this line and `O.buildWood`, two lines on, replaced
     `O.pDesk` with `desk-oak` before anything had drawn with it — a whole
     program compiled, linked and collected on the path to the first
     picture for nothing. P2's stack probe charged it 20.5-30.5 ms of
     `getShaderParameter` in task 1 (objects.js:2897, REPORT-room-P2.md
     section 1) plus its own compile in the driver's queue, which every
     later query had to wait behind. The oak IS the desk's program. */
  O.buildWood();
  var e4 = SHEET_SRC(''); O.pSheet = mk.prog(gl, e4[0], e4[1], 'sheet-e4');
  /* C0: and each piece's own frame-1 compile, in registration order, in
     the slot the two books' program had. NOTHING NEW GOES HERE — a piece
     that wants a program it did not already have on this path asks for it
     in `late` (the idle slot after the first picture) or `warm` (the
     room's staged lane), which is what keeps a new thing on the desk off
     the first map paint (scripts/ROOM-STARTUP.md, REPORT-room-B2.md). */
  var ctx0 = deskCtx(gl, S, null);
  for (var pi = 0; pi < PIECES.length; pi++) {
    var pc0 = PIECES[pi];
    if (!pc0.hooks.init) continue;
    try { pc0.hooks.init(gl, ctx0); }
    catch (e0) { pc0.dead = 1; pc0.err++; console.warn('desk init ' + pc0.name, e0); }
  }

  /* THE PAPER'S GRID. The creases are shaded per fragment now, so the only
     thing on the sheet that needs vertices is the turned corner — 64 sheet
     units of fold, which at 224 x 128 is six cells along each leg, enough
     that its edge is a fold and not a staircase. 57,344 triangles, one
     static buffer, built once and never rebuilt. */
  if (O.pSheet) {
    var NX = 224, NY = 128;
    var gy = gradeAxis([0, 0.5, 1 - 160 / SHEET_BOX().h, 1], [42, 38, 48], [0.40, 0.30, 0.00]);
    /* X2: AND THE CREASE ACROSS HAS TO BE A ROW OF THIS GRID. The three
       long creases are at a quarter, a half and three quarters of the
       width and the columns are even, so 56, 112 and 168 of 224 land on
       them exactly. The ROWS are graded (they are packed where the paper
       bends), so a half of the height is not a row — and a fold whose
       hinge falls between two rows drags a cell of paper over itself and
       comes out ragged. The nearest row IS the crease: it is 0.6 sheet
       units off the half at this grading, which is under a millimetre on
       a sheet 850 mm deep and nothing the eye can find. */
    O.foldCy = gy[0];
    for (var q2 = 0; q2 <= NY; q2++) if (Math.abs(gy[q2] - 0.5) < Math.abs(O.foldCy - 0.5)) O.foldCy = gy[q2];
    var v = new Float32Array((NX + 1) * (NY + 1) * 2), n = 0;
    for (var j = 0; j <= NY; j++) for (var i = 0; i <= NX; i++) { v[n++] = i / NX; v[n++] = gy[j]; }
    var idx = new Uint32Array(NX * NY * 6); n = 0;
    for (j = 0; j < NY; j++) for (i = 0; i < NX; i++) {
      var A = j * (NX + 1) + i, B = A + 1, Cc = A + NX + 1, D = Cc + 1;
      idx[n++] = A; idx[n++] = Cc; idx[n++] = B; idx[n++] = B; idx[n++] = Cc; idx[n++] = D;
    }
    O.sheetN = n;
    var vb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, vb);
    gl.bufferData(gl.ARRAY_BUFFER, v, gl.STATIC_DRAW);
    var ib = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idx, gl.STATIC_DRAW);
    O.vaoSheet = gl.createVertexArray(); gl.bindVertexArray(O.vaoSheet);
    gl.bindBuffer(gl.ARRAY_BUFFER, vb); gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
    gl.bindVertexArray(null);
    buildSkirt(gl); buildCurl(gl); buildCorner(gl);   /* merge: R22's SE curl + R23's NE corner */
  }
  O.ready = true;
};

/* =================================================================
   R7: THE MESHES
   ================================================================= */
/* THE CUT EDGE OF THE SHEET, as a perimeter strip. Inset three units
   inside the deckle so the torn outline in the furniture texture is
   always what makes the silhouette and this band is never seen past it;
   the top of the band takes the sheet's own lie in the vertex shader and
   the foot of it sits on the oak. The south-east corner is the chamfer
   the curl is folded off, walked in six steps. */
function SHEET_BOX() {
  return (window.RoomField && RoomField.sheetBox) ? RoomField.sheetBox() : { w: 2364, h: 1216 };
}

/* ==== R22 (c): THE PAPER'S GRID IS GRADED, NOT UNIFORM ===============
   R15 left this open: "the fold is quantised to one grid cell … raising
   the grid is the fix and it costs triangles." It does not have to. The
   sheet's height field is held at EXACTLY ZERO inside the neat rule and
   for a further twelve units past it (R7's `pmask`), so two thirds of the
   224 x 128 stations were being spent on a plane. Everything that has a
   shape in it — the lie's rim, the drape over a board, the turned corner
   — is in the margins.

   So the same 28,900 vertices and the same 57,344 triangles are
   REDISTRIBUTED: each axis is cut at anchors that must stay on a vertex
   line, each span is given its own count, and inside a span the stations
   are pushed toward its two ends by

       S(x) = x - A sin(2 pi x) / (2 pi),   S' = 1 - A cos(2 pi x)

   which is monotone for A < 1, fixes both ends, and at A = .6 puts the
   outermost cell at four tenths of the even one.

   ONE AXIS, NOT TWO, AND WITH A CEILING ON THE CELL. Every book on this
   desk lies along the SOUTH edge, so it is the rows that want the
   stations and the columns are left exactly as R7 walked them. And no
   cell that a board's edge can fall in may exceed about thirteen sheet
   units, or the paper sinks THROUGH the cover just inside the board (see
   DRAPE_HOLD) — the south span is walked evenly at 3.3, which is a third
   of what it was; the two northern spans, where the height field is held
   at zero by the neat rule and no book can ever be, take the slack.

   The anchors are not arbitrary either: 0.5 across is the sheet's cross
   crease, which R7 needs ON a vertex line for the mesh to carry a tent at
   all, and 160 units off the foot is clear of every board's head down to
   a quarter of the fit's zoom. Measured at the fit: the cell at the
   sheet's foot goes from 9.5 sheet units (5.7 css px) to 3.3 (2.0). */
function gradeAxis(anchors, counts, push) {
  var out = [], k;
  for (var s = 0; s < counts.length; s++) {
    var a = anchors[s], b = anchors[s + 1], N = counts[s], A = push[s];
    for (var i = (s === 0 ? 0 : 1); i <= N; i++) {
      var x = i / N;
      if (A > 0) x -= A * Math.sin(6.283185307 * x) / 6.283185307;
      out.push(a + (b - a) * x);
    }
  }
  /* the ends are exact: nothing about the deckle may move by a rounding */
  out[0] = anchors[0]; out[out.length - 1] = anchors[anchors.length - 1];
  return out;
}

/* ==== R22 (c): THE TURNED CORNER IS ITS OWN MESH =====================
   The fold runs at forty-five degrees across a grid whose columns and
   rows are axis-aligned, so wherever the fold is carried by moving the
   SHEET's vertices it comes out as a staircase — a whole cell of it, and
   at 3x over the journal that staircase is the loudest thing in the
   corner. Grading the grid makes the step small; it cannot make it
   straight.

   So the fold is taken off the sheet's grid altogether. The face COLLAPSES
   onto the fold line (SHEET_VS, `uWarp > 1.5`: every station past the fold
   is slid back along the diagonal until q is exactly zero), which leaves
   the face's boundary lying exactly on the fold at any zoom with no new
   triangles; and the flap that turns back off it is this mesh, walked in
   its OWN coordinates — along the fold and across it — so its two edges
   are the fold and the sheet's own cut, exactly, and the cut edge running
   round its tip is as fine as the fold is long.

   960 triangles, one static buffer, one draw. */
function buildCurl(gl) {
  var sb = SHEET_BOX(), curl = Math.min(64, sb.w * 0.045);
  /* R45: thirty-two rows along q, not twelve. R22's flap was a fold with a
     shallow bow in it and twelve rows carried that; a ROLL turns through
     four and a half radians over the same strip, and at twelve the curl's
     outline came out as a visible polygon — R23 walks its own fan in 48
     for exactly this reason. Across the crease twenty-eight is enough:
     that direction is straight and nothing in it bends. 1,792 triangles,
     one static buffer, one draw. */
  var q2 = 2 * curl, N = 28, M = 32, av = [], idx = [], row = [], prev = null;
  for (var j = 0; j <= M; j++) {
    var q = q2 * j / M, span = q2 - q;
    row = [];
    for (var i = 0; i <= N; i++) {
      /* the station q units past the fold and s units in from the east
         edge: x = w - s, y = h + s - 2*curl + q */
      var s = span * i / N;
      row.push(av.length / 2);
      av.push((sb.w - s) / sb.w, (sb.h + s - q2 + q) / sb.h);
    }
    if (prev) for (var c = 0; c < N; c++)
      idx.push(prev[c], prev[c + 1], row[c], row[c], prev[c + 1], row[c + 1]);
    prev = row;
  }
  O.curlN = idx.length;
  var vb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, vb);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(av), gl.STATIC_DRAW);
  var ib = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(idx), gl.STATIC_DRAW);
  O.vaoCurl = gl.createVertexArray(); gl.bindVertexArray(O.vaoCurl);
  gl.bindBuffer(gl.ARRAY_BUFFER, vb); gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
  gl.bindVertexArray(null);
}

function buildSkirt(gl) {
  var sb = SHEET_BOX();
  var curl = Math.min(64, sb.w * 0.045), IN = 3;
  var x0 = IN / sb.w, x1 = 1 - IN / sb.w, y0 = IN / sb.h, y1 = 1 - IN / sb.h;
  /* ==== R22 (a): THE CUT EDGE WAS CUT FOR THE WRONG CORNER ============
     The fold in SHEET_VS turns back everything with
     `q = (x - (w - curl)) + (y - (h - curl)) > 0`, and that line meets the
     east edge at `h - 2*curl` and the foot at `w - 2*curl`: the corner the
     sheet actually loses is TWICE the chamfer — 128 sheet units, 77 css px
     on each leg. The skirt was cut at `curl`. So thirty-eight css pixels of
     the paper's cut edge ran on down the east side and along the foot past
     where the paper ends, and a hairline of chamfer lay across the middle
     of the notch — a grey needle standing on the oak beside the journal's
     head and a pale scratch over its cover, both of them paper's edge with
     no paper on it. They are the whole of what reads as "a weird effect on
     the paper over the journal" at the south-east corner.

     Inset perpendicular, not per-axis: the fold line moved IN by three
     units is `x + y = (w - curl) + (h - curl) - 3*sqrt2`, which meets the
     inset east line at `h - 2*curl - 0.414*IN`. */
  var C2 = 2 * curl + 0.4142 * IN;
  var cx = C2 / sb.w, cy = C2 / sb.h;
  /* R15: the foot and the two sides are walked THREE TIMES as finely as
     R7 walked them. The band's foot steps from the oak up onto a book's
     cover at the board's edge (SHEET_VS, `bookOn`) and at R7's 64 / 20
     stations that step was a whole 20 css px of slanted paper; at 192 /
     64 it is three, which is the fold the eye reads. R22: and the chamfer
     is walked at 32, not 6 — it is twice as long as R7 thought it was and
     the journal's own head crosses it, so its foot has to step from the
     oak onto the cover somewhere along it. */
  var run = [
    [[x0, y0], [x1, y0], 64, [0, -1]],                       // the head
    [[x1, y0], [x1, 1 - cy], 64, [1, 0]],                    // the east
    [[x1, 1 - cy], [1 - cx, y1], 32, [0.7071, 0.7071]],      // the chamfer
    [[1 - cx, y1], [x0, y1], 192, [0, 1]],                   // the foot
    [[x0, y1], [x0, y0], 64, [-1, 0]]                        // the west
  ];
  var av = [], bv = [], idx = [], n = 0;
  run.forEach(function (seg) {
    var a = seg[0], b = seg[1], N = seg[2], nr = seg[3], base = n;
    for (var i = 0; i <= N; i++) {
      var t = i / N, x = a[0] + (b[0] - a[0]) * t, y = a[1] + (b[1] - a[1]) * t;
      av.push(x, y, x, y); bv.push(nr[0], nr[1], 0, nr[0], nr[1], 1); n += 2;
    }
    for (var j = 0; j < N; j++) {
      var A = base + j * 2, B = A + 1, C = A + 2, D = A + 3;
      idx.push(A, B, C, C, B, D);
    }
  });
  O.skirtN = idx.length;
  var vb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, vb);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(av), gl.STATIC_DRAW);
  var nb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, nb);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(bv), gl.STATIC_DRAW);
  var ib = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(idx), gl.STATIC_DRAW);
  O.vaoSkirt = gl.createVertexArray(); gl.bindVertexArray(O.vaoSkirt);
  gl.bindBuffer(gl.ARRAY_BUFFER, vb); gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindBuffer(gl.ARRAY_BUFFER, nb); gl.enableVertexAttribArray(1);
  gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 0, 0);
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
  gl.bindVertexArray(null);
}

/* R23: THE CORNER'S OWN MESH — a 20 x 20 fan over the triangle past the
   north-east fold line, in the sheet's own uv. Its first row IS the fold
   line and its two outer columns ARE the sheet's north and east edges, so
   the three boundaries that can be seen are exact; the rows converge on the
   corner's own point, which is the one place resolution is not wanted. The
   attribute carries q/c per vertex rather than letting the shader recover
   it from the position, so the row on the fold line is 0 to the bit and
   the crease cannot crack. 800 triangles, one static buffer, one draw, and
   the draw is skipped entirely while the sheet is at the fit. */
function buildCorner(gl) {
  var sb = (window.RoomField && RoomField.sheetBox) ? RoomField.sheetBox() : { w: 2364, h: 1216 };
  /* S-B2: A QUARTER DISC, IN POLAR. Rows run from the peel's boundary in
     to the corner's own point, columns round the quarter turn from the
     north edge to the east edge, so the two edges that bend are rows of
     the mesh and read as curves rather than as a polygon. Forty by forty:
     the boundary is where a peel is most easily caught out (any facet
     there would be the very crease the peel exists to be rid of) and it
     is the row with the most screen length. 1681 vertices, 3200 quads,
     one static buffer, one draw, skipped entirely at the fit.
     The attribute carries u and the turn; the SHADER builds the station,
     because the radius breathes with the tug. */
  var c = NE.c, N = 40, M = 40, av = [], bv = [], idx = [];
  for (var i = 0; i <= N; i++) {
    var t = i / N;                                       // 0 at the boundary, 1 at the point
    for (var j = 0; j <= M; j++) {
      var v = j / M, th = v * Math.PI * 0.5, rho = c * (1 - t);
      var x = sb.w - rho * Math.cos(th), y = rho * Math.sin(th);
      av.push(x / sb.w, y / sb.h);                       // the flat station, for reference only
      bv.push(t, v, 0);
    }
  }
  for (var r = 0; r < N; r++) for (var k = 0; k < M; k++) {
    var A = r * (M + 1) + k;
    idx.push(A, A + 1, A + M + 1, A + 1, A + M + 2, A + M + 1);
  }
  O.cornerN = idx.length;
  var vb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, vb);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(av), gl.STATIC_DRAW);
  var nb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, nb);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(bv), gl.STATIC_DRAW);
  var ib = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(idx), gl.STATIC_DRAW);
  O.vaoCorner = gl.createVertexArray(); gl.bindVertexArray(O.vaoCorner);
  gl.bindBuffer(gl.ARRAY_BUFFER, vb); gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindBuffer(gl.ARRAY_BUFFER, nb); gl.enableVertexAttribArray(1);
  gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 0, 0);
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
  gl.bindVertexArray(null);
}


/* THE NEAT RULE, in sheet units — the one rectangle nothing may lift
   under. field.js's sheetBox() builds it from these same four numbers
   (C.px/C.py/C.gap/C.band and the 9 the rule stands off the band). */
function neatRect() {
  var C = window.RoomField && RoomField.C;
  if (!C) return [67, 103, 2230, 656];
  /* AND ONE GRID CELL WIDER THAN THE RULE. The lift is evaluated per
     VERTEX and the grid's cells are 10.6 x 9.5 sheet units, so a vertex
     just outside the rule carries its triangle's lift a cell INSIDE it:
     measured, a six-device-pixel ring round the rule differed from E4 at
     tilt 0. Holding the zero out past the rule puts the first lifted
     vertex a whole cell clear of it and the ring goes to nothing.

     R22: AND IT IS NOT THE SAME BOTH WAYS ANY MORE. The COLUMNS are still
     R7's even 10.6, so twelve is still a cell across; the ROWS are graded
     (gradeAxis) and the two spans the rule's head and foot fall in are
     walked at up to 13.5, so down the sheet it takes sixteen. Measured at
     tilt 0 against the merged engine, inside the 780,923 px of the map
     window: 109 px came back ONE level off at twelve, 35 at sixteen, and
     not one pixel of either is off by two. */
  var mx = C.gap + C.band + 9 + 12, my = C.gap + C.band + 9 + 16;
  return [C.px - mx, C.py - my, C.cw * C.weeks + mx * 2, C.ch * C.rows + my * 2];
}

/* WHERE THE CAMERA IS, in sheet units. The eye is in world coordinates
   (engine.js vp()), and the world is the sheet laid at ox/oy and scaled
   by k with its y turned over. The sheen on paper, cloth and card is the
   half-vector against this, so it has to be exact and it has to be asked
   for every frame: it moves under every gesture. */
function eyeInSheet(S) {
  var e = S.camEye, L = S.layout;
  if (!e) return [L.cw * 0.5, L.ch * 0.5, 900 / L.k];
  return [(e[0] - L.ox) / L.k, (-e[1] - L.oy) / L.k, e[2] / L.k];
}

/* how much sheen the sky has to give. Overcast and at night there is no
   specular worth the name; in sun there is, and it is what says the
   objects are solid at the room pose. Held under a twentieth either way. */
function specOf(s) {
  return 0.055 * (0.30 + 0.70 * s.cast) * (1.0 - 0.55 * s.night);
}




/* the DOM keeps the labels for now (E6 owns the open books), so the box
   they are set in has to be where the cover is drawn */
/* THE DOM LAY. The lamp source is the one thing `place` still does here:
   the camp is a place in the WORLD and only the engine knows where the
   world has been panned to (sky.js :696). Everything a PIECE lays out —
   the two labels' boxes, their hit clips — is the piece's own `place`. */
O.place = function (S) {
  if (window.Sky && Sky.setLampSource && !O._lampWired) {
    O._lampWired = true;
    /* LP1 (2026-10-07): THE LAMP IS OVER THE SHEET, NOT AT THE CAMP. Tommy: "The lamp shouldn't be a location on
       the map. The lamp should cast light over the sheet, and therefore everything on the map should be lit the
       same". The light stood at the survey camp on today's front, so a pan west walked out of its pool and the
       whole room went dark. It stands over the middle of the map window now, wherever the map is panned to;
       campPoint is kept for anything that wants the camp itself. */
    Sky.setLampSource(function () { return lampOverSheet(O.S); });
  }
  for (var i = 0; i < PIECES.length; i++) {
    var pc = PIECES[i];
    if (pc.dead || !pc.hooks.place) continue;
    try { pc.hooks.place(S); }
    catch (e) { pc.err++; if (pc.err > 3) pc.dead = 1; console.warn('desk place ' + pc.name, e); }
  }
};

/* WHERE THE LANTERN IS, IN THE ROOM.

   Under the sun or the moon the wax catches toward a BEARING, which is
   what --lamp-x has always meant. Under the survey party's lantern the
   light is a PLACE — the camp, at the front of the survey, inside the
   portal — and survey-map.js (:363) re-centres the pool on it and makes
   every shadow on the desk radiate away from it, short beside it and long
   away from it (Desk.one :295). E1 never registered a lamp source, so at
   night the engine's desk was lit from a bearing it does not have: the
   left margin came out 30 of luma too bright and the right 18 too dark
   against 8102 at `?sky=2026-09-11T22:00`.

   The camp is where the plough is: the middle of the front's own day
   cell, on the front's row. It is taken at ground level, which is exact
   on the flat sheet and within a few pixels when the map is raised. */
function lampOverSheet(S) {
  var P = S && (S.portalScr || S.portal);
  if (!P || !(P.w > 0)) return null;
  return { x: P.l + P.w / 2, y: P.t + P.h / 2 };
}
function campPoint(S) {
  var f = S && S.fld, L = S && S.layout, C = window.RoomField && window.RoomField.C;
  if (!f || !L || !C || f.front.none) return null;
  var mx = (f.front.e0 + f.front.e1) * 0.5;
  var my = C.py + (f.front.row + (C.EDGE_ROWS || 0) + 0.5) * C.ch;
  /* E6, closing E4's note 6: THE CAMP IS ON THE GROUND, NOT AT THE DATUM.
     E4 took it at ground level because nothing on the main thread could
     read the field; the pick reads it now, so the lamp's pool is asked of
     the same forward transform the shaders use — height, tilt, turn,
     aim-lift and the perspective magnification and all — instead of the
     flat approximation below, which was a few pixels out whenever the map
     was raised. The old arithmetic stands as the fallback for `?day=0`.

     R42 (c): and that fallback now uses the OLD aim rule, because the new
     one is the ground's height under the aim and the ground can only be
     read through day.js — which is the one module `?day=0` takes away. So
     under `?day=0` alone the lamp's pool stands a few px from where the
     shaders put the camp at a tilt. TODO: if the pick ever moves out of
     day.js, this fallback should go with it rather than be mended. */
  if (S.day && S.day.toScreen) {
    var sp = S.day.toScreen(mx, my);
    if (sp) return { x: sp[0], y: sp[1] };
  }
  var kk = S.portal.w / S.view.w / L.k;
  var dx = (mx - (S.view.x + S.view.w * 0.5)) * kk;
  var dy = (my - (S.view.y + S.view.h * 0.5)) * kk;
  var cy = Math.cos(S.yaw), sy = Math.sin(S.yaw);
  var rx = dx * cy - dy * sy, ry = dx * sy + dy * cy;
  var rel = (function (t) { var k = Math.min(Math.max(t / (75 * Math.PI / 180), 0), 1); return 1 - (1 - k) * (1 - k); })(S.tilt);
  var lift = Math.min(0.34 * f.maxHeight * 20 * rel, 0.2 * S.view.h * rel) * kk;
  var v = ry * Math.cos(S.tilt) + lift * Math.sin(S.tilt);
  var px = (S.portal.l - L.ox) / L.k + (S.portal.w / L.k) * 0.5 + rx;
  var py = (S.portal.t - L.oy) / L.k + (S.portal.h / L.k) * 0.5 + v;
  return { x: L.ox + px * L.k, y: L.oy + py * L.k };
}
O.campPoint = campPoint;

/* the sun, as the objects on the desk need it */
function sunOf() {
  var L = window.Sky && Sky.screen ? Sky.screen() : null;
  var el = L ? Math.max(4, L.el) : 34;
  var s = {
    dx: L ? L.dx : 0.7071, dy: L ? L.dy : 0.7071,
    len: L ? L.len : 1, cast: L && L.cast !== undefined ? L.cast : 1,
    soft: L ? L.soft : 1, dark: L ? L.dark : 1, el: el
  };
  var w = (window.Sky && Sky.wash) ? Sky.wash() : null;
  s.night = w ? w.night : 0;
  s.dusk = w ? w.dusk : 0;
  var wx = (window.Sky && Sky.weather) ? Sky.weather() : null;
  s.wet = wx ? (wx.rain || 0) * (1 - s.night * 0.5) : 0;
  /* the direction the light COMES FROM, in sheet space: the shadow runs
     away from it, so the light is the other way, lifted by its height */
  var t = Math.tan(el * Math.PI / 180);
  var m = Math.hypot(s.dx, s.dy, t) || 1;
  s.L = [-s.dx / m, -s.dy / m, t / m];
  /* ASKED FOR NOW, NOT CACHED FROM THE LAST PUBLISH. The camp moves under
     every pan and sky.js only publishes on a turn or the clock, so a point
     taken from Sky.screen() lags the sheet by a whole gesture. lampPoint()
     returns null unless the light IS the lantern. */
  s.point = (window.Sky && Sky.lampPoint) ? Sky.lampPoint() : null;
  return s;
}

/* ==== R42 (a): THE LANTERN, AS THE PAPER SEES IT ====================
   drawDesk puts the lamp on the oak as a point in CSS px a lamp's height
   over the top (uLampW), with the fallback bearing for the hours when the
   lantern is coming up and Sky has no point to give. The sheet and the
   card are drawn in SHEET units, so the same point is handed to them
   converted once, here, rather than worked out twice — one lamp, one
   frame, exactly as the sun is. The fourth number is the NIGHT, which is
   what makes the whole thing nothing by day. ==================== */
/* ==== R52: AND IT IS WORKED OUT ONCE A FRAME, NOT ONCE A PROGRAM.
   sunOf() asks Sky for the light, the wash, the weather and the lantern's
   point and builds an object, every call. Until R52 there were two callers
   a frame (engine.js's ground and its frame-1 sheet); R52 added four more
   in marks.js and one in lettering.js, and six sunOf()s a frame is real
   main-thread work for six identical answers — measured on the 780x1410
   canvas at night, where the frame is only about 1.5 ms to begin with: the
   six cost about 0.4 ms of it, and the memo gives it back (REPORT-room-R52).
   S.drawn is bumped at the END of engine.js draw(), so it is constant
   across one frame and changes on the next: it is exactly the token this
   wants. The memo is only for the callers that ask for the ROOM's own sun
   (no second argument); day.js hands in its own `s` and is untouched. ==== */
var _lampTok = -1, _lampVal = null;
function lampSheet(S, s) {
  var L = S.layout, memo = (s === undefined);
  if (memo && _lampTok === S.drawn && _lampVal) return _lampVal;
  s = s || sunOf();
  var lp = s.point || { x: (0.50 - s.dx * 0.22) * L.cw, y: (0.50 - s.dy * 0.22) * L.ch };
  /* THE POOL IS THE OAK'S OWN POOL. The wood's layer 3 above darkens on
     rad(p, c, vec2(cw*.74, ch*.74)) and holds full light inside a fifth of
     it; the paper is warmed on exactly that ellipse, so there is ONE pool
     across the desk and the paper does not have a second, differently
     shaped one of its own. It is measured in SHEET units, not on the
     glass, because a pool of light lies on the thing it falls on: pan the
     map and the warm stays where the lantern is standing. */
  var out = [(lp.x - L.ox) / L.k, (lp.y - L.oy) / L.k,
             L.cw * 0.74 / L.k, L.ch * 0.74 / L.k,
             /* LK1: the fifth number is how much the LAMP is the light: the
                night, and under half the dusk before it, so the paper dims
                and warms through the evening rather than at a stroke. Still
                exactly nought by day (dusk is nought above 12 degrees). */
             Math.min(1, (s.night || 0) + 0.45 * (s.dusk || 0))];
  if (memo) { _lampTok = S.drawn; _lampVal = out; }
  return out;
}
O.lampSheet = lampSheet;

/* THE SAME ARITHMETIC survey-map.js's Desk.one does for every node it
   holds: under a point light each thing on the desk works out its own
   direction from where it is lying relative to the lamp, short beside it
   and long away from it, held to twice the bearing-light's length. */
function sunAt(s, cx, cy) {
  if (!s.point) return [s.dx, s.dy, s.len];
  var vx = cx - s.point.x, vy = cy - s.point.y, m = Math.hypot(vx, vy);
  if (!(m > 1)) return [s.dx, s.dy, s.len];
  var reach = Math.max(240, (window.innerWidth || 1200) * 0.42);
  return [vx / m, vy / m, s.len * (0.45 + 1.35 * Math.min(1, m / reach))];
}
O.sun = sunOf;

/* ==== LD3 (2026-10-02): FRAME 1 DOES NOT WAIT FOR THE OAK ==============
   The loading investigation (notes/overnight-2026-10-02/loading.md, S3):
   on a cold load the oak's link is 0.75-0.88 s on Tommy's 3070 Ti, every
   other program frame 1 draws with is under 0.15 s, so the first picture
   waited on the grain alone. Now, where the driver links in parallel, the
   oak is linked beside a PLAIN desk (this same builder, `deskFS(true,
   true)`: the oak with oak() answering its mean, see WOOD_STUB) and
   only the plain one is in frame 1's set. Frame 1 draws the plain desk;
   the oak comes in OVER it, a constant-alpha cross-fade of HANDOFF_MS, and
   it does not start before the map's window has opened, so the grain
   arrives inside the map's own rise and not as a change on its own. A
   load where the oak is linked by the first picture (any warm cache)
   never draws the plain desk at all. Without KHR_parallel_shader_compile
   nothing changes: the oak alone, as before. ==== */
var HANDOFF_MS = 400;
/* `?oak=plain` keeps the grainless desk for good: the A of the pair that
   struck WOOD_MEAN */
var PLAIN_ONLY = Q.get('oak') === 'plain';
O.drawDesk = function (gl, S) {
  if (!O.pDesk) return false;
  var w = O.pWood, pl = O.pPlain;
  if (!pl || !w) return drawDeskP(gl, S, O.pDesk);
  var oak = !!(w.u || (O.mk.linked && O.mk.linked(w)));
  if (O.woodFrom == null && oak && !PLAIN_ONLY) {
    if (!O.plainDrawn) O.woodFrom = -1e9;              /* never showed the plain one: no fade */
    else if (S.boot && S.boot.open) O.woodFrom = performance.now();
  }
  var a = O.woodFrom == null ? 0 : Math.min(1, (performance.now() - O.woodFrom) / HANDOFF_MS);
  /* the stand-up's own blend owns the floor and the edge: off the desk
     pose the hand-over is a cut (only a load that boots into the room) */
  if (a > 0 && deskT(S) > 0) a = 1;
  if (a >= 1) { O.pPlain = null; O.woodMs = +(performance.now() - S.tBoot).toFixed(1); return drawDeskP(gl, S, w); }
  drawDeskP(gl, S, pl);
  O.plainDrawn = true;
  if (a > 0) {
    var e = a * a * (3 - 2 * a);
    gl.enable(gl.BLEND);
    gl.blendColor(0, 0, 0, e);
    gl.blendFunc(gl.CONSTANT_ALPHA, gl.ONE_MINUS_CONSTANT_ALPHA);
    drawDeskP(gl, S, w);
    gl.disable(gl.BLEND);
    gl.blendColor(0, 0, 0, 0);
    if (S.invalidate) S.invalidate();
  } else woodWatch(S);
  return true;
};
/* while the plain desk stands, a cheap look every 50 ms for the oak (and
   for the window), so the hand-over starts on time on a page that is not
   otherwise drawing */
function woodWatch(S) {
  if (O.woodWatching) return;
  O.woodWatching = setTimeout(function () {
    O.woodWatching = 0;
    if (!O.pPlain) return;
    var oak = !!(O.pWood.u || (O.mk.linked && O.mk.linked(O.pWood)));
    if (oak && S.boot && S.boot.open && S.invalidate) S.invalidate(); else woodWatch(S);
  }, 50);
}
function drawDeskP(gl, S, p) {
  var L = S.layout, f = S.fld, s = sunOf();
  gl.useProgram(p);
  gl.bindVertexArray(S.vaoQuad);
  gl.uniformMatrix4fv(p.u.uVP, false, S.VP);
  gl.uniform3f(p.u.uSheet, L.ox, L.oy, L.k);
  gl.uniform1f(p.u.uZ, -0.5);
  gl.uniform2f(p.u.uCanvas, L.cw, L.ch);
  gl.uniform4f(p.u.uSun, s.dx, s.dy, s.len, s.cast);
  gl.uniform4f(p.u.uSun2, s.soft, s.dark, s.night, s.wet);
  /* the pool of lamplight is round the LAMP; the wax is toward the bearing */
  if (s.point) gl.uniform2f(p.u.uLamp, s.point.x / L.cw, s.point.y / L.ch);
  else gl.uniform2f(p.u.uLamp, 0.50 - s.dx * 0.22, 0.50 - s.dy * 0.22);
  /* LT1: and the BANKER'S lamp, which is a different light from the one
     above - its own measured base rather than the lantern's point, and on
     at every hour. `?lamp=0` takes it away here as it does in place.js; a
     page where desk-lamp.js never landed has no lamp to paint and writes
     a nought on. */
  if (p.u.uLampD) {
    var lb = O.lampBase ? O.lampBase(S) : null;
    var lsrc = (O.lamp && O.lamp.source) || { power: 1.60, headZ: 330 };
    if (lb && !NO_LAMP) {
      gl.uniform4f(p.u.uLampD, lb.x, lb.y, lsrc.headZ * lb.mm, 1);
      var reach = Math.max(1, lsrc.power * 1000 * lb.mm);   /* metres -> css px */
      gl.uniform1f(p.u.uLampF, 3.2 / (reach * reach));
    } else {
      gl.uniform4f(p.u.uLampD, 0, 0, 1, 0);
      gl.uniform1f(p.u.uLampF, 1);
    }
  }
  /* X2: and when the sheet is folded, from what the sheet still covers */
  if (O.foldT > 0) {
    var sp2 = O.foldSpan();
    gl.uniform4f(p.u.uPaper, L.ox + sp2.x * L.k, L.oy + sp2.y * L.k, sp2.w * L.k, sp2.h * L.k);
  } else
  gl.uniform4f(p.u.uPaper, L.ox, L.oy, f.sheet.w * L.k, f.sheet.h * L.k);
  /* R58: the sheet as it would be if nothing were folded — the scale the
     pencil, the lamp's base, the boards and the pool of light are struck
     from, and the one rectangle on this desk that a fold does not move */
  if (p.u.uFull) gl.uniform4f(p.u.uFull, L.ox, L.oy, f.sheet.w * L.k, f.sheet.h * L.k);
  gl.uniform1f(p.u.uCurl, Math.min(64, f.sheet.w * 0.045) * L.k);
  var sp = sunAt(s, L.ox + f.sheet.w * L.k / 2, L.oy + f.sheet.h * L.k / 2);
  gl.uniform3f(p.u.uSunP, sp[0], sp[1], sp[2]);
  /* C0: and what is standing ON the oak, from RoomObjects.shadow. The two
     books used to be written here by name, with the sun struck at each
     one's own station; both come off the list now and the sun is struck
     at each entry's. */
  shUniforms(gl, p, deskCtx(gl, S, s), ctxSunAt);
  /* THE TABLE TOP, in css px. Wide enough that the flat sheet never sees
     an edge, and short enough that the reader standing in the room looks across it to the room
     rather than at a horizon of oak. */
  var cx = L.ox + f.sheet.w * L.k / 2, cy = L.oy + f.sheet.h * L.k / 2;
  /* R71 (a): the top's own rect, which is a DESK once the reader has left
     the desk pose and the canvas rule while they are still at it */
  var dT = deskT(S), dR = deskRect(S, dT);
  /* B1: the fit's own front edge used to be recomputed here as
     `(L.ch - cy)*O_NEAR` "only read where dT is 0". `deskRect` already
     carries it — its unmoved rect is `far + near` high from `cy - far` —
     so the band below takes `dR.y + dR.h` and there is one front edge in
     this function instead of two spellings of one. */
  gl.uniform4f(p.u.uEdge, dR.x, dR.y, dR.w, dR.h);
  gl.uniform1f(p.u.uEdged, 1);
  oakUniforms(gl, p, deskCtx(gl, S, s));
  glowUniforms(gl, p, CTX);
  gl.uniform1f(p.u.uDpr, S.dpr);
  gl.uniform1f(p.u.uSeed, SEED);
  /* THE SHEEN NEEDS THE REAL CAMERA AND THE REAL LIGHT. The eye is the one
     engine.js flies (S.camEye, world css px); the sun is sunOf's own
     direction with the sheet's y turned over; the lamp is a point a desk
     lamp's height over the top, where the pool of light already is. */
  var e = S.camEye || [L.cw / 2, -L.ch / 2, L.ch];
  gl.uniform3f(p.u.uEye, e[0], e[1], e[2]);
  gl.uniform3f(p.u.uLdir, s.L[0], -s.L[1], s.L[2]);
  var lp = s.point || { x: (0.50 - s.dx * 0.22) * L.cw, y: (0.50 - s.dy * 0.22) * L.ch };
  gl.uniform4f(p.u.uLampW, lp.x, -lp.y, Math.max(120, L.ch * 0.30), 1);
  var big = Math.max(f.sheet.w, f.sheet.h) * 3;
  gl.uniform4f(p.u.uRect, f.sheet.w / 2 - big, f.sheet.h / 2 - big, big * 2, big * 2);

  /* ==== R17 (c): THE FLOOR, THE FRONT EDGE, THE TOP — in that order ===
     Three draws of one quad under one program, painter's order, no depth
     and no second mesh: the floor is everywhere, the front edge is a band
     standing on end (uDrop), and the top paints over both and discards
     past uEdge. */
  /* R71 (b): AND THE FLOOR UNDER IT IS PLACE.JS'S ONCE THE READER STANDS
     UP. These two draws — a floor quad the size of the world and a band
     for the front edge — are painted with NO DEPTH TEST after the room's
     own pass, so at the room pose they covered the room's floor, the
     desk's apron and all four of its legs: the desk had no body in any
     picture and the plane under it was the desk's own oak, which is
     exactly the "wooden surface on another wooden surface" Tommy read.
     At the desk pose (`deskT` nought) not one byte of this changes. */
  /* ==== B1: AND IT HANDS OVER, IT DOES NOT CUT ========================
     R71's gate was `!(dT > 0)`: at `deskT` a ten-thousandth of one these
     two draws stopped dead, and what they handed to was the room at
     `uRoom = cam.room` — two hundredths of an alpha over the clear
     colour. Measured at the centre column, the bottom thirty rows of the
     frame went from luma 46.7 to 23.1 between `room` 0.020 and 0.021: a
     black band under the desk for the first tenth of a second of every
     stand-up, from a cut, not from the light.
     So the gate is now `dT < HANDOVER` and the two draws carry the rest
     of that quarter as their alpha. At `deskT` nought that is one,
     blending is not even enabled and the front page is the picture it
     was; by `room` 0.112 they are gone, well inside R71's ruling that
     they be off the frame the moment the top is the measured desk. In
     between the oak floor dissolves into the room's own floor, which is
     now AT THE SAME HEIGHT, so the two pictures of one plane cross-fade
     instead of swapping.
     A QUARTER, and not the whole ramp, because the two draws are not
     free: measured 2.98 ms a frame with them against 2.75 without, at
     `room` 0.299 and 0.301 — the same camera to two thousandths of the
     move — so they cost 0.23 ms while they are issued. The oak quad has
     left the frame long before the alpha runs out: at `room` 0.1 this
     build and the old one are the same picture to the byte, no pixel
     over a channel of 24 anywhere. So the fade is given the first
     quarter of `deskT`, twice the reach the pictures need and half the
     frames the whole ramp would have charged for.
     The front edge's row is `dR.y + dR.h` rather than `cy + near`: at
     `deskT` nought those are the same number to the bit (deskRect's `a`
     is `cy - far` high by `far + near`), and away from it the band
     follows the interpolating top instead of standing where the fit's
     edge used to be. ==== */
  if (EDGE && p.u.uFace && dT < HANDOVER) {
    var mm = f.sheet.w * L.k / 840;
    var slab = deskSlabMm() * mm, drop = (deskTopMm() - deskSlabMm()) * mm;
    gl.uniform2f(p.u.uSlab, slab, drop);
    var frontY = dR.y + dR.h;                     // the table's front edge, css px
    if (p.u.uFloorA) gl.uniform1f(p.u.uFloorA, 1 - dT / HANDOVER);
    if (dT > 0) { gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA); }
    /* the floor: the same big rect, a desk's height down */
    gl.uniform1i(p.u.uFace, 2);
    gl.uniform1f(p.u.uZ, -0.5 - slab - drop);
    gl.uniform1f(p.u.uDrop, 0);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    /* the front edge: zero height in sheet y, `slab` of drop in z */
    gl.uniform1i(p.u.uFace, 1);
    gl.uniform1f(p.u.uZ, -0.5);
    gl.uniform1f(p.u.uDrop, slab);
    gl.uniform4f(p.u.uRect, (dR.x - L.ox) / L.k, (frontY - L.oy) / L.k, dR.w / L.k, 0);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    if (dT > 0) gl.disable(gl.BLEND);
    /* and back to the top, which is never anything but opaque */
    gl.uniform1i(p.u.uFace, 0);
    gl.uniform1f(p.u.uDrop, 0);
    if (p.u.uFloorA) gl.uniform1f(p.u.uFloorA, 1);
    gl.uniform4f(p.u.uRect, f.sheet.w / 2 - big, f.sheet.h / 2 - big, big * 2, big * 2);
  }
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  return true;
}


/* =====================================================================
   C0 — THE DESK PASS: EVERY REGISTERED PIECE, IN ITS PLACE

   engine.js calls this where it used to call `drawBooks`, between the oak
   it stands on and the sheet that lies over it (engine.js draw, step 1c).
   The NAME is engine.js's and engine.js is not this card's file, so it
   keeps it; what it does is run the list. Registration order is the order
   the files are listed in base.njk, which is the order the draws were in
   before: the lamp's solid, the two books, the machine.

   A piece that throws is dropped after three frames and named with a `!`
   in `RoomObjects.figures()` — one thing on the desk is not the desk
   (the same rule B2 gave the room's furniture, place.js warmStep).
   ===================================================================== */
O.drawBooks = function (gl, S) {
  var s = sunOf(), ctx = deskCtx(gl, S, s);
  for (var i = 0; i < PIECES.length; i++) {
    var pc = PIECES[i];
    if (pc.dead) continue;
    try { pc.fn(gl, ctx); pc.drew++; }
    catch (e) { pc.err++; if (pc.err > 3) pc.dead = 1;
                if (pc.err === 1) console.warn('desk piece ' + pc.name, e); }
  }
};

/* THE SMOKE, in text, so a later card can prove a piece without taking a
   picture: every piece by name, whether it has been drawn, how many
   times, what it says of itself, and both lists as they last evaluated.
   `Room.state()` is the engine's; this is the desk's. */
O.figures = function () {
  return {
    pieces: PIECES.map(function (p) {
      var sm = null;
      if (p.hooks.smoke) { try { sm = p.hooks.smoke(); } catch (e) { sm = 'threw'; } }
      return { name: p.name + (p.err ? '!' : ''), drew: p.drew, err: p.err,
               dead: !!p.dead, smoke: sm };
    }),
    under: UNDER.map(function (u, i) {
      return { name: u.name, box: Array.prototype.slice.call(UND_BOX, i * 4, i * 4 + 4),
               rise: UND_RISE[i], err: u.err };
    }),
    shadow: SHADOW.map(function (h, i) {
      return { name: h.name, box: Array.prototype.slice.call(SH_BOX, i * 4, i * 4 + 4),
               cast: SH_K[i * 4], contact: SH_K[i * 4 + 1], lift: SH_K[i * 4 + 2],
               height: SH_K[i * 4 + 3], err: h.err };
    }),
    datum: UND_TOP[0], caps: [UNDER_CAP, SHADOW_CAP],
    unrolled: [UNDER_N, SHADOW_N],
    err: (window.__roomErr || []).length
  };
};

/* E8's room is drawn before the desk and leaves its own depth behind, and
   the ground's pass wants a clean buffer: so the objects clear it going in
   and clear it going out, and nothing outside these three draws can tell
   the depth test was ever on. */
/* X1: and whether the depth test is up, so the laptop — which is drawn
   between the books and the sheet and may find `?books=0` has left it
   nothing to stand on — never has to ask the driver (a gl.isEnabled is a
   round trip and a stall). */
function depthFore(gl) { gl.clear(gl.DEPTH_BUFFER_BIT); gl.enable(gl.DEPTH_TEST); gl.depthFunc(gl.LEQUAL); O._depth = 1; }
function depthBack(gl) { gl.disable(gl.DEPTH_TEST); gl.clear(gl.DEPTH_BUFFER_BIT); O._depth = 0; }

/* THE SHEET'S LIE, in sheet units: about two millimetres at this desk's
   scale (the card is 1.4 units for "about a millimetre", E6), a feather
   of 55 units — the side margins are only 79 wide — and a crease ridge of
   3.2. The three long creases land on the grid's own columns 56/112/168
   and the cross one on row 64, which is the only reason a 224 x 128 grid
   can carry a tent at all. */
var LIE = [3.1, 55.0, 3.2];
/* the crease's own SHADING height, which is what creaseNormal/creaseGrad
   strike the fold's normal from. It was a literal in two uniform writes;
   DR1 (3) scales both by the fold, so it has a name. */
var CREASE_H = 2.6;

/* ==== DR1 (3), 2026-09-21: THERE ARE NO CREASES UNTIL THERE IS A FOLD ==
   Tommy: "the fold lines from the sheet are also weird on the map, as
   soon as you pan they go odd. i don't mind dropping them for simplicity."

   He is describing a real thing and it is not only the pan. The sheet has
   carried three long crease TENTS and a cross one since E6 — a ridge of
   3.2 units of geometry, a normal struck off CREASE_H, and a scored line
   at .26 — on a sheet that, at rest, has been lying open on a desk. Paper
   that has been folded and opened out does hold its creases, but it holds
   them as a soft set in the fibre, not as four standing tents with a
   drawn line down each; and under the MAP, where the ridges are masked
   out but the LINE is not, what is left is four dark hairlines ruled
   across a picture with no shape under them at all. That is what goes odd
   on a pan: a line with no geometry has nothing to be lit by, so it does
   not move with the light the way everything else on this sheet does.

   So all three of them — the ridge, the normal and the line — ride the
   fold's own progress. Nothing at rest, full by the time the first crease
   has any angle worth the name, and they rise WITH the fold, which is the
   one moment a reader is looking for them. It is one number, taken here,
   and it multiplies the two uniforms every consumer already reads
   (uUnd.z, uFold.z) plus the line's own weight, which is normalised off
   uFold.z so there is no third uniform and no third rule. */
/* ==== CR2, 2026-09-21: AND EACH CREASE COMES WITH ITS OWN TURN =========
   Tommy: "I also think that we got rid of the folds on the paper, which I
   like, but as soon as you start folding the paper, the folds reappear."
   DR1 (3) gated all four creases on one number, the gesture's first three
   percent, so the moment the wheel moved every crease stood up at once,
   including the three the paper had not been bent along yet. A crease is
   made by the turn that bends it: the one at w/2 by the first turn, the
   two at w/4 and 3w/4 by the second (the east half is on the stack by
   then and bends with it), the one across by the third. So there are four
   gates, one a crease, each rising over the first seven degrees of its
   own turn (CREASE_ON_RAD) and falling with it on the way back, written
   as uCreaseOn to every program that carries the crease body (undDecl),
   and read by the ridge (creaseH), the normal (creaseGrad) and the line.
   uUnd.z and uFold.z are the plain constants again. */
var CREASE_ON_RAD = 0.12;
function creaseGate(a) {
  var t = a / CREASE_ON_RAD;
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return t * t * (3 - 2 * t);
}
function creaseGates() {
  var a = foldAnglesNow();                 /* CF3: the leaves own angles while the unfold runs */
  return [creaseGate(a[1]), creaseGate(a[0]), creaseGate(a[1]), creaseGate(a[2])];
}
function creaseUniform(gl, p) {
  if (!p.u.uCreaseOn) return;
  var g = creaseGates();
  gl.uniform4f(p.u.uCreaseOn, g[0], g[1], g[2], g[3]);
}

/* =================================================================
   R23: THE CORNER'S LIFT — the one number the whole thing is driven by

   `u` is 0 with the sheet at the fit and 1 with it anywhere else, eased
   over 300 ms each way; `h` is 0 with no hand near it and 1 under the
   pointer, eased over 140 ms — R14's pin lifted on .16s and this is the
   same beat. The two are added in the shader: the fold is the lift's, the
   height is the lift's plus the hand's two millimetres.

   THE EASE IS SMOOTHSTEP, NOT A RAMP. A corner peeling off a desk starts
   slowly (it is being let go of) and lands slowly (it is paper, not a
   spring), and a linear 300 ms of it reads as a hinge.

   AND A CLICK TAKES 620 ms TO LIE DOWN, because that is exactly how long
   `Room.home` takes to lay the sheet back out: the corner settles AS the
   sheet eases, not 300 ms into it and then waiting. Every other way home —
   the R key, a double-click on the desk, the index's own year — gets the
   ordinary 300, which is right: nothing was touching the corner.

   Nothing here schedules a frame of its own beyond the ease: `S.invalidate`
   is asked for one only while a number is still moving, so a sheet sitting
   at the fit or sitting off it costs nothing at all. */
/* THE CORNER, in the sheet's own units. The chamfer is where the crease
   is struck: 44 units is 31 mm at this desk's scale (E6's card is 1.4 units
   to the millimetre), so the strip that rolls is 62 units — 44 mm — of
   paper, which is the corner of a big sheet a hand has actually picked up
   by, not a tab. 1.95 radians is 112 degrees of roll: past the vertical,
   so the tip has turned over far enough to show its own back, and not so
   far that it lies down and reads as a printed dog-ear. `?ne=c,phi,rise`
   overrides all three for the contact sheet. */
/* S-B2: and the three numbers the peel is made of. `c` is its RADIUS at
   rest -- 76 units is 54 mm at this desk's scale, a hand's worth of paper
   and not a tab. `a0` is the height as a fraction of that radius: at 0.34
   the corner stands 26 units off the oak at rest, which is about what the
   old chamfered roll reached, so the sheet at rest has not changed its
   habit, only lost its crease. `b0` is how far the tip draws back in,
   also as a fraction of the radius: at 0.315 the surface arrives at the
   tip a hair short of vertical, so the paper shows the edge of its own
   back and not a flat printed underside. The three `rise` numbers are
   what the rise channel adds to each per unit -- the tug and the hand
   both come through there. `?ne=c,a0,b0` overrides for the contact
   sheet. */
var NE = { c: 76, a0: 0.34, b0: 0.315, riseR: 0.55, riseA: 0.075, riseB: 0.09,
           up: 300, down: 300, hover: 140, home: 620, gap: 360 };
(function () {
  var m = /[?&]ne=([\d.]+)(?:,([\d.]+))?(?:,([\d.]+))?(?:,([\d.]+))?/.exec(location.search);
  if (!m) return;
  NE.c = +m[1];
  if (m[2]) NE.a0 = +m[2];
  if (m[3]) NE.b0 = +m[3];
  if (m[4]) NE.riseR = +m[4];
})();
/* THE PEEL AS IT STANDS THIS FRAME, and the one place it is worked out:
   the uniform the shader draws from and the projected outline the
   invisible hand is sized by must not be able to disagree. Radius and
   height BOTH grow with the rise, so a tugged corner is not the same
   corner a little higher -- it is a wider piece of paper coming up, which
   is the whole difference between a dog-ear and a hand under the sheet. */
/* THE DRAW-IN GOES WITH THE LIFT. Tommy (2026-09-20, the corner nearly
   flat): "I can get it into this position where it's basically all
   flattened back down, and then the circle's missing. Then I move my
   mouse, and the paper goes whole into a proper pointy square corner,
   which is weird!" B was a base draw-in whatever the lift, so at a lift
   of nearly nought the tip still stood fifteen units in from the corner
   (the notch, with the oak in it), and at exactly nought the discard
   stopped and the flat sheet's square corner came back in one frame. The
   height already dies with u; the draw-in now does too, so the notch
   closes as the paper lies down and there is nothing to snap. */
function peel(u, h) {
  return { R: NE.c*(0.62 + 0.38*u)*(1.0 + NE.riseR*h),
           A: (NE.a0 + NE.riseA*h)*u,
           B: (NE.b0 + NE.riseB*h)*u };
}
/* ==== R45 (b): THE SOUTH-EAST CORNER'S ROLL =========================
   `phi` is the whole turn from the crease to the tip and `hold` is how
   much of the strip is still lying flat before the roll begins. The pair
   was picked off the pictures (room-rev6/R45-se-phi-*.png): under about
   three and a half radians the paper never turns far enough to show its
   own back, which is the one thing that says "curl" rather than "bent
   up", and the corner reads as a ramp standing over the journal; at four
   and a half it is a tight roll whose tip points back at the sheet and
   hangs over it, which is what a corner of a carried sheet does. HOLD is
   nought — the roll begins at the crease, exactly as R23's north-east
   corner does, and a flat hold before it put a plateau of paper out over
   the journal that read as a slab. */
var SE = { phi: 4.40, hold: 0.0 };
/* the tip's height over the paper, in sheet units of the chamfer — what
   the journal's own cover takes the roll's shadow from */
function seTip(curl) {
  if (!(SE.phi > 0)) return curl*0.10;
  var len = 2*curl*0.70711, R = len*(1 - SE.hold)/SE.phi;
  return R*(1 - Math.cos(SE.phi));
}
/* R47: AND IT IS THE DEFAULT NOW. Tommy, 2026-09-14: "fold is good". The
   fold is now the only path. Nothing about the fit changes with it —
   at rest uFoldOn is nought and the vertex path is the two comparisons it
   always was (the fit diff in REPORT-room-R47.md is 0). */
var FOLD_LEAF = 0.9;
/* ==== FD1: AND IT NEVER LEAVES WHAT IT IS LYING ON ==================
   R80 (2026-09-15) answered "a fold cannot keep the sheet draped over
   either support and also turn it as a flat leaf" by lifting the whole
   sheet to a plane 32 mm over the oak across the first four percent of
   the gesture. Tommy, 2026-09-20: "it's annoying that the map doesn't
   deform over the journal whilst the sheet itself does ... it's like
   there's a final unfolded state that a) gets rid of the fold in the map,
   b) adds the clip on ... and c) the paper locks onto the field guide +
   journal and doing this on the field guide makes the 'section along the
   line' draw a little bit differently".

   Both halves of that are the lift: the plane is a fourth surface the
   sheet visibly snaps on to and off, and everything struck into the
   furniture — the section over the field book among it — is drawn on a
   sheet that has stopped draping. So the lift is gone and R65 (a) is the
   whole answer: the fold is worked on flat paper and the support is added
   back at the station each point has LANDED at, which is a sheet that
   rides over the journal and the field book at every t and never has a
   state of its own to enter. */
O.foldOn = true;
/* R47: the fold's GLSL, for the other things printed on this paper —
   ruler.js compiles this exact string so there is one arithmetic and not
   two (`foldPt`, `foldUp`; the uniforms are uFoldC, uFoldA, uFoldOn). */
O.foldGLSL = FOLD_GLSL + FOLD_INST_GLSL;

/* ==== FD2 (a), 2026-09-20: THE MAP IS PRINT ON THIS PIECE OF PAPER ====
   Tommy: "the folds of the sheet no longer appear through the map, as
   well as the shape of the journal. they essentially appear the second
   you engage the folding process, even if you've not moved the sheet
   yet. and also the raised parts of the sheet suddenly get much higher
   as witnessed by the deformation around the field guide in the
   'section along the line'".

   WHY THE FIRST DEGREE LOOKED LIKE A DIFFERENT SHEET. The map — the
   ground, the sea, the marks, the names — is a 3-D pass drawn into the
   window in the paper, and it was drawn on the PLANE the paper lies on
   (engine.js sheetToClip, z = the map's own relief and nothing else).
   The paper is not that plane: it has been folded in eight, it undulates,
   and it is draped over two books. So at rest the window was a flat
   rectangle cut into a sheet that was anything but, and the moment there
   was a crease in the paper R58's print raster glued that same picture ON
   to the paper, where it took the paper's own shape and the paper's own
   light at once. Nothing had moved; the map had simply started being ink.

   So it is ink at rest as well. There are two halves and both are the
   sheet's own arithmetic, compiled once here and handed to every program
   that draws into the window:

     paperSurf(p)      where the paper's face IS at that station, which
                       is what sheetToClip now adds to every map point
     paperInk(col, p)  the light that falls on ink lying on that face:
                       the sheet's own lambert, its crease line, and the
                       lantern's temperature — the three terms the sheet's
                       fragment shader applies to its OWN print

   INSIDE THE NEAT RULE THE PAPER CARRIES ONLY WHAT IT IS LYING ON. The
   lie and the crease RIDGES are masked out under the map (pmask is
   exactly nought inside the neat rule, and the window is inside it by a
   margin), so the height there is `boardH` — the books — and the branch
   is taken the same way by every fragment of the window.

   uPapInk is nought on the ONE frame that strikes the print raster: the
   raster is the albedo, and the sheet lights it once when it samples it
   (R58's uUnlit, for the same reason). ==== */
O.paperInkGLSL = function () {
  return `
  uniform vec4 uPapRect;     // FD2: the sheet, in sheet units
  uniform vec4 uPapNeat;     // the neat rule — nothing lifts inside it
  uniform vec3 uPapUnd;      // the lie: amplitude, feather, crease ridge
  uniform vec3 uPapFold;     // curl, crease sigma, crease height
  uniform vec3 uPapLight;    // the sun as a direction in sheet space
  uniform float uPapPx;      // device px per sheet unit, for the crease's line
  uniform float uPapInk;     // 0 on the frame that strikes the print raster
` + undDecl() + BK_FIL + BOOK_LIFT() + BOOK_FALL() + PAPER_LIE + CREASE_GLSL_BODY + `
  /* the paper's face at a station, in sheet units */
  float paperSurf(vec2 p){
    /* LF1: BOTH ARMS ASK THE SAME THING OF THE BOOKS. This function is
       inlined into every stage that puts a point on the paper — twice in
       the lettering's vertex shader alone — and with the measurement
       inside boardH and inside paperH each of those inlines carried two
       passes over the entries, one per arm, of which exactly one is ever
       taken. Measured on Tommy's card, the whole fold takes the
       lettering's link from 1.43 s to 0.99 s and the family mark's from
       0.55 s to 0.32 s (medians of three, old and new alternating in one
       run; boardOf/paperOf are the same formulas, so the height is the
       height it was — nought differing pixels on the sheet, on a tilted
       deep link and in the room). */
    float bt, bl; bookAt(p, bt, bl);
    /* the window is inside the neat rule, where the lie and the crease
       ridges are masked to nothing: what is left is the two books */
    if (pmask(p, uPapNeat, uPapUnd.y) <= 0.0) return boardOf(bt, bl);
    return paperOf(p, uPapRect, uPapNeat, uPapUnd, uPapFold, bt, bl);
  }
  /* and the light on ink that is lying on it — the sheet's own three
     terms, in the sheet's own order (objects.js SHEET_FS). The sheen and
     the two hairlines off a board's arris are held to the lie's mask,
     which is nought under the map, so they are not here.
     THE LANTERN IS NOT HERE EITHER: every pass that draws into the window
     carries its own lampTemp already, and keeps it on the raster's frame
     too (LB1): the print carries the lantern and the sheet does not light
     what it reads off the print a second time. */
  vec3 paperInk(vec3 col, vec2 p){
    if (uPapInk <= 0.0) return col;
    vec2 gd; float sd;
    float fall = bkFall(p, gd, sd);
    vec2 gl2 = vec2(0.0);
    if (pmask(p, uPapNeat, uPapUnd.y) > 0.0) {
      float e = 6.0;
      gl2 = vec2(lieH(p + vec2(e,0.0), uPapRect, uPapNeat, uPapUnd) - lieH(p - vec2(e,0.0), uPapRect, uPapNeat, uPapUnd),
                 lieH(p + vec2(0.0,e), uPapRect, uPapNeat, uPapUnd) - lieH(p - vec2(0.0,e), uPapRect, uPapNeat, uPapUnd))/(2.0*e);
    }
    vec2 g = creaseGrad(p, uPapRect, uPapFold.y, uPapFold.z)
           + gl2*(1.0 - fall*${(1 - LIE_BOARD).toFixed(2)}) + gd*(uOverZ.w*6.0*fall*(1.0 - fall));
    /* DS1 (2026-10-02): the fall is LIT as a rounded drape. gd is the
       straight ramp's slope, the same at every station of the run, so a
       slope turned from the sun was one flat grey with a hard edge at the
       arris and at the foot: "a grey rectangle" in the map over the
       journal's north edge, which rode the land when the map was tilted.
       6f(1-f) is the slope of a smoothstep over the same run (it averages
       to one, so the drop is the same drop): nought at both ends, most in
       the middle, and the shade fades in and out instead of standing as a
       box. The sheet's own shader (SHEET_FS) takes the same term. */
    vec3 nrm = normalize(vec3(-g.x, -g.y, 1.0));
    float lam = clamp(dot(nrm, uPapLight), 0.0, 1.0);
    col *= 1.0 + 0.80*(lam - uPapLight.z);
    /* DR1 (3): nought until the paper is being folded, as on the sheet;
       CR2: each crease with its own turn (uCreaseOn), as on the sheet */
    float ln = uCreaseOn.w*clamp(0.8 - abs(p.y - (uPapRect.y + uPapRect.w*0.5))*uPapPx, 0.0, 0.6);
    for (int i = 0; i < 3 + uZero; i++)
      ln = max(ln, uCreaseOn[i]*clamp(0.8 - abs(p.x - (uPapRect.x + uPapRect.z*(0.25 + 0.25*float(i))))*uPapPx, 0.0, 0.6));
    return mix(col, vec3(0.553,0.447,0.282), 0.26*ln);
  }
`;
};
/* FD2 (a): and the uniforms behind it, for any program that compiles the
   chunk above. The books come off the same UNDER list the sheet's own
   two programs are written from, so there is one set of numbers. */
O.paperInkUniforms = function (gl, p, S) {
  if (!p || !p.u || !p.u.uPapRect) return;
  var f = S.fld, L = S.layout;
  if (!f || !L) return;
  var nr = neatRect();
  gl.uniform4f(p.u.uPapRect, 0, 0, f.sheet.w, f.sheet.h);
  gl.uniform4f(p.u.uPapNeat, nr[0], nr[1], nr[2], nr[3]);
  gl.uniform3f(p.u.uPapUnd, LIE[0], LIE[1], LIE[2]);
  gl.uniform3f(p.u.uPapFold, Math.min(64, f.sheet.w * 0.045), 7.0, CREASE_H);
  creaseUniform(gl, p);                                     /* CR2 */
  var s = sunOf(S);
  gl.uniform3f(p.u.uPapLight, s.L[0], s.L[1], s.L[2]);
  if (p.u.uPapPx) gl.uniform1f(p.u.uPapPx, L.k / (S.frameK || 1) * S.dpr);   /* MP2: device px on the glass */
  if (p.u.uPapInk) gl.uniform1f(p.u.uPapInk, S.capPrint ? 0 : 1);
  undUniforms(gl, p, CTX);
};
O.foldT = 0;
O.foldGrab = 0;      /* a hand is on the corner right now */
/* the three turns, from one progress. Each fold takes a third of the
   gesture and eases in and out of it, so a hand that stops halfway leaves
   a flap standing at the angle it stopped at. */
function foldTurns(t) {
  var a = [0, 0, 0];
  for (var k = 0; k < 3; k++) {
    var u = Math.min(1, Math.max(0, (t - k / 3) * 3));
    a[k] = Math.PI * u * u * (3 - 2 * u);
  }
  return a;
}
/* ==== CF3 (2026-09-23): THE UNFOLD IS THE LEAVES' OWN =================
   Tommy, of R58's unfold: "It's particularly uncanny when you unfold
   because your sheet basically hits the journal, and then the card slides
   along. It's the inverse of the way down, but it's not even the right
   time." Three equal thirds of one progress, each crease through the same
   smoothstep, is a film of the fold run backwards. A map being opened is
   three leaves, each LIFTED by a hand to the vertical and then let FALL.

   So while `Room.unfold` runs, the three angles are not `foldTurns(t)`:
   they are the leaves' own, set here by `O.foldAngles` from the plan
   below, and `O.foldT` is kept as the overall progress they amount to
   (each leaf still in the air counts the third of the gesture its angle
   is worth through `foldTurns`' own smoothstep, and a leaf that has
   landed counts nothing, whatever its settle is doing), because day.js,
   desk-books.js, pin.js and the engine's gates read it as that.
   `O.foldGeom().a` returns the actual angles either way. `O.fold(t)`,
   the fold's own path, clears the override: the corner's drag and
   `foldTo(1, …)` are foldTurns exactly as they were.

   THE PLAN, per leaf, in the order the leaves come off the packet (the
   one across at cy, then c2, then c1):
     · TIME IS THE SQUARE ROOT OF THE LEAF'S SPAN (a pendulum's period
       goes as the root of its length, and a leaf twice as long is not
       twice as slow): UNF_K ms a root unit, so c1's leaf (1182) takes
       1463 ms and the two short ones (591, 608) 1034 and 1049.
     · UNF_LIFT of it is the hand's lift, lying to vertical, a cubic
       Hermite that starts at rest and passes the vertical still moving
       (the speed the fall starts at), so the hand throws the leaf over
       rather than stopping it on its edge.
     · the rest is the FALL: accelerating (gravity's torque grows as the
       leaf leans) to a peak, then the AIR CUSHION: a falling sheet traps
       air under it and floats the last centimetre, so the speed falls to
       a tenth of the peak by the landing and the last 6 degrees of the
       long leaf take ~180 ms. The fall is a table of the angle against
       time built once from that speed profile (UNF_FALL), scaled to each
       leaf's own duration.
     · then ONE settle: the free edge flexes up UNF_SETTLE_A (1.5
       degrees) and back over UNF_SETTLE_MS (120 ms). Only a leaf that
       fell from 20 degrees or more has one.
     · NO DEAD STOPS: the next leaf's lift starts the moment the previous
       one passes the vertical and is falling on its own.
     · and O.foldSettle, which holds the paper taut (objects.js foldFlat,
       the corner's press), is one until the last leaf has landed and
       eases to nought over UNF_LIE_MS (450 ms) after it.
   ==================================================================== */
O.foldOver = null;      /* CF3: the three angles while the unfold runs, else null */
O.foldSettle = 0;       /* CF3: the paper held taut, 1..0, after the last landing */
function foldAnglesNow() {
  return O.foldOver ? O.foldOver.slice() : foldTurns(O.foldT || 0);
}
/* the inverse of foldTurns' smoothstep: the progress one leaf's angle is worth */
function turnsInv(a) {
  var s = Math.min(1, Math.max(0, a / Math.PI));
  return 0.5 - Math.sin(Math.asin(1 - 2 * s) / 3);
}
O.foldAngles = function (a, landed, settle) {
  if (!a) { O.foldOver = null; O.foldSettle = 0; if (O.S && O.S.invalidate) O.S.invalidate(); return; }
  O.foldOver = [a[0], a[1], a[2]];
  var t = 0;
  for (var k = 0; k < 3; k++) if (!(landed && landed[k])) t += turnsInv(a[k]) / 3;
  O.foldT = t < 1e-9 ? 0 : Math.min(1, t);
  O.foldSettle = settle || 0;
  if (O.S && O.S.invalidate) O.S.invalidate();
};
var UNF_K = 42.55;             /* ms per root sheet unit: the whole run in eight lands at 2.40 s */
var UNF_LIFT = 0.45;           /* the lift's share of a leaf's time; the fall has the rest */
var UNF_SETTLE_A = 1.5 * Math.PI / 180, UNF_SETTLE_MS = 120;
var UNF_LIE_MS = 450;
/* the fall's speed against its own time tau, 0..1, as a share of the
   peak: 0.3 at the vertical (the throw), up as tau squared to the peak at
   0.52, then down as (1 - tau)^3.5 to 0.1 at the landing (the cushion) */
var UNF_FALL = (function () {
  var WS = 0.3, WL = 0.1, TC = 0.52, KC = 3.5, N = 512;
  function om(t) { return t < TC ? WS + (1 - WS) * (t / TC) * (t / TC)
                                 : WL + (1 - WL) * Math.pow((1 - t) / (1 - TC), KC); }
  var A = new Float64Array(N + 1), area = 0;
  for (var i = 1; i <= N; i++) { area += (om((i - 1) / N) + om(i / N)) / (2 * N); A[i] = area; }
  var sc = (Math.PI / 2) / area;
  for (var j = 0; j <= N; j++) A[j] *= sc;
  return { A: A, N: N, w0: WS * sc,          /* the speed at the vertical, radians a unit tau */
           at: function (tau) {               /* the angle fallen from the vertical */
             var x = Math.min(1, Math.max(0, tau)) * N, i0 = Math.floor(x), i1 = Math.min(N, i0 + 1);
             return A[i0] + (A[i1] - A[i0]) * (x - i0); },
           tauOf: function (fallen) {         /* and back: the tau that has fallen this far */
             var lo = 0, hi = N;
             if (fallen <= 0) return 0;
             if (fallen >= A[N]) return 1;
             while (hi - lo > 1) { var m = (lo + hi) >> 1; if (A[m] < fallen) lo = m; else hi = m; }
             return (lo + (fallen - A[lo]) / (A[hi] - A[lo])) / N; } };
})();
O.unfoldPlan = function (from) {
  var g = O.foldGeom(), a = from || g.a, PI = Math.PI;
  var span = [g.w - g.c1, g.c1 - g.c2, g.cy];
  var order = [2, 1, 0], leaves = [], t = 0, last = 0;
  for (var i = 0; i < 3; i++) {
    var k = order[i], as = a[k];
    if (!(as > 1e-5)) continue;
    var D = UNF_K * Math.sqrt(span[k]), L = D * UNF_LIFT, F = D - L;
    var lf = { k: k, as: as, t0: t, L: 0, F: F, tau0: 0, bounce: as >= 20 * PI / 180 };
    if (as > PI / 2) {
      lf.L = L * (as - PI / 2) / (PI / 2);
      /* the Hermite's end slope, in its own units, that is the fall's
         starting speed: one number whatever the leaf's size */
      lf.m = lf.L > 0 ? (UNF_FALL.w0 / F) * lf.L / (as - PI / 2) : 0;
    } else {
      lf.tau0 = UNF_FALL.tauOf(PI / 2 - as);
    }
    lf.tv = t + lf.L;                                   /* passes the vertical */
    lf.tl = lf.tv + F * (1 - lf.tau0);                  /* lands */
    leaves.push(lf);
    t = lf.tv;                                          /* the next lift begins here */
    last = Math.max(last, lf.tl);
  }
  var setEnd = 0;
  leaves.forEach(function (lf) { setEnd = Math.max(setEnd, lf.tl + (lf.bounce ? UNF_SETTLE_MS : 0)); });
  return { leaves: leaves, land: last, end: Math.max(setEnd, last + UNF_LIE_MS),
           from: [0, 1, 2].map(function (k) { return a[k] > 1e-5 ? a[k] : 0; }) };
};
O.unfoldAt = function (plan, ms) {
  var a = plan.from.slice(), landed = [false, false, false], PI = Math.PI;
  for (var i = 0; i < plan.leaves.length; i++) {
    var lf = plan.leaves[i], k = lf.k, th;
    if (ms <= lf.t0) th = lf.as;
    else if (ms < lf.tv) {
      var u = (ms - lf.t0) / lf.L, m = lf.m;
      th = lf.as - (lf.as - PI / 2) * (3 * u * u - 2 * u * u * u + m * (u * u * u - u * u));
    } else if (ms < lf.tl) {
      th = PI / 2 - UNF_FALL.at(lf.tau0 + (ms - lf.tv) / lf.F);
    } else {
      landed[k] = true;
      var s = (ms - lf.tl) / UNF_SETTLE_MS;
      th = (lf.bounce && s < 1) ? UNF_SETTLE_A * Math.sin(PI * s) : 0;
    }
    a[k] = th;
  }
  var settle = ms < plan.land ? 1 : 1 - (function (x) { x = Math.min(1, Math.max(0, x)); return x * x * (3 - 2 * x); })((ms - plan.land) / UNF_LIE_MS);
  return { a: a, landed: landed, settle: settle, done: ms >= plan.end };
};
/* WHERE THE CREASES ARE, in the sheet's own units, and what the fold has
   reached — engine.js cuts the window's ground to the paper that is still
   flat with this, and day.js stands the note clear of the packet. */
O.foldGeom = function () {
  var sb = SHEET_BOX();
  return { on: true, t: O.foldT, a: foldAnglesNow(),
           c1: sb.w * 0.5, c2: sb.w * 0.25, cy: (O.foldCy === undefined ? 0.5 : O.foldCy) * sb.h,
           leaf: FOLD_LEAF, w: sb.w, h: sb.h };
};
/* ==== X2: WHAT THE SHEET STILL COVERS, in its own units ==============
   A fold takes the paper in from the east and then from the north, and
   two things on the desk have to know it: the OAK, whose shadow of the
   sheet is struck from this rectangle (drawDesk's uPaper — a map folded
   away that goes on throwing a full-sheet shadow is the one thing in the
   picture that says none of this is real), and the WINDOW's scissor in
   engine.js, which cuts the map to the paper that is still lying flat.

   The east edge is whichever is further east: the crease the fold is
   turning about, or the FREE EDGE of the flap that is turning over it —
   which is the crease plus the flap's own length foreshortened by the
   turn, and which crosses back west of the crease once the flap is past
   vertical and comes down on the paper. That is a cosine, and it is the
   same cosine the vertex shader is folding with. ==== */
O.foldSpan = function () {
  var g = O.foldGeom(), a = g.a;
  var E = g.w, N = 0;
  if (a[0] > 0) E = Math.max(g.c1, g.c1 + (E - g.c1) * Math.cos(a[0]));
  if (a[1] > 0) E = Math.max(g.c2, g.c2 + (E - g.c2) * Math.cos(a[1]));
  if (a[2] > 0) N = Math.min(g.cy, g.cy * (1 - Math.cos(a[2])));
  return { x: 0, y: N, w: E, h: g.h - N, g: g };
};
/* ==== X2: AND WHAT IS STILL UNCOVERED, which is NOT the same rectangle.
   `foldSpan` is how far the PAPER reaches — the flap in the air counts,
   because its shadow does. This is how far the paper still LIES OVER the
   desk and the map: a flap past vertical has stopped covering the ground
   it came from and started covering the ground it is falling on to, so
   here the two terms are taken the other way round. engine.js cuts the
   window with this one, and the note under the map is drawn only east of
   it. ==== */
O.foldOpen = function () {
  var g = O.foldGeom(), a = g.a;
  var E = g.w, N = 0;
  if (a[0] > 0) E = Math.min(E, g.c1, g.c1 + (g.w - g.c1) * Math.cos(a[0]));
  if (a[1] > 0) E = Math.min(E, g.c2, g.c2 + (g.c1 - g.c2) * Math.cos(a[1]));
  if (a[2] > 0) N = Math.max(N, g.cy, g.cy * (1 - Math.cos(a[2])));
  return { x: 0, y: N, w: Math.max(0, E), h: Math.max(0, g.h - N), g: g };
};
/* ==== R47: WHAT IS ON TOP OF THE PILE, in sheet units ================
   A folded map is eight leaves and seven of them are underneath. The
   sheet's own print rides its own texture and is sorted for nothing by
   the mesh's order (west to east, and the east panels are drawn last
   because they end up on top); the RULER is instanced, in layout order,
   with no depth buffer to appeal to, so it has to be told. This is the
   highest leaf that has LANDED — a panel still in the air is over
   everything by construction and answers for itself — and ruler.js
   refuses any graduation lying below it.

   The eight panels are walked through the same three gates the shader
   takes, which is why this is here beside them and not in ruler.js: the
   gate is the position as it stands, and a panel whose turn is not
   finished is not in the pile yet.
   ==================================================================== */
/* TODO (R58): THIS IS NOW DEAD, AND IT WAS WRONG. engine.js no longer
   draws the ruler while there is a fold in the sheet (the graduations are
   in the print raster with everything else on that paper), so ruler.js
   only ever asks this at foldT 0, where it is nought. It is left standing
   because ruler.js's two fold passes are left standing; when those go,
   this and FOLD_INST_GLSL's `foldPile` go with them. Note for whoever
   does that: the arithmetic here is NOT the sheet's. It adds 1, 3 and 7
   leaves as the gates pass, but the shader's z is MIRRORED by every half
   turn (foldX takes q.z to -q.z at pi), so the true pile at a quarter is
   0, 3, 2, 1 west to east and this says 0, 3, 4, 1. A flip reverses the
   order of the leaves under it; adding a constant does not. */
O.foldTop = function () {
  var g = O.foldGeom(), a = g.a, L = g.leaf, PI = Math.PI, top = 0;
  for (var i = 0; i < 8; i++) {
    var cx = (i & 3) * g.w * 0.25 + g.w * 0.125;   /* the quarter's middle */
    var north = (i & 4) !== 0, lift = 0, landed = true;
    if (cx > g.c1) {
      if (a[0] < PI - 1e-4) { landed = false; }
      lift += L * 1; cx = g.c1 + (cx - g.c1) * Math.cos(a[0]);
    }
    if (landed && cx > g.c2) {
      if (a[1] < PI - 1e-4) landed = false;
      lift += L * 3;
    }
    if (landed && north) {
      if (a[2] < PI - 1e-4) landed = false;
      lift += L * 7;
    }
    if (landed && lift > top) top = lift;
  }
  return top;
};
O.fold = function (t) {
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  /* CF3: the fold's own path is foldTurns', so any unfold override goes */
  var over = !!O.foldOver || O.foldSettle > 0;
  O.foldOver = null; O.foldSettle = 0;
  if (t === O.foldT && !over) return O.foldT;
  O.foldT = t;
  if (O.S && O.S.invalidate) O.S.invalidate();
  return O.foldT;
};
var ne = { u: 0, h: 0, from: 0, to: 0, t0: 0, ms: 300, hFrom: 0, hTo: 0, h0: 0,
           lock: 0, hov: 0, tug: 0, tugT: 0 };
function ease(x) { return x*x*(3.0 - 2.0*x); }
function neAt(now) {
  var a = ne.ms > 0 ? (now - ne.t0)/ne.ms : 1;
  ne.u = a >= 1 ? ne.to : ne.from + (ne.to - ne.from)*ease(a < 0 ? 0 : a);
  if (a >= 1) ne.lock = 0;
  var b = (now - ne.h0)/NE.hover;
  ne.h = b >= 1 ? ne.hTo : ne.hFrom + (ne.hTo - ne.hFrom)*ease(b < 0 ? 0 : b);
  return (a < 1) || (b < 1);
}
function neWant(to, ms) {
  if (ne.to === to) return;
  ne.from = ne.u; ne.to = to; ne.ms = ms; ne.t0 = (window.performance || Date).now();
}
/* ==== B2: THE RISE CHANNEL HAS TWO CLAIMANTS ========================
   `ne.h` was the hand's rise and nothing else. It is now shared with the
   TUG: Tommy, 2026-09-19, "it's maybe a little bit hard to discover that
   you can fold up the map… Let's not make it too discoverable… zooming
   out further, tugging the corner up slightly". A wheel rolled OUT
   against the widest view's stop (engine.js's wheel handler, where the
   clamp gives the width back unaltered) asks for `cornerTug(true)`, and
   the corner comes up a few millimetres past the curl it already has off
   the fit and stays there for as long as the view is at that stop. The
   hand on it still wins outright — hover is the whole rise, the tug is
   three quarters of it — so taking hold of a tugged corner still moves.
   ================================================================== */
/* S-B2: AND THE TUG HAS TWO STAGES. Tommy, review of 2026-09-19: "maybe
   there's a second level of zoom, so it pops out a little bit, and as you
   zoom further, it pulls it even further." Stage one is a corner lifted;
   stage two is a corner a hand is about to take. The hand still wins
   outright at either -- HOVER is added ON TOP of whatever stage the tug
   has reached, so taking hold of a corner always moves it, which is the
   tell that says it can be taken hold of. */
var TUG = [0, 0.55, 1.15], HOVER = 0.50;
function hWant(to) {
  if (ne.hTo === to) return;
  ne.hFrom = ne.h; ne.hTo = to; ne.h0 = (window.performance || Date).now();
  if (O.S && O.S.invalidate) O.S.invalidate();
}
function hRest() { var t = TUG[ne.tug || 0] || 0; return ne.hov ? t + HOVER : t; }
/* the hand, and the fold the hand is being shown the way to */
O.cornerHover = function (on) { ne.hov = on ? 1 : 0; hWant(hRest()); };
O.cornerTug = function (on) {
  var cur = ne.tug || 0, to = on ? Math.min(cur + 1, 2) : Math.max(cur - 1, 0);
  if (to === cur) return;
  /* A trackpad sends a wheel a frame and a STAGE is a decision, so one is
     taken at a time and the rest of the flick is spent on nothing. */
  var now = (window.performance || Date).now();
  if (now - (ne.tugT || 0) < NE.gap) return;
  ne.tugT = now; ne.tug = to; hWant(hRest());
};
O.cornerTugged = function () { return !!ne.tug; };
O.cornerSettle = function () {          /* the sheet is going home: lie down with it */
  ne.hov = 0; ne.tug = 0;
  ne.hFrom = ne.h; ne.hTo = 0; ne.h0 = (window.performance || Date).now();
  neWant(0, NE.home);
  /* AND IT IS NOT ASKED AGAIN UNTIL IT IS DOWN. `atFit` does not answer
     yes until the move's last frame, so without this the next frame would
     see the sheet still off the fit and start the corner back up. The lock
     lifts the moment the ease lands — a hand that grabs the sheet during
     the settle gets its corner back as soon as the corner is flat. */
  ne.lock = 1;
};
/* WHERE THE HAND STARTS, in sheet units. The flat square remains the fold
   grip and the coarse-pointer allowance. The lifted corner does NOT stay
   inside its flat projection: perspective can put the rolled tip well east
   of the sheet edge, so `cornerHit` below adds the curl's projected mesh to
   this square instead of pretending the flat plane is still its outline. */
O.cornerBox = function (grow) {
  var sb = (window.RoomField && RoomField.sheetBox) ? RoomField.sheetBox() : { w: 2364, h: 1216 };
  var side = NE.c*1.25*(grow || 1);
  return { x: sb.w - side, y: 0, s: side, off: false };
};
/* THE CURL'S GLASS OUTLINE. This is the vertex shader's `neAt` and corner
   fan, sampled along both cut edges and the middle. It returns one tight
   screen-space box around the old flat grip AND the paper actually drawn;
   pin.js uses it only after `onPaperQuad` has established that this panel
   is uncovered. Forty-three point projections per laid frame are cheaper
   than one DOM read and keep the invisible hand on the visible paper at
   every pan, zoom, tilt and bearing. */
O.cornerHit = function (S, grow) {
  if (!S || !S.layout || !window.Room || !Room.paperPoint || !Room.scenePoint) return null;
  var sb = (window.RoomField && RoomField.sheetBox) ? RoomField.sheetBox() : { w: 2364, h: 1216 };
  var b = O.cornerBox(grow), pts = [], L = S.layout;
  function addPaper(x, y) {
    var p = Room.paperPoint(x, y); if (p) pts.push(p);
  }
  addPaper(b.x, b.y); addPaper(b.x + b.s, b.y);
  addPaper(b.x + b.s, b.y + b.s); addPaper(b.x, b.y + b.s);
  /* The torn tip is drawn past the mathematical sheet rectangle even when
     the map is home. Continue the grip's eastward screen vector by the same
     1.25 chamfer allowance used to size it, so both push-home and pull-fold
     begin on the paper the eye can actually see. */
  addPaper(b.x + b.s*2.25, b.y);
  addPaper(b.x + b.s*2.25, b.y + b.s);
  if (ne.u > 0 || ne.h > 0) {
    /* S-B2: the same profile the shader draws, walked in to the point at
       five bearings -- fifty-five projections a laid frame, still cheaper
       than one DOM read, and the hand stays on the paper the eye sees at
       every pan, zoom, tilt and bearing. */
    var pk = peel(ne.u, ne.h);
    for (var i = 0; i <= 10; i++) {
      var t = i/10, w = t*t*t;
      var rho = pk.R*(1 - t) + pk.R*pk.B*w, rise = pk.R*pk.A*w;
      for (var j = 0; j <= 4; j++) {
        var th = j/4*Math.PI*0.5;
        var x = sb.w - rho*Math.cos(th), y = rho*Math.sin(th);
        var z = rise + (O.paperHeight ? O.paperHeight(S, x, y) : 0);
        var p = Room.scenePoint(L.ox + x*L.k, -(L.oy + y*L.k), z*L.k);
        if (p) pts.push(p);
      }
    }
  }
  if (!pts.length) return null;
  var x0 = pts[0][0], x1 = x0, y0 = pts[0][1], y1 = y0;
  for (var k = 1; k < pts.length; k++) {
    x0 = Math.min(x0, pts[k][0]); x1 = Math.max(x1, pts[k][0]);
    y0 = Math.min(y0, pts[k][1]); y1 = Math.max(y1, pts[k][1]);
  }
  return { x: x0, y: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) };
};
O.corner = function () {
  var pk = peel(ne.u, ne.h);
  return { lift: +ne.u.toFixed(4), hover: +ne.h.toFixed(4), c: NE.c,
           stage: ne.tug || 0, r: +pk.R.toFixed(2),
           rise: +(pk.R*pk.A).toFixed(2), drawIn: +(pk.R*pk.B).toFixed(2),
           tris: O.cornerN/3 };
};

/* ==== R43: THE PAPER'S SURFACE HEIGHT, IN JS =========================
   REPORT-room-X3's OPEN: everything laid on the paper by `Room.paperPoint`
   sat off the print under it — measured on the 780 x 1410 canvas, the year
   index's rule by 5.9 device px and the six marks' row by 8.2 — because
   the projection put the element on z = 0 while the print is on the
   paper's own TOP FACE. The sheet's margins are not at z = 0: PAPER_LIE
   lifts them (the lie, and the rim curl that makes the cut edge visible
   all the way round), BOOK_LIFT drapes them over the two books, and the
   creases ride on top of both. Inside the neat rule every one of those
   terms is held at nought, which is why the map itself never showed it and
   a steep pose in the margin did.

   So this is `paperH` — the vertex shader's own function, a few hundred
   lines above — written out in JS, reading the uniforms off the same
   expressions `drawSheet` writes them from, so there is one arithmetic and
   not two. It is EXACT where the sheet is flat: `pmask` is nought inside
   the neat rule and so is every term, to the last decimal.

   Two things it deliberately does not carry.

     · THE FOLD (X2). `foldX`/`foldY` are a rigid turn of whole panels, not
       a height over (x, y) — and the DOM this projection lays is hidden
       the moment the sheet is in eight (styles.css :1137), because DOM
       cannot fold. engine.js's own `drawNote` asks `paperPoint` for
       points on the FLAT plane (`foldSpan`'s east edge is already the
       cosine of the turn), so turning them here would move the note. At rest the fold adds nothing anyway, exactly:
       uFoldC.w * leaves * (1 - cos 0) * 0.5 = 0, so the leaf's thickness
       is in the answer whenever the answer is asked for.

     · THE TURNED CORNER (R7's dog-ear, R23's curl). Both are strips on
       their own meshes with their own parameter; nothing is laid on
       either, and the corner's hotspot is a box on the plane under it.
   ==================================================================== */
function phStep(e1, x) {                    /* smoothstep(0.0, e1, x) */
  var t = x / e1;
  return t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t);
}
function phMask(x, y, nr, fe) {             /* pmask */
  var dx = Math.max(nr[0] - x, x - (nr[0] + nr[2])),
      dy = Math.max(nr[1] - y, y - (nr[1] + nr[3]));
  return phStep(fe, dx > dy ? dx : dy);
}
function phWave(ux, uy) {                   /* pwave */
  return Math.sin(6.28318 * (ux * 1.30 + 0.21)) * Math.cos(6.28318 * (uy * 0.78 - 0.13))
       + 0.42 * Math.sin(6.28318 * (ux * 2.45 - 0.37) + 4.1 * uy)
       + 0.30 * Math.cos(6.28318 * (uy * 1.90 + 0.08) + 1.7 * ux);
}
function phLie(x, y, r, m) {                /* lieH, given pmask */
  var dx = Math.min(x - r[0], r[0] + r[2] - x),
      dy = Math.min(y - r[1], r[1] + r[3] - y);
  var rim = 1 - phStep(70.0, dx < dy ? dx : dy);
  return m * LIE[0] * (phWave((x - r[0]) / r[2], (y - r[1]) / r[3]) + 0.52 * rim);
}
function phCrease(x, y, r, sg, g) {         /* creaseH; g = creaseGates() (CR2) */
  var t = 0, d;
  for (var i = 0; i < 3; i++) {
    d = (x - (r[0] + r[2] * (0.25 + 0.25 * i))) / sg;
    t += g[i] * Math.exp(-d * d);
  }
  d = (y - (r[1] + r[3] * 0.5)) / sg;
  return t + g[3] * Math.exp(-d * d);
}
function phQ(x, e) {                        /* R45: bkQ, to the letter */
  var w = e > 1e-5 ? e : 1e-5, u = 1 - Math.abs(x) / w;
  if (u < 0) u = 0;
  return (x > 0 ? x : 0) + 0.25 * w * u * u;
}
function phFil(t, ei, eo) {                 /* R45: bkFil, to the letter */
  return 1 - phQ(1 - phQ(t, eo), ei);
}
function phBookM(x, y, b, run, fi, fo) {    /* bookM */
  if (b[2] <= 0) return 0;
  var dx = Math.abs(x - (b[0] + b[2] * 0.5)) - b[2] * 0.5,
      dy = Math.abs(y - (b[1] + b[3] * 0.5)) - b[3] * 0.5;
  var qx = dx > 0 ? dx : 0, qy = dy > 0 ? dy : 0;
  var sd = Math.sqrt(qx * qx + qy * qy) + Math.min(Math.max(dx, dy), 0);
  return phFil(1 - (sd - DRAPE_HOLD) / run, fi / run, fo / run);
}
/* DR1 concept C's `phCardZ` — the JS mirror of `cardZ` — stood here.
   DR2, 2026-09-21: deleted with the concept. */
/* the frame's uniforms, kept until one of the things drawSheet builds them
   from moves — a pan does not move any of them, so the whole of this is a
   dozen multiplies per point on every ordinary frame */
var PH = { on: 0 };
function phCtx(S) {
  var L = S && S.layout, f = S && S.fld;
  if (!L || !f || !f.sheet) return null;
  /* C0: the LIST is asked every call — it is two function calls that do a
     dozen multiplies, and caching it would mean caching each registrant's
     own state here, which is the cross-knowledge this card took out. The
     rest of the frame's constants are still kept until the layout moves. */
  PH.und = undJS(deskCtx(null, S, null));
  if (PH.on && PH.ox === L.ox && PH.oy === L.oy && PH.k === L.k &&
      PH.cw === L.cw && PH.ch === L.ch && PH.w === f.sheet.w && PH.h === f.sheet.h &&
      PH.r7 === !!(O.pLate && O.pLate.u)) return PH;
  PH.on = 1;
  PH.ox = L.ox; PH.oy = L.oy; PH.k = L.k; PH.cw = L.cw; PH.ch = L.ch;
  PH.w = f.sheet.w; PH.h = f.sheet.h;
  PH.r7 = !!(O.pLate && O.pLate.u);          /* R7's paper, or E4's */
  PH.rect = [0, 0, f.sheet.w, f.sheet.h];    /* uRect */
  PH.neat = neatRect();                      /* uNeat */
  PH.sg = 7.0;                               /* uFold.y */
  /* DR2: PH.run and PH.fo are gone with DRAPE_RUN and DRAPE_FILL_FOOT.
     The run and the foot are each entry's own, taken below. The arris's
     radius is still one number for the desk, as it always was. */
  PH.fi = DRAPE_FILL_TOP / L.k;              /* R45: uOverF.x */
  return PH;
}
/* the height of the paper's top face at a point of the SHEET, in sheet
   units — `Room.paperPoint` multiplies it by the layout's k to get the
   world's own css pixels, exactly as `sheetPos` does */
O.paperHeight = function (S, x, y) {
  var c = phCtx(S || O.S);
  if (!c) return 0;
  /* the same two functions the vertex shader is given, over the same
     list: bookLift is the largest drape and bookTop is the height of
     whichever entry that is — the first of equals, as the shader has it */
  var u = c.und, lift = 0, top = 0;
  for (var i = 0; i < u.length; i++) {
    /* DR1 concept B: the run and the foot are THIS entry's, as
       undRun/undFoot emit them — 3.5 times its own height, and a quarter
       of that for the foot. DR2: and that is the only way they are taken
       now; the desk's one run and R45's foot are deleted. */
    var ri = DRAPE_RUN_K * u[i].top;
    var foi = DRAPE_RUN_K * DRAPE_FOOT_K * u[i].top;
    var mi = ri > 0 ? phBookM(x, y, u[i].box, ri, c.fi, foi) : 0;
    if (i === 0 || mi > lift) { lift = mi; top = u[i].top; }
  }
  var plateau = (top + PAPER_FLOAT) * lift;                  /* boardH(p): (bookTop + uOverZ.y)*bookLift */
  /* DR1 concept C took the higher of the plate and the plateau here and
     pressed the lie out on a plate that was bridging. DR2: deleted — the
     support is the plateau, which is what boardH returns. */
  var z;
  if (!c.r7) z = plateau;                                    /* E4's sheet, frame 1 */
  else {
    var m = phMask(x, y, c.neat, LIE[1]);
    var lie = phLie(x, y, c.rect, m);
    /* DR1 concept C rearranged this as boardH + lie*(1 - lift*(1 -
       LIE_BOARD)) so it could carry a boardH that was a max() of two
       supports. DR2: the max is gone, so R45's own expression stands
       character for character and not even the last bit of the mantissa
       moves. */
    var drape = lie + (top + PAPER_FLOAT + lie * LIE_BOARD - lie) * lift;   /* mix(lieH, bookTop + float + lie*LIE_BOARD, lift) */
    /* DR1 (3): and the mirror takes the same factor the uniform does */
    z = drape + m * LIE[2] * phCrease(x, y, c.rect, c.sg, creaseGates());   /* CR2 */
  }
  /* FD1, 2026-09-20: the fold's own lift is gone with R80, so there is
     nothing to add here. A DOM box (the year index, the glyph row, the
     cartouche, the clip, the section) is laid at the resting height of
     the paper under it, and the sheet's own vertices are at that height
     too for the whole of the gesture — which is what the REVIEW of
     2026-09-19 was reaching for when it made this field follow the lift.
     The two agree again by there being one height and not two. */
  return z;
};

/* ==== CV1: THE COVER, WHICH IS THE ONLY THING THE PACKET SAYS ========
   Tommy, on the sheet folded away: "is zooming out from the map when it's
   folded up too difficult for someone to figure out? Could we put
   something on the folded-up map, but I don't want it to be too obvious?
   ... a zoom-out gesture ... then you're just spelling it out."

   A gesture written on the paper is an instruction, and this desk does not
   give instructions. But every real folded survey sheet answers the same
   question without naming a gesture at all: the panel that ends up on top
   of the packet is PRINTED ON ITS BACK with the sheet's name, its series
   and the office that issued it. It is not a hint and it does not appear
   when the reader is stuck — it is ink that was on the paper before the
   sheet was ever folded, and it is seen for the same reason the verso's
   rag is seen. All it says is that there is an office.

   WHICH PANEL. The sheet folds in eight by three rigid half turns (see
   foldTurns and foldPt): east half over at x = w/2, then over again at
   x = w/4, then the north half down at y = h/2. Walk the four columns
   A B C D (west to east) through the two long turns with foldRoll's own
   arithmetic and they land in the westmost quarter at 0, 3, 2, 1 leaves
   — which is what R65's own note says — so B is on top, printed side
   DOWN (one mirror). The turn across then takes each NORTH panel over the
   south one under it and inverts that order: A-north, which has taken no
   long turn at all, wraps the whole stack and comes to rest at 7 leaves,
   on top of everything, mirrored ONCE and therefore back up.

   So the uppermost face of the landed packet is the VERSO of the
   NORTH-WEST panel: x in [0, w/4], y in [0, h/2] in the sheet's own
   units, which is uFoldC.y and uFoldC.z in the shader. It is the only
   panel that carries the cover, and it carries it whenever that part of
   the back is seen — through the third turn as the leaf comes over, and
   not one frame sooner.

   HOW IT IS MADE. The same way everything else lettered at run time on
   this paper is made (strikeNote, strikeScale, handle.js's section): an
   SVG in SHEET UNITS carrying the print's own @font-face, decoded through
   an <img>, drawn once into a 2D canvas and uploaded. It is a ONE-OFF —
   struck the first time there is a fold in the paper, never on a settle,
   never on a frame — and it costs nothing at all until the sheet is
   folded, which is why the flat page is untouched to the byte.

   THE TYPE (taste calls, written down). The title is set at 23 sheet
   units against the light line's 11.5, which is twice the print and still
   inside the ruled border with room to spare; the series line and the
   office line are the print's own size, opened up (0.17 em and 0.21 em)
   the way a cover is lettered and not a caption. Every line is MEASURED
   before it is set and shrunk to fit if it is too long, because these
   three lines are placeholders and the one Tommy writes will not be the
   length of the one Claude drafted. The border is a single hairline inset
   a tenth of the panel — a cover panel's rule, not a cartouche.

   IT IS ON. It stood behind `?cover=1` for the pick; Tommy, 2026-09-20:
   "the content written onto the folded map itself that i said i loved".
   The switch is gone with the pick, as every switch goes once a decision
   is made. `COVER_ON` stays a constant so the shader text below keeps
   its one gate.
   ==================================================================== */
var COVER_ON = true;
var COVER_K = 1.3;        /* texels per sheet unit of the strike */
var COVER_INK = 0.78;     /* how much of the ink the BACK of the sheet takes */
var coverTex = null, coverBusy = 0, coverLast = null;
function coverWord(k) { return window.RoomCopy ? RoomCopy.t('sheet.cover_' + k) : ''; }
/* the measurer: the same face the strike is set in, so a line rewritten
   longer than the draft is set SMALLER and never runs into the border. A
   context that will not do letterSpacing gets it added by hand, as
   handle.js's own measurer does. */
var coverMeas = null;
function coverWidth(text, size, sp) {
  if (!coverMeas) {
    var cv = document.createElement('canvas'); cv.width = cv.height = 8;
    coverMeas = cv.getContext('2d');
  }
  var g = coverMeas, w;
  g.font = size.toFixed(3) + 'px "Sorts Mill Goudy", Georgia, serif';
  try { g.letterSpacing = (size*sp).toFixed(3) + 'px'; } catch (e) { /* older */ }
  w = g.measureText(text).width;
  if (g.letterSpacing === undefined) w += size*sp*text.length;
  try { g.letterSpacing = '0px'; } catch (e) { /* older */ }
  return w;
}
function coverFit(text, size, sp, maxW) {
  if (!text) return size;
  var w = coverWidth(text, size, sp);
  return (w > maxW && w > 0) ? size*maxW/w : size;
}
function coverMarkup(pw, ph, px, py) {
  var inset = Math.round(Math.min(pw, ph)*0.10);
  var maxW = pw - 2*inset - Math.round(pw*0.075);
  var cx = pw*0.5;
  function esc(t) { return String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
  /* text-anchor centres the ADVANCE, and letter-spacing puts a space after
     the last glyph too, so the ink sits half a space west of the middle
     unless it is given back */
  function line(text, size, sp, y, fill) {
    if (!text) return '';
    var s = coverFit(text, size, sp, maxW);
    return '<text x="' + (cx + s*sp*0.5).toFixed(3) + '" y="' + y.toFixed(2) +
      '" text-anchor="middle" font-size="' + s.toFixed(3) +
      '" letter-spacing="' + (s*sp).toFixed(3) + '" fill="' + fill + '">' +
      esc(text) + '</text>';
  }
  var ty = Math.round(ph*0.415);
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + pw.toFixed(3) + ' ' + ph.toFixed(3) +
    '" width="' + px + '" height="' + py + '">' +
    '<style>' + ((window.Room && Room.printFace()) || '') +
    'text{font-family:"Sorts Mill Goudy",Georgia,serif;stroke-width:0;' +
    'font-variant-numeric:lining-nums tabular-nums}</style>' +
    '<rect x="' + inset + '" y="' + inset + '" width="' + (pw - 2*inset).toFixed(3) +
    '" height="' + (ph - 2*inset).toFixed(3) + '" fill="none" stroke="#2e261c" stroke-width="1.1"></rect>' +
    line(coverWord('title'), 23, 0.10, ty, '#2e261c') +
    line(coverWord('series'), 11.5, 0.17, ty + 34, '#55483a') +
    line(coverWord('office'), 11.5, 0.21, ph - inset - Math.round(ph*0.048), '#55483a') +
    '</svg>';
}
/* struck once, the first time there is a fold in the paper */
function strikeCover(gl) {
  if (!COVER_ON || coverTex || coverBusy) return false;
  if (!window.Room || !Room.printFace) return false;
  if (Room.printFace(function () { strikeCover(gl); }) === null) return false;  /* the face is on its way */
  var fg = O.foldGeom(), pw = fg.c2, ph = fg.cy;
  if (!(pw > 0 && ph > 0)) return false;
  var px = Math.round(pw*COVER_K), py = Math.round(ph*COVER_K);
  var t0 = (window.performance && performance.now) ? performance.now() : 0;
  coverBusy = 1;
  var url = URL.createObjectURL(new Blob([coverMarkup(pw, ph, px, py)], { type: 'image/svg+xml' }));
  var img = new Image();
  img.onload = function () {
    URL.revokeObjectURL(url);
    var cv = document.createElement('canvas');
    cv.width = px; cv.height = py;
    cv.getContext('2d').drawImage(img, 0, 0, px, py);
    var tex = gl.createTexture();
    var pre = gl.getParameter(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    /* premultiplied and top row first — the furniture's own unpack state,
       because the shader un-premultiplies this exactly as it does that */
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, cv);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, pre);
    coverTex = tex; coverBusy = 0;
    coverLast = { panel: [+pw.toFixed(2), +ph.toFixed(2)], texels: [px, py],
                  ms: t0 ? +(performance.now() - t0).toFixed(2) : 0 };
    if (O.S && O.S.invalidate) O.S.invalidate();
  };
  img.onerror = function () { URL.revokeObjectURL(url); coverBusy = 0; };
  img.src = url;
  return true;
}
/* FOR THE HARNESS: whether the cover is on, and what was struck */
O.coverSpan = function () {
  return COVER_ON ? { on: true, up: !!coverTex, ink: COVER_INK, struck: coverLast } : { on: false };
};

O.drawSheet = function (gl, S) {
  if (!O.pSheet) return false;
  var L = S.layout, f = S.fld, s = sunOf();
  /* R7's paper the moment the driver has finished with it, E4's until
     then — the same picture at the desk pose either way */
  var p = (O.pLate && O.pLate.u) ? O.pLate : O.pSheet, R = p !== O.pSheet;
  /* R23: is the sheet at home? If it is not, the corner is up. It waits
     for R7's program (the corner lives only in that one) so the lift never
     starts against a shader that cannot draw it. */
  /* R58: THE FRAME THAT MAKES THE PRINT is the FLAT sheet — no fold, no
     turned corner, no light. The corner is up for the whole of a fold
     (the hand is holding it), and a curled corner baked into the print
     would be a curl printed on the paper; so the raster's own frame is
     taken with the corner down and the animation is not touched. */
  var CAP = !!O.flatCapture, neU = CAP ? 0 : ne.u, neH = CAP ? 0 : ne.h;
  /* X2: the fold's own amount, asked for HERE because the corners want it.
     FD1, 2026-09-20: and it is the hand's own amount again, as R47 had it
     — R80's four-percent take-up held the draw at rest for the first
     hundred milliseconds of the gesture and then handed over, which is
     the state Tommy saw the paper lock on to. */
  var fg = O.foldGeom();
  var ft = fg.t;
  /* CF3: and the paper is still in the fold draw while it settles after
     the last leaf has landed (O.foldSettle): the angles are all nought,
     the lie and the corner come back on uFoldS, and the print raster the
     face is textured with stays until the settle is done. */
  var drawFt = ft > 0 ? ft : (O.foldSettle > 0 ? 1e-4 : 0);
  /* ==== FP1: A CORNER TREATMENT BELONGS TO THE FLAT SHEET ==============
     Tommy, 2026-09-20, of the landed packet: "the corner of the paper
     still being curled up is odd too."

     Both of this sheet's live corners — R45's south-east ROLL and S-B2's
     north-east PEEL — rode the fold. The roll is in the fold's own frame
     (uWarp 1), so it was carried round all three turns and set down still
     rolled on the packet; the sheet's south-east corner lands at the
     packet's south-west one, which is exactly where the roll is in the
     picture. The peel was held up on purpose for the whole gesture ("the
     hand is holding the corner"), and its corner lands at the same
     station, so the packet had both.

     A HAND THAT FOLDS A MAP FLATTENS ITS CORNERS. That is the whole of
     it: the first crease presses a curled or peeled corner out, and eight
     leaves pressed together have no corner standing off any of them. So
     the moment there is a crease in the paper neither treatment exists —
     not the roll's mesh, not the face's chamfer, not either one's shading
     — and the leaf is a square-cornered rectangle. At drawFt nought this
     is the sheet it always was, to the bit. ==== */
  var creased = CAP ? 0 : drawFt;
  /* ==== FD2, 2026-09-20: AND IT IS PRESSED OUT, NOT SWITCHED OFF =======
     Tommy, of the whole first degree: "they essentially appear the second
     you engage the folding process, even if you've not moved the sheet
     yet." FP1's rule is right — eight leaves pressed together have no
     corner standing off any of them — but it was a step: at drawFt nought
     the sheet had a 64-unit chamfer with a rolled flap over it and one
     frame later it had a square corner. The press is R58's own ease, the
     first crease turning to 1.75 radians, which is the same ease the
     paper's lie goes out on; at the end of it FP1's packet is FP1's
     packet, to the bit.

     AND THE PRINT RASTER IS STRUCK WITH THE CORNER ALREADY PRESSED. The
     raster is what the face is textured with for the whole of the fold,
     so what has to be under the growing face is the sheet's own PRINT at
     that station — not a picture of the flap that was standing over it.
     The roll's own mesh takes its ink from uFurn and never from the
     raster, so it is unaffected. ==== */
  var pressK = CAP ? 1 : Math.max(Math.min(1, fg.a[0] / 1.75), drawFt > 0 ? (O.foldSettle || 0) : 0);
  /* ==== PC1, 2026-10-08: AND THE PEEL IS PRESSED OUT ON THE SAME EASE.
     Tommy: "The actual folded corner disappears as soon as you start
     pulling it, and also a load of shadow disappears." FD2 eased the
     south-east roll but FP1 still zeroed the north-east peel (and with it
     its cast shadow) on the first frame with a crease. The peel's mesh is
     placed through sheetPos, so it rides the leaf it is on; it goes down
     with the first crease like the roll. */
  if (creased > 0) { neU *= 1 - pressK; neH *= 1 - pressK; }
  if (!CAP && R && !ne.lock && window.Room && Room.atFit) {
    /* X2: A HAND THAT IS FOLDING THE SHEET IS HOLDING THE CORNER, so the
       corner is up for as long as there is a fold in the paper — the lift
       is what the hand has hold of, and it may not settle out from under
       it because the view happens to be at the fit. */
    /* REVIEW 2026-09-19, Tommy: "The corner is still folding with any map
       change, like it did back when it was the reset UI." The clip is the
       reset now (pin.js), so the off-fit curl has nothing left to say.
       The corner lies flat and comes up for three things only: a fold in
       progress, the tug at the widest view, and a hand on it. */
    /* FP1: and the fold is no longer one of the three. A hand reaching for
       the corner still lifts it (foldGrab, before there is any crease);
       the first crease presses it flat and it stays flat for the whole of
       the fold and the whole of the packet. Were the lift left to run on
       underneath the draw it would spring back up the instant the sheet
       came unfolded and then ease down, which is not a corner at all. */
    /* PC1: and the hand keeps hold of it until the first crease has pressed
       it flat (pressK, above), so it goes down with the fold, not before */
    var up = (!creased || (!!O.foldGrab && pressK < 1)) && (!!O.foldGrab || !!ne.tug || !!ne.hov);
    neWant(up ? 1 : 0, up ? NE.up : NE.down);
  }
  if (neAt((window.performance || Date).now()) && S.invalidate) S.invalidate();
  /* R58: on the print's own frame the books are not drawn, so the depth
     they would have cleared is cleared here instead */
  if (O.pBook && !CAP) { gl.enable(gl.DEPTH_TEST); gl.depthFunc(gl.LEQUAL); } else depthFore(gl);
  gl.useProgram(p);
  gl.uniformMatrix4fv(p.u.uVP, false, S.VP);
  gl.uniform3f(p.u.uSheet, L.ox, L.oy, L.k);
  gl.uniform4f(p.u.uRect, 0, 0, f.sheet.w, f.sheet.h);
  gl.uniform3f(p.u.uFold, Math.min(64, f.sheet.w * 0.045) * (1 - pressK), 7.0, CREASE_H);
  creaseUniform(gl, p);                                     /* CR2 */
  /* R22: 2 is "collapse the face onto the fold and let vaoCurl carry the flap".
     FP1: and 0 is "there is no flap" — the face keeps its own square
     corner, which is what a folded leaf has. */
  gl.uniform1f(p.u.uWarp, pressK >= 1 ? 0 : 2);
  gl.uniform3f(p.u.uLight, s.L[0], s.L[1], s.L[2]);
  gl.uniform4f(p.u.uSun, s.dx, s.dy, s.len, s.cast);
  /* R42 (a): the lantern's temperature on the paper — nought by day */
  if (p.u.uLampS) {
    var lsh = lampSheet(S, s);
    gl.uniform4f(p.u.uLampS, lsh[0], lsh[1], lsh[2], lsh[3]);
    gl.uniform1f(p.u.uLampN, lsh[4]);
  }
  gl.uniform1f(p.u.uPxPerSheet, L.k / (S.frameK || 1) * S.dpr);   /* MP2 */
  /* X2: the fold. Three uniforms, written every frame whether or not the
     switch is up, because a uniform that is only sometimes written is a
     uniform that holds the last thing somebody else put in it. */
  /* fg, ft, lk and drawFt are taken at the top of this function now — the
     corner treatments above are asked the same question. */
  /* CV1: the cover is struck the first time there is a fold in the paper,
     and never on the frame that makes the print raster */
  if (COVER_ON && !CAP && drawFt > 0) strikeCover(gl);
  gl.uniform4f(p.u.uFoldC, fg.c1, fg.c2, fg.cy, fg.leaf);
  gl.uniform3f(p.u.uFoldA, fg.a[0], fg.a[1], fg.a[2]);
  gl.uniform1f(p.u.uFoldOn, CAP ? 0 : drawFt);
  if (p.u.uFoldS) gl.uniform1f(p.u.uFoldS, CAP ? 0 : (O.foldSettle || 0));
  /* R58: the print raster, and the matrix that takes a sheet station to
     it. Written on every frame (a uniform only sometimes written holds
     whatever somebody else left in it) and switched off both when there
     is no fold and on the frame that is making the raster. */
  if (p.u.uPrintOn) {
    var snap = S.printSnap;
    /* The engine strikes the print raster at the first degree of the
       fold and this pass reads it from the same frame. */
    var pOn = (!CAP && drawFt > 0 && snap && snap.tex) ? 1 : 0;
    /* AND THE SAMPLER IS ALWAYS GIVEN A WHOLE TEXTURE, whether or not it
       is read. A sampler2D left pointing at unit 0 with nothing complete
       bound to it is an INCOMPLETE sampler, and ANGLE answers an
       incomplete sampler by dropping the draw call on the floor — no
       error, no warning, the whole sheet simply absent from the frame.
       (That is exactly what the print's own frame did until this line:
       the map landed in the raster and the paper under it did not.) */
    /* and NEVER the raster itself on the frame that is drawing INTO it —
       a texture attached to the bound framebuffer and sampled by the same
       draw is a feedback loop, which is GL_INVALID_OPERATION and, again,
       a draw call that silently does not happen. */
    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, (!CAP && snap && snap.tex) ? snap.tex : S.texFurn);
    gl.uniform1i(p.u.uPrint, 3);
    if (pOn) gl.uniformMatrix4fv(p.u.uPrintM, false, snap.M);
    gl.uniform1f(p.u.uPrintOn, pOn);
    /* PR1: this pixel's own texel in the raster. Both pictures are vp()
       under a 2-D zoom (the raster's widening or the glass's lens): undo
       this frame's, apply the raster's. And how far the panels must go
       over to their stations whatever their turn: all the way once the
       camera has moved off the one the raster was taken from. */
    if (pOn && p.u.uScr) {
      var ra = snap.aff || [1, 0, 0], ca = S.lensAff || [1, 0, 0];
      var cw = S.cv.width, chh = S.cv.height, kk = ra[0] / ca[0];
      gl.uniform4f(p.u.uScr, kk / cw, kk / chh,
                   0.5 * kk * (-1 - ca[1]) + 0.5 * ra[1] + 0.5,
                   0.5 * kk * (-1 - ca[2]) + 0.5 * ra[2] + 0.5);
      var vn = S.vpNow || null, vs = snap.vp, dv = 1;
      if (vn && vs) {
        var mx = 0, ref = 1e-9;
        for (var vi = 0; vi < 16; vi++) { mx = Math.max(mx, Math.abs(vn[vi] - vs[vi])); ref = Math.max(ref, Math.abs(vs[vi])); }
        dv = mx / ref;
      }
      var sm = function (a, b, x) { var t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
      gl.uniform1f(p.u.uScrW, sm(0.0005, 0.01, dv));
    } else if (p.u.uScr) { gl.uniform4f(p.u.uScr, 0, 0, 0, 0); gl.uniform1f(p.u.uScrW, 1); }
  }
  /* CV1: the cover's own strike, on its own unit — and, for the same
     reason the print's sampler is, ALWAYS given a whole texture whether
     or not it is read (an incomplete sampler is a draw call ANGLE drops
     on the floor without a word). TEXTURE1 is bound after this, so the
     unit left active at the end of the draw is the one it always was. */
  if (p.u.uCover) {
    gl.activeTexture(gl.TEXTURE4);
    gl.bindTexture(gl.TEXTURE_2D, coverTex || S.texFurn);
    gl.uniform1i(p.u.uCover, 4);
    gl.uniform1f(p.u.uCoverOn, (!CAP && coverTex && drawFt > 0) ? 1 : 0);
  }
  if (p.u.uUnlit) gl.uniform1f(p.u.uUnlit, CAP ? 1 : 0);
  /* R79: asked at draw time, not copied from the DOM lay which happens at
     the end of the frame, so a dragged bracket and its ink never differ by
     one camera frame. Noughts keep frame 1 and ?idx=0 unchanged. */
  var di = window.RoomIndex && RoomIndex.ink ? RoomIndex.ink(S) : null;
  if (p.u.uDate) gl.uniform4fv(p.u.uDate, di ? di.bracket : [0, 0, 0, 0]);
  if (p.u.uDateYear) gl.uniform4fv(p.u.uDateYear, di ? di.years : [0, 0, 0, 0]);
  /* R15 / C0: what is UNDER the paper, so the paper can be laid over it.
     The two books used to be written here by name; they register
     themselves now (desk-books.js) and this walks the list. The four
     numbers of the drape — the float, the run and R45's two radii — are
     the PAPER's and stay here, on BOTH programs every frame, so frame 1's
     paper and R7's agree at the corner and over anything on the desk. */
  undUniforms(gl, p, deskCtx(gl, S, s));
  if (p.u.uSE) gl.uniform2f(p.u.uSE, SE.phi, SE.hold);
  gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, S.texFurn);
  gl.uniform1i(p.u.uFurn, 1);
  gl.uniform1f(p.u.uHasFurn, S.furnReady ? 1 : 0);
  if (R) {
    var nr = neatRect(), ey = eyeInSheet(S);
    gl.uniform4f(p.u.uNeat, nr[0], nr[1], nr[2], nr[3]);
    gl.uniform3f(p.u.uUnd, LIE[0], LIE[1], LIE[2]);
    creaseUniform(gl, p);                                   /* CR2 */
    gl.uniform1f(p.u.uDeskZ, -0.5 / L.k);
    gl.uniform3f(p.u.uEye, ey[0], ey[1], ey[2]);
    gl.uniform1f(p.u.uSpec, specOf(s));
    /* the cut edge first: it hangs below the lip, so the face is what
       makes the silhouette and the band is only ever seen under it */
    gl.uniform1f(p.u.uSkirt, 1);
    gl.bindVertexArray(O.vaoSkirt);
    gl.drawElements(gl.TRIANGLES, O.skirtN, gl.UNSIGNED_SHORT, 0);
    gl.uniform1f(p.u.uSkirt, 0);
    var pk = peel(neU, neH);
    gl.uniform4f(p.u.uNE, pk.R, pk.A, neU, pk.B);
  }
  gl.bindVertexArray(O.vaoSheet);
  gl.drawElements(gl.TRIANGLES, O.sheetN, gl.UNSIGNED_INT, 0);
  /* R22 (c): and the turned corner off its own mesh, in the fold's own
     coordinates — after the face, so the flap's cut edge is put down over
     the notch it has just left open */
  if (O.vaoCurl && pressK < 1) {
    gl.uniform1f(p.u.uWarp, 1);
    /* R45: premultiplied, the house's own blend — everything the roll
       lies over (the journal's cover, the oak, the sheet's own face) is
       already on the glass when this draw happens, and the flap's free
       edge is the raster's own antialiased deckle */
    if (SE.phi > 0) { gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); }
    gl.bindVertexArray(O.vaoCurl);
    gl.drawElements(gl.TRIANGLES, O.curlN, gl.UNSIGNED_SHORT, 0);
    if (SE.phi > 0) gl.disable(gl.BLEND);
    gl.uniform1f(p.u.uWarp, 0);            /* merge: the NE corner below is not in the fold's frame */
  }
  /* R23: THE CORNER LAST, over the face it was cut out of. It is skipped
     altogether at the fit — no draw, no state, nothing to pay for the
     sheet lying flat — and it is only ever in R7's program, so frame 1
     (which is E4's) cannot be changed by it. */
  if (R && neU > 0) {
    /* premultiplied, the house's own blend (marks.js, day.js, ruler.js):
       everything the curl lies over — the oak, its shadow, the paper's own
       face — is already on the glass when this draw happens */
    gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.uniform1f(p.u.uCorner, 1);
    gl.bindVertexArray(O.vaoCorner);
    gl.drawElements(gl.TRIANGLES, O.cornerN, gl.UNSIGNED_SHORT, 0);
    gl.uniform1f(p.u.uCorner, 0);
    gl.disable(gl.BLEND);
  }
  depthBack(gl);
  return true;
};

/* WHAT EACH PASS COSTS, on its own. Room.perf times a whole frame; this
   times ONE object, with a 1x1 readPixels either side, because a bare
   gl.finish() reports a thousandth of a millisecond in ANGLE and is a lie
   (ROOM-PLAN §0). The scene is left as it was: the passes are drawn again
   over the frame that is already on the glass. */
function pieceFn(name) {
  for (var i = 0; i < PIECES.length; i++) if (PIECES[i].name === name) {
    var pc = PIECES[i];
    return function (gl, S) { pc.fn(gl, deskCtx(gl, S, null)); };
  }
  return null;
}
O.perf = function (which, n) {
  var S = O.S, gl = S.gl, px = new Uint8Array(4);
  /* C0: `pieces` is the whole desk pass; a NAME is one piece of it. */
  var f = which === 'desk' ? O.drawDesk
        : which === 'pieces' || which === 'books' ? O.drawBooks
        : which === 'sheet' ? O.drawSheet
        : pieceFn(which) || O.drawSheet;
  gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
  var t0 = performance.now();
  for (var i = 0; i < n; i++) f(gl, S);
  gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
  var t1 = performance.now();
  return { pass: which, frames: n, perFrame: +((t1 - t0) / n).toFixed(4) };
};

/* WHAT THE DESK'S PROGRAM COSTS TO BUILD. Frame 1 needs it (ROOM-REVIEW-1
   R6), and one number off one boot is noise — ANGLE's translator and the
   d3d12 back end are both jittery, and a boot also carries the page's own
   load. This builds BOTH desks n times in the running context, each with a
   unique comment appended so nothing comes back from the driver's program
   cache, and reports the medians. `Room.state().deskCompileMs` is the real
   boot's own figure; this is the A against the B. */
/* THE OAK'S PROGRAM, AND THE ONLY DESK PROGRAM THERE IS (P3). Returns true
   when it has just built one; idempotent.

   THE TWO ARMS THAT BAIL, since `desk-plain` no longer stands behind this
   one (P2's plan item 2). If the oak is already built the desk is simply
   pointed at it again — free, and it means a second `O.init` cannot leave
   the desk with no program. If there is no `mk` there is no engine to
   compile with, and nothing can be built at all: the desk is left unset on
   purpose, and engine.js's own fallback (`if (!RoomObjects.pDesk)
   S.pDesk = prog(..., 'desk')`, immediately after `RoomObjects.init`)
   compiles the flat desk it has always kept for that case. The draw then
   bails on `!O.pDesk` as it always did, so the oak pass is skipped and the
   engine's desk is what the reader sees — a plain desk, never no desk. */
O.buildWood = function () {
  if (O.pWood) { O.pDesk = O.pWood; return false; }
  if (!O.mk) return false;
  /* LD3: the oak leaves frame 1's set and a plain desk takes its place
     there (see O.drawDesk), linked FIRST so the driver's pool takes it
     before the grain. `?oak=wait` puts the old order back. */
  var plain = O.mk.khr && Q.get('oak') !== 'wait';
  if (plain) O.pPlain = O.mk.prog(O.S.gl, O.mk.FLAT_VS, deskFS(true, true), 'desk-plain');
  var t0 = performance.now();
  O.pWood = O.mk.prog(O.S.gl, O.mk.FLAT_VS, deskFS(true), 'desk-oak');
  if (plain) O.pWood.__f1 = false;
  O.woodCompileMs = +(performance.now() - t0).toFixed(2);
  /* `Room.state().deskCompileMs` has always meant "what the desk's program
     cost this boot"; the oak is that program now, so it is what feeds it. */
  O.deskCompileMs = O.woodCompileMs;
  O.pDesk = O.pWood;
  return true;
};

O.compileProbe = function (n) {
  var gl = O.S.gl, out = { wood: [], plain: [] }, k, i, t0, pr;
  for (i = 0; i < (n || 7); i++) {
    for (k in out) {
      var src = deskFS(k === 'wood') + '\n/* ' + k + i + ' ' + Math.random() + ' */\n';
      t0 = performance.now();
      /* R17: A UNIQUE NAME EVERY TIME. R4 made `prog()` memoise by name and
         this probe has asked for 'probe' ever since, so every run after the
         first was answered out of the cache and came back 0.0 ms — R6's own
         compile figures were measured before that landed. Found while
         timing R17's variants; the fix is the name. */
      pr = O.mk.prog(gl, O.mk.FLAT_VS, src, 'probe-' + k + '-' + i + '-' + Math.random());
      gl.getProgramParameter(pr, gl.LINK_STATUS);
      out[k].push(+(performance.now() - t0).toFixed(2));
      gl.deleteProgram(pr);
    }
  }
  function med(a) { a = a.slice().sort(function (x, y) { return x - y; }); return a[a.length >> 1]; }
  return { woodMs: med(out.wood), plainMs: med(out.plain),
           wood: out.wood, plain: out.plain, chars: [deskFS(true).length, deskFS(false).length] };
};

/* C0: the laptop is desk-laptop.js now — the machine, its screen, its
   keyboard, its legend atlas and the ground under it. objects.js keeps
   the oak, the sheet, the fold raster and the corner, and nothing else. */

/* R71 (a): the desk's real top, for place.js — one rect, two files */
O.DESK_MM = DESK_MM;
O.deskT = deskT;
O.deskRect = deskRect;
O.eye = eyeInSheet;
/* R12: the board the desk was drawn from, for the terminal's `status` */
O.seed = function () { return SEED; };
/* R17: where the table's front edge is, and where the matrix puts it on
   the screen — the one number the edge variant had to be tuned against */
O.r17 = function () {
  var S = O.S; if (!S) return null;
  var L = S.layout, f = S.fld, m = S.VP;
  var cy = L.oy + f.sheet.h * L.k / 2, near = (L.ch - cy) * O_NEAR;
  var mm = f.sheet.w * L.k / 840, frontY = cy + near;
  /* B1: and the height the floor is actually struck from, which is
     place.js's the moment place.js exists. `top` is floor-to-top-surface
     (the drop below the underside is top - slab), and `src` says which
     file the number came from, so a capture can tell a cold front page
     from a page that has been in the room. */
  var top = deskTopMm(), sl = deskSlabMm();
  function scr(x, y, z) {
    var cx = m[0]*x + m[4]*y + m[8]*z + m[12], cyy = m[1]*x + m[5]*y + m[9]*z + m[13];
    var w = m[3]*x + m[7]*y + m[11]*z + m[15];
    return [+((cx/w*0.5 + 0.5)*L.cw).toFixed(1), +((0.5 - cyy/w*0.5)*L.ch).toFixed(1)];
  }
  return { cw: L.cw, ch: L.ch, ox: +L.ox.toFixed(1), oy: +L.oy.toFixed(1), k: +L.k.toFixed(4),
           sheet: [f.sheet.w, f.sheet.h], mm: +mm.toFixed(3),
           cy: +cy.toFixed(1), near: +near.toFixed(1), frontY: +frontY.toFixed(1),
           slab: +(sl*mm).toFixed(1), drop: +((top - sl)*mm).toFixed(1),
           deskTopMm: top, slabMm: sl,
           src: (window.RoomPlace && window.RoomPlace.DESK_H_MM > 0) ? 'place' : 'fallback',
           pitch: +(S.cam.pitch*180/Math.PI).toFixed(2), camk: S.cam.k,
           sheetFootOnScreen: scr(L.ox + f.sheet.w*L.k/2, -(L.oy + f.sheet.h*L.k), 0),
           arris: scr(L.cw/2, -frontY, -0.5),
           underEdge: scr(L.cw/2, -frontY, -0.5 - sl*mm),
           floorAtEdge: scr(L.cw/2, -frontY, -0.5 - top*mm) };
};
O.spec = specOf;
window.RoomObjects = O;
})();
